// src/services/SimplifiedLabelManager.js
import Graphic from "@arcgis/core/Graphic";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import Color from "@arcgis/core/Color";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Polygon from "@arcgis/core/geometry/Polygon";
import Point from "@arcgis/core/geometry/Point";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import * as webMercatorUtils from "@arcgis/core/geometry/support/webMercatorUtils";

/**
 * Optimized LabelManager to provide centralized label handling with enhanced performance
 * - Manages creation, editing, and persistence of labels
 * - Single source of truth for label positions
 * - Unified storage mechanism with compression
 * - Ensures labels are unique to each map configuration
 * - Optimized collision detection and positioning algorithms
 */
class LabelManager {
  constructor(view) {
    this.view = view;
    this.editMode = false;
    this.selectedLabel = null;
    this.isMoving = false;
    this.editedLabels = new Map();
    this.projectId = null;
    this.mapConfigId = null;
    this.mapType = null;
    this.handlers = [];
    this.dragInfo = { startPoint: null, labelOffset: null };
    this.lastPointerPosition = null;
    this.labelLayer = null;
    this._collisionResolutionTimer = null;

    // Track zoom level for detecting significant zoom changes
    this._lastSignificantZoom = null;
    this._zoomThreshold = 1.5; // Reset manual positions after 1.5 zoom levels change

    // Direct drag handling state
    this.isDragging = false;
    this.dragStartPoint = null;
    this.draggedLabel = null;
    this.originalOffset = null;

    // Performance optimization: Cache for text measurements
    this._textMeasurementCache = new Map();
    this._canvasContext = null;
    this._boundingBoxCache = new WeakMap();
    
    // Optimized search patterns - pre-computed for better performance
    this._searchPatterns = null;
    this._zoomIndependentPatterns = null;

    // Debounce timers for performance
    this._saveDebounceTimer = null;
    this._refreshDebounceTimer = null;
    this._zoomCollisionTimer = null;

    // Visible bounding boxes for debugging collision detection
    this.showVisibleBoundingBoxes = true; // Set to true to debug label placement
    this.boundingBoxGraphics = new Map();

    // Try to create a separate layer for bounding boxes for better visibility
    this.boundingBoxLayer = null;

    // Collision tracking to avoid repeated attempts with same results
    this.lastCollisionCount = null;
    this.consecutiveSameCollisionCount = 0;
    this.collisionDetectionDisabled = false;

    // Default settings with performance optimizations
    this.settings = {
      fontSize: 10,
      fontFamily: "sans-serif",
      haloSize: 3,
      haloColor: [255, 255, 255, 0.9],
      color: [0, 0, 0, 0.95],
      avoidCollisions: true,
      minZoom: 8,
      autoSaveIntervalMs: 30000,
      // Performance settings
      collisionDetectionThreshold: 0.3,
      maxOptimizationAttempts: 100,
      enableTextMeasurementCache: true,
      enableBoundingBoxCache: true,
    };

    this.autoSaveInterval = null;
    this._initialize();
  }

  /**
   * Optimized storage key generation with caching
   * @returns {string} The storage key
   */
  getStorageKey() {
    // Cache the storage key to avoid repeated string concatenation
    if (this._cachedStorageKey && 
        this._lastMapConfigId === this.mapConfigId && 
        this._lastMapType === this.mapType) {
      return this._cachedStorageKey;
    }

    // Ensure we use a stable key that's based on the map configuration
    if (!this.mapConfigId) {
      this.mapConfigId = sessionStorage.getItem("currentMapConfigId");
    }

    if (!this.mapType) {
      this.mapType = sessionStorage.getItem("currentMapType");
    }

    // Build the storage key with enhanced uniqueness
    const baseKey = "customLabelPositions";
    let storageKey = baseKey;

    if (this.mapConfigId) {
      storageKey = `${storageKey}_config_${this.mapConfigId}`;
    }

    if (this.mapType) {
      storageKey = `${storageKey}_type_${this.mapType}`;
    }

    // Cache the result
    this._cachedStorageKey = storageKey;
    this._lastMapConfigId = this.mapConfigId;
    this._lastMapType = this.mapType;

    return storageKey;
  }

  /**
   * Initialize the label manager with optimizations
   * @private
   */
  _initialize() {
    // Create a dedicated graphics layer for labels if needed
    if (!this.labelLayer && this.view?.map) {
      this.labelLayer = new GraphicsLayer({
        id: "label-graphics-layer",
        title: "Map Labels",
        listMode: "hide",
      });
      this.view.map.add(this.labelLayer);
    }

    // Initialize canvas context for text measurements
    this._initializeTextMeasurement();

    // Pre-compute search patterns for better performance
    this._precomputeSearchPatterns();

    // Set up optimized auto-save interval
    this._setupOptimizedAutoSave();
    
    // Set up zoom change detection for collision resolution
    this._setupZoomChangeDetection();

    // Load project info from storage
    this.projectId = localStorage.getItem("currentProjectId") || 
                    sessionStorage.getItem("currentProjectId");
    this.mapConfigId = sessionStorage.getItem("currentMapConfigId");
    this.mapType = sessionStorage.getItem("currentMapType");

    // Load saved positions
    if (this.projectId) {
      this.loadPositions();
    }

  }

  /**
   * Initialize optimized text measurement system
   * @private
   */
  _initializeTextMeasurement() {
    if (this.settings.enableTextMeasurementCache) {
      const canvas = document.createElement("canvas");
      this._canvasContext = canvas.getContext("2d");
      
      // Set canvas to a reasonable size for better performance
      canvas.width = 1000;
      canvas.height = 200;
    }
  }

  /**
   * Pre-compute search patterns for optimal performance
   * @private
   */
  _precomputeSearchPatterns() {
    this._searchPatterns = this._generateOptimizedSearchPatterns();
    this._zoomIndependentPatterns = this._generateZoomIndependentSearchPatterns();
  }

  /**
   * Optimized bounding box calculation with caching and improved text measurements
   * @param {Object} label - The label graphic
   * @param {Object} position - The label position {x, y}
   * @returns {Object} Bounding box {x, y, width, height, centerX, centerY}
   * @private
   */
  _calculateLabelBoundingBox(label, position) {
    if (!label || !label.symbol) {
      return null;
    }

    // Check cache first if enabled
    if (this.settings.enableBoundingBoxCache) {
      const cacheKey = `${label.uid || 'unknown'}_${position.x}_${position.y}`;
      if (this._boundingBoxCache.has(label)) {
        const cached = this._boundingBoxCache.get(label);
        if (cached.position.x === position.x && cached.position.y === position.y) {
          return cached.boundingBox;
        }
      }
    }

    const text = label.symbol.text || label.attributes?.text || "";

    if (!text) {

      return null;
    }

    const fontSize = label.symbol.font?.size || this.settings.fontSize;
    const fontWeight = label.symbol.font?.weight || "normal";
    const fontFamily = label.symbol.font?.family || this.settings.fontFamily;

    // Optimized text measurement with caching
    const textDimensions = this._getOptimizedTextDimensions(text, fontSize, fontWeight, fontFamily);

    // Account for background padding and borders
    const hasBackground = !!label.symbol.backgroundColor;
    const borderSize = label.symbol.borderLineSize || 0;
    const padding = hasBackground ? Math.max(4, borderSize * 2) : 2;

    // Account for halo effect
    const haloSize = label.symbol.haloSize || 0;
    const extraPadding = Math.max(padding, haloSize);

    // CRITICAL FIX: Convert pixel dimensions to map units
    // textDimensions are in pixels, but position is in map coordinates
    // We must convert to the same coordinate system
    const pixelToMapRatio = this.view?.extent?.width / this.view?.width || 0.0001;

    // Ensure minimum dimensions for short labels to prevent collision detection failures
    // Short labels (1-3 characters) can have very small bounding boxes that slip through collision detection
    const minWidth = 40; // Minimum 40 pixels width
    const minHeight = 20; // Minimum 20 pixels height

    const effectiveWidth = Math.max(textDimensions.width + extraPadding * 2, minWidth);
    const effectiveHeight = Math.max(textDimensions.height + extraPadding * 2, minHeight);

    const widthInMapUnits = effectiveWidth * pixelToMapRatio;
    const heightInMapUnits = effectiveHeight * pixelToMapRatio;

    const boundingBox = {
      x: position.x - widthInMapUnits / 2,
      y: position.y - heightInMapUnits / 2,
      width: widthInMapUnits,
      height: heightInMapUnits,
      centerX: position.x,
      centerY: position.y,
      textWidth: textDimensions.width * pixelToMapRatio,
      textHeight: textDimensions.height * pixelToMapRatio,
    };

    // Cache the result if enabled
    if (this.settings.enableBoundingBoxCache) {
      this._boundingBoxCache.set(label, {
        position: { x: position.x, y: position.y },
        boundingBox: boundingBox
      });
    }

    return boundingBox;
  }

  /**
   * Create a visible bounding box around a label for debugging collision detection
   * @param {Object} labelGraphic - The label graphic
   * @param {string} labelId - Label identifier
   * @private
   */
  _createVisibleBoundingBox(labelGraphic, labelId) {
    if (!labelGraphic || !this.labelLayer) {
      
      return;
    }

    try {
      // Remove existing bounding box if any
      this._removeVisibleBoundingBox(labelId);

      // Get saved positions to find the actual label coordinates
      const savedPositions = this._getSavedPositions();
      const savedData = savedPositions[labelId];
      let centerMapPoint = null;
      
      // CRITICAL FIX: Account for text alignment when calculating bounding box position
      // The label geometry is the anchor point, but we need the visual center of the text
      
      if (labelGraphic.geometry) {
        const anchorPoint = labelGraphic.geometry;
        
        // Get text properties for alignment calculation
        const text = labelGraphic.symbol?.text || "";
        const fontSize = labelGraphic.symbol?.font?.size || this.settings.fontSize;
        const fontWeight = labelGraphic.symbol?.font?.weight || this.settings.fontWeight;
        const fontFamily = labelGraphic.symbol?.font?.family || this.settings.fontFamily;
        const horizontalAlignment = labelGraphic.symbol?.horizontalAlignment || "center";
        const verticalAlignment = labelGraphic.symbol?.verticalAlignment || "bottom";
        
        // Calculate text dimensions
        const textDimensions = this._getOptimizedTextDimensions(text, fontSize, fontWeight, fontFamily);
        
        // Convert anchor point to screen coordinates
        const anchorScreen = this.view.toScreen(anchorPoint);
        if (!anchorScreen) {
          
          return;
        }
        
        // Calculate visual center offset based on alignment
        let centerOffsetX = 0;
        let centerOffsetY = 0;
        
        // Horizontal alignment offset
        if (horizontalAlignment === "left") {
          centerOffsetX = textDimensions.width / 2; // Center is half-width to the right
        } else if (horizontalAlignment === "right") {
          centerOffsetX = -textDimensions.width / 2; // Center is half-width to the left
        }
        // center alignment: no offset needed
        
        // Vertical alignment offset
        if (verticalAlignment === "baseline" || verticalAlignment === "bottom") {
          centerOffsetY = -textDimensions.height / 2; // Center is half-height up
        } else if (verticalAlignment === "top") {
          centerOffsetY = textDimensions.height / 2; // Center is half-height down
        }
        // middle alignment: no offset needed
        
        // Calculate visual center screen position
        const centerScreen = {
          x: anchorScreen.x + centerOffsetX,
          y: anchorScreen.y + centerOffsetY
        };
        
        // Convert back to map coordinates
        centerMapPoint = this.view.toMap(centerScreen);
      } else {
        
        return;
      }
      // Calculate text dimensions for proper sizing
      const text = labelGraphic.symbol?.text || "";
      const fontSize = labelGraphic.symbol?.font?.size || this.settings.fontSize;
      const fontWeight = labelGraphic.symbol?.font?.weight || this.settings.fontWeight;
      const fontFamily = labelGraphic.symbol?.font?.family || this.settings.fontFamily;
      
      const textDimensions = this._getOptimizedTextDimensions(text, fontSize, fontWeight, fontFamily);
      // Calculate bounding box size in map units
      const mapExtentWidth = this.view.extent.width;
      const viewWidth = this.view.width;
      const pixelToMapRatio = mapExtentWidth / viewWidth;
      
      // Use text dimensions with minimal padding
      const padding = 4; // Padding around text in pixels
      const boxWidthInMapUnits = (textDimensions.width + padding * 2) * pixelToMapRatio;
      const boxHeightInMapUnits = (textDimensions.height + padding * 2) * pixelToMapRatio;
      // Create bounding box corners around the actual label position
      const halfWidth = boxWidthInMapUnits / 2;
      const halfHeight = boxHeightInMapUnits / 2;
      
      const topLeft = { x: centerMapPoint.x - halfWidth, y: centerMapPoint.y + halfHeight };
      const topRight = { x: centerMapPoint.x + halfWidth, y: centerMapPoint.y + halfHeight };
      const bottomLeft = { x: centerMapPoint.x - halfWidth, y: centerMapPoint.y - halfHeight };
      const bottomRight = { x: centerMapPoint.x + halfWidth, y: centerMapPoint.y - halfHeight };
      // Create polygon geometry
      const polygonGeometry = new Polygon({
        rings: [[
          [topLeft.x, topLeft.y],
          [topRight.x, topRight.y],
          [bottomRight.x, bottomRight.y],
          [bottomLeft.x, bottomLeft.y],
          [topLeft.x, topLeft.y]
        ]],
        spatialReference: this.view.spatialReference
      });
      // Create visible bounding box symbol - clean outline around text
      
      const boundingBoxSymbol = new SimpleFillSymbol({
        color: [255, 0, 0, 0], // Invisible fill
        outline: new SimpleLineSymbol({
          color: [255, 0, 0, 0], // Invisible outline
          width: 1.5
        })
      });
      // Create bounding box graphic
      const boundingBoxGraphic = new Graphic({
        geometry: polygonGeometry,
        symbol: boundingBoxSymbol,
        attributes: {
          labelId: labelId,
          isBoundingBox: true
        }
      });
      // Create dedicated bounding box layer if needed
      if (!this.boundingBoxLayer && this.view) {
        
        this.boundingBoxLayer = new GraphicsLayer({
          title: "Label Bounding Boxes (Debug)",
          id: "labelBoundingBoxes"
        });
        this.view.map.add(this.boundingBoxLayer);
        
      }

      // Choose which layer to add to - prefer dedicated layer
      const targetLayer = this.boundingBoxLayer || this.labelLayer;
      targetLayer.add(boundingBoxGraphic);
      this.boundingBoxGraphics.set(labelId, boundingBoxGraphic);

      // Also visualize the data point circle for debugging
      // Note: Using screen-pixel based symbols so they scale with zoom
      const labelData = this.editedLabels.get(labelId);

      // DEBUG: Log all possible sources
      console.log(`[DEBUG] Label ${labelId}:`, {
        hasAttributeDataPoint: !!labelGraphic.attributes?.dataPointGeometry,
        hasTrackedOriginal: !!labelData?.originalPosition,
        labelGeometry: labelGraphic.geometry,
        xoffset: labelGraphic.symbol?.xoffset,
        yoffset: labelGraphic.symbol?.yoffset,
        attributeDataPoint: labelGraphic.attributes?.dataPointGeometry,
        trackedOriginal: labelData?.originalPosition
      });

      // Get the true data point geometry from multiple possible sources
      let dataPointGeometry = labelGraphic.attributes?.dataPointGeometry ||
                              labelData?.originalPosition;

      // If still not found, check if this label hasn't been moved yet (no pixel offsets)
      // In that case, the current geometry IS the data point
      if (!dataPointGeometry && labelGraphic.symbol?.xoffset === 0 && labelGraphic.symbol?.yoffset === 0) {
        dataPointGeometry = labelGraphic.geometry;
        console.log(`[DEBUG] Using current geometry as data point for ${labelId}`);
        // Store it for future use
        if (labelGraphic.attributes) {
          labelGraphic.attributes.dataPointGeometry = labelGraphic.geometry.clone();
        }
      }

      if (dataPointGeometry) {
        console.log(`[DEBUG] Drawing circle at:`, dataPointGeometry.x, dataPointGeometry.y);
        const dataPointRadius = this._getDataPointRadius();

        // Use simple-marker which is screen-pixel based (scales with zoom)
        const circleGraphic = new Graphic({
          geometry: dataPointGeometry,
          symbol: {
            type: "simple-marker",
            style: "circle",
            color: [0, 0, 255, 0], // Invisible fill
            size: dataPointRadius * 2, // Diameter in pixels
            outline: {
              color: [0, 0, 255, 0], // Invisible outline
              width: 1.5
            }
          },
          attributes: {
            labelId: labelId,
            isDataPointCircle: true
          }
        });

        targetLayer.add(circleGraphic);
        // Store reference so we can remove it later
        this.boundingBoxGraphics.set(labelId + "_datapoint", circleGraphic);
      }

      // Log final geometry coordinates for verification
    } catch (error) {
    }
  }

  /**
   * Create a circle polygon from a center point and radius
   * @param {Point} center - Center point
   * @param {number} radius - Radius in map units
   * @param {number} numPoints - Number of points to create the circle (default 32)
   * @returns {Polygon} Circle polygon geometry
   * @private
   */
  _createCirclePolygon(center, radius, numPoints = 32) {
    const points = [];
    for (let i = 0; i < numPoints; i++) {
      const angle = (i / numPoints) * 2 * Math.PI;
      const x = center.x + radius * Math.cos(angle);
      const y = center.y + radius * Math.sin(angle);
      points.push([x, y]);
    }
    // Close the polygon by adding the first point again
    points.push(points[0]);

    return new Polygon({
      rings: [points],
      spatialReference: center.spatialReference
    });
  }

  /**
   * Remove visible bounding box for a label
   * @param {string} labelId - Label identifier
   * @private
   */
  _removeVisibleBoundingBox(labelId) {
    const existingBox = this.boundingBoxGraphics.get(labelId);
    const targetLayer = this.boundingBoxLayer || this.labelLayer;
    if (existingBox && targetLayer) {
      try {
        targetLayer.remove(existingBox);
      } catch (error) {
        // Ignore removal errors
      }
    }
    this.boundingBoxGraphics.delete(labelId);

    // Also remove data point circle if it exists
    const dataPointCircle = this.boundingBoxGraphics.get(labelId + "_datapoint");
    if (dataPointCircle && targetLayer) {
      try {
        targetLayer.remove(dataPointCircle);
      } catch (error) {
        // Ignore removal errors
      }
    }
    this.boundingBoxGraphics.delete(labelId + "_datapoint");
  }

  /**
   * Toggle visible bounding boxes on/off
   * @param {boolean} show - Whether to show bounding boxes
   */
  setVisibleBoundingBoxes(show) {
    this.showVisibleBoundingBoxes = show;

    if (!show) {
      // Remove all existing bounding boxes
      this.boundingBoxGraphics.forEach((graphic, labelId) => {
        this._removeVisibleBoundingBox(labelId);
      });
    } else {
      // Create bounding boxes for all current labels
      this.editedLabels.forEach((labelData, labelId) => {
        if (labelData.graphic && this.labelLayer.graphics.includes(labelData.graphic)) {
          this._createVisibleBoundingBox(labelData.graphic, labelId);
        }
      });
    }
  }

  /**
   * Optimized text dimension calculation with caching
   * @param {string} text - The text to measure
   * @param {number} fontSize - Font size
   * @param {string} fontWeight - Font weight
   * @param {string} fontFamily - Font family
   * @returns {Object} Text dimensions {width, height}
   * @private
   */
  _getOptimizedTextDimensions(text, fontSize, fontWeight, fontFamily) {
    // Create cache key
    const cacheKey = `${text}_${fontSize}_${fontWeight}_${fontFamily}`;
    
    // Check cache first
    if (this.settings.enableTextMeasurementCache && this._textMeasurementCache.has(cacheKey)) {
      return this._textMeasurementCache.get(cacheKey);
    }

    let textWidth, textHeight;

    if (this._canvasContext) {
      // Use cached canvas context for better performance
      this._canvasContext.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
      const metrics = this._canvasContext.measureText(text);
      textWidth = metrics.width;
      
      // Use more accurate height calculation
      if (metrics.actualBoundingBoxAscent !== undefined && metrics.actualBoundingBoxDescent !== undefined) {
        textHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
      } else {
        textHeight = fontSize * 1.2; // Fallback line height factor
      }
      
      // Reduced buffer for more accurate collision detection
      // Previous aggressive buffering was causing unnecessary collisions
      const baseBuffer = fontSize * 0.15; // Reduced from 40% to 15%
      
      // Reduced progressive buffer for longer labels
      let lengthBuffer;
      if (text.length <= 10) {
        lengthBuffer = text.length * fontSize * 0.02; // Reduced from 5% to 2%
      } else {
        // For longer labels, reduced per-character buffer
        lengthBuffer = 10 * fontSize * 0.02 + (text.length - 10) * fontSize * 0.03; // Reduced from 8% to 3%
      }
      
      const minBuffer = fontSize * 0.5; // Reduced minimum buffer from 2x to 0.5x
      const totalBuffer = Math.max(minBuffer, baseBuffer + lengthBuffer);
      textWidth += totalBuffer;
      
    } else {
      // Fallback to creating new canvas (less optimal)
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      context.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
      const metrics = context.measureText(text);
      textWidth = metrics.width;
      
      // Use more accurate height calculation
      if (metrics.actualBoundingBoxAscent !== undefined && metrics.actualBoundingBoxDescent !== undefined) {
        textHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
      } else {
        textHeight = fontSize * 1.2;
      }
      
      // Reduced buffer for more accurate collision detection
      // Previous aggressive buffering was causing unnecessary collisions
      const baseBuffer = fontSize * 0.15; // Reduced from 40% to 15%
      
      // Reduced progressive buffer for longer labels
      let lengthBuffer;
      if (text.length <= 10) {
        lengthBuffer = text.length * fontSize * 0.02; // Reduced from 5% to 2%
      } else {
        // For longer labels, reduced per-character buffer
        lengthBuffer = 10 * fontSize * 0.02 + (text.length - 10) * fontSize * 0.03; // Reduced from 8% to 3%
      }
      
      const minBuffer = fontSize * 0.5; // Reduced minimum buffer from 2x to 0.5x
      const totalBuffer = Math.max(minBuffer, baseBuffer + lengthBuffer);
      textWidth += totalBuffer;
    }

    const dimensions = { width: textWidth, height: textHeight };

    // Cache the result
    if (this.settings.enableTextMeasurementCache) {
      this._textMeasurementCache.set(cacheKey, dimensions);
      
      // Limit cache size to prevent memory leaks
      if (this._textMeasurementCache.size > 1000) {
        const firstKey = this._textMeasurementCache.keys().next().value;
        this._textMeasurementCache.delete(firstKey);
      }
    }

    return dimensions;
  }

  /**
   * Optimized bounding box overlap check with early termination
   * @param {Object} box1 - First bounding box
   * @param {Object} box2 - Second bounding box
   * @returns {boolean} True if boxes overlap
   * @private
   */
  _checkBoundingBoxOverlap(box1, box2) {
    if (!box1 || !box2) return false;

    // Early termination optimizations
    return !(
      box1.x + box1.width < box2.x ||
      box2.x + box2.width < box1.x ||
      box1.y + box1.height < box2.y ||
      box2.y + box2.height < box1.y
    );
  }

  /**
   * Optimized overlap area calculation
   * @param {Object} box1 - First bounding box
   * @param {Object} box2 - Second bounding box
   * @returns {number} Overlap area
   * @private
   */
  _calculateOverlapArea(box1, box2) {
    if (!this._checkBoundingBoxOverlap(box1, box2)) return 0;

    const xOverlap = Math.min(box1.x + box1.width, box2.x + box2.width) - 
                    Math.max(box1.x, box2.x);
    const yOverlap = Math.min(box1.y + box1.height, box2.y + box2.height) - 
                    Math.max(box1.y, box2.y);

    return xOverlap * yOverlap;
  }

  /**
   * Highly optimized label position optimization with improved algorithms
   * @param {Array} labelsToOptimize - Array of {graphic, labelId, position} objects
   * @returns {Map} Map of labelId to optimized position
   * @private
   */
  _optimizeLabelPositions(labelsToOptimize) {
    if (!labelsToOptimize || labelsToOptimize.length === 0) return new Map();
    const optimizedPositions = new Map();

    // Optimized sorting with cached calculations
    const sortedLabels = this._optimizedLabelSorting(labelsToOptimize);

    // Use pre-computed search patterns
    const searchPatterns = this._searchPatterns;

    // Track optimization attempts AND time to prevent infinite loops
    let totalAttempts = 0;
    const MAX_TOTAL_ATTEMPTS = sortedLabels.length * this.settings.maxOptimizationAttempts * 2;
    const startTime = performance.now();
    const MAX_OPTIMIZATION_TIME = 4000; // 4 seconds max

    // Process each label with optimized algorithm
    for (let index = 0; index < sortedLabels.length; index++) {
      // Safety check 1: Time limit (check every label for better enforcement)
      const elapsed = performance.now() - startTime;
      if (elapsed > MAX_OPTIMIZATION_TIME) {
        console.warn(`[SimplifiedLabelManager] Optimization stopped - time limit exceeded (${Math.round(elapsed)}ms). Processed ${index}/${sortedLabels.length} labels.`);
        this._showCollisionWarning(sortedLabels.length);
        break;
      }

      // Safety check 2: Attempt count limit
      if (totalAttempts > MAX_TOTAL_ATTEMPTS) {
        console.warn(`[SimplifiedLabelManager] Optimization stopped - too many attempts (${totalAttempts}). Remaining labels will use default positions.`);
        this._showCollisionWarning(sortedLabels.length);
        break;
      }
      const labelData = sortedLabels[index];
      const { graphic, labelId, position } = labelData;

      if (!position) {
        optimizedPositions.set(labelId, { x: 0, y: 0 });
        continue;
      }

      let bestPosition = { ...position };
      let minOverlapScore = Number.MAX_VALUE;
      let foundPerfectPosition = false;
      let attemptCount = 0;

      // Optimized search with early termination
      for (const pattern of searchPatterns) {
        if (foundPerfectPosition || attemptCount >= this.settings.maxOptimizationAttempts) {
          break;
        }

        // Check time limit inside inner loop to ensure we respect it
        if (performance.now() - startTime > MAX_OPTIMIZATION_TIME) {
          break;
        }

        const testPosition = {
          x: position.x + pattern.x,
          y: position.y + pattern.y,
        };

        // Calculate bounding box for this test position
        const testBox = this._calculateLabelBoundingBox(graphic, testPosition);
        if (!testBox) continue;

        // Optimized overlap calculation
        let overlapScore = this._calculateOptimizedOverlapScore(
          testBox, labelData, sortedLabels, optimizedPositions, index
        );

        // Apply position quality modifiers
        overlapScore += this._calculatePositionPenalty(position, testPosition, pattern);

        // Update best position if this is better
        if (overlapScore < minOverlapScore) {
          minOverlapScore = overlapScore;
          bestPosition = testPosition;

          // Early termination for perfect positions
          if (overlapScore <= this.settings.collisionDetectionThreshold) {
            foundPerfectPosition = true;
          }
        }

        attemptCount++;
        totalAttempts++;
      }

      // Store the optimized position
      optimizedPositions.set(labelId, bestPosition);

      // Log significant moves for debugging (optimized logging)
      if (bestPosition.x !== position.x || bestPosition.y !== position.y) {
        const distanceSquared = Math.pow(bestPosition.x - position.x, 2) + 
                               Math.pow(bestPosition.y - position.y, 2);
        if (distanceSquared > 100) { // Only log moves > 10px
          const distance = Math.sqrt(distanceSquared);
          
        }
      }
    }

    return optimizedPositions;
  }

  /**
   * Optimized label sorting with cached calculations
   * @param {Array} labelsToOptimize - Labels to sort
   * @returns {Array} Sorted labels
   * @private
   */
  _optimizedLabelSorting(labelsToOptimize) {
    // Pre-calculate sorting criteria to avoid repeated calculations
    const labelsWithCriteria = labelsToOptimize.map(labelData => {
      const text = labelData.graphic.symbol?.text || "";
      const fontSize = labelData.graphic.symbol?.font?.size || this.settings.fontSize;
      
      return {
        ...labelData,
        _textLength: text.length,
        _fontSize: fontSize,
        _sortKey: text.length * 1000 + fontSize // Combined sort key for better performance
      };
    });

    // Single sort operation with combined criteria
    return labelsWithCriteria.sort((a, b) => b._sortKey - a._sortKey);
  }

  /**
   * Optimized overlap score calculation with early termination
   * @param {Object} testBox - Bounding box of the test position
   * @param {Object} currentLabel - Current label being positioned
   * @param {Array} allLabels - All labels being processed
   * @param {Map} optimizedPositions - Already optimized positions
   * @param {number} currentIndex - Index of current label
   * @returns {number} Total overlap score (lower is better)
   * @private
   */
  _calculateOptimizedOverlapScore(testBox, currentLabel, allLabels, optimizedPositions, currentIndex) {
    let totalScore = 0;
    const maxScore = 10000; // Early termination threshold

    // Check against already optimized labels (more important)
    for (let i = 0; i < currentIndex && totalScore < maxScore; i++) {
      const otherLabel = allLabels[i];
      const otherPosition = optimizedPositions.get(otherLabel.labelId);

      if (!otherPosition) continue;

      const otherBox = this._calculateLabelBoundingBox(otherLabel.graphic, otherPosition);
      if (!otherBox) continue;

      const overlapArea = this._calculateOverlapArea(testBox, otherBox);
      if (overlapArea > 0) {
        const importanceWeight = this._getLabelImportanceWeight(otherLabel.graphic);
        totalScore += overlapArea * importanceWeight;
      }
    }

    // Early termination if score is already too high
    if (totalScore >= maxScore) return totalScore;

    // Check against remaining unprocessed labels (lower weight)
    for (let i = currentIndex + 1; i < allLabels.length && totalScore < maxScore; i++) {
      const otherLabel = allLabels[i];
      const otherBox = this._calculateLabelBoundingBox(otherLabel.graphic, otherLabel.position);

      if (!otherBox) continue;

      const overlapArea = this._calculateOverlapArea(testBox, otherBox);
      if (overlapArea > 0) {
        totalScore += overlapArea * 0.5; // Lower weight for unprocessed
      }
    }

    // Early termination if score is already too high
    if (totalScore >= maxScore) return totalScore;

    // Check collisions with ALL data point circles
    const pixelToMapRatio = this.view?.extent?.width / this.view?.width || 0.0001;
    let dataPointCollisionCount = 0;

    for (let i = 0; i < allLabels.length && totalScore < maxScore; i++) {
      if (i === currentIndex) continue; // Skip current label's own data point

      const otherLabel = allLabels[i];

      // Get data point geometry from editedLabels tracking first, then fallback to graphic
      let dataPointGeometry = null;
      if (otherLabel.labelId) {
        const trackedData = this.editedLabels.get(otherLabel.labelId);
        dataPointGeometry = trackedData?.originalPosition;
      }

      // Fallback to graphic attributes or geometry
      if (!dataPointGeometry) {
        dataPointGeometry = otherLabel.graphic?.attributes?.dataPointGeometry ||
                           otherLabel.graphic?.geometry;
      }

      if (dataPointGeometry) {
        const hasCollision = this._checkCollisionWithDataPoint(testBox, dataPointGeometry, pixelToMapRatio);
        if (hasCollision) {
          // Heavy penalty for colliding with data points
          totalScore += 5000;
          dataPointCollisionCount++;
        }
      }
    }

    return totalScore;
  }

  /**
   * Optimized position penalty calculation
   * @param {Object} originalPos - Original position
   * @param {Object} testPos - Test position
   * @param {Object} pattern - Search pattern used
   * @returns {number} Position penalty score
   * @private
   */
  _calculatePositionPenalty(originalPos, testPos, pattern) {
    // Pre-calculate distance squared to avoid sqrt when possible
    const distanceSquared = Math.pow(testPos.x - originalPos.x, 2) + 
                           Math.pow(testPos.y - originalPos.y, 2);
    
    let penalty = distanceSquared * 0.0001; // Reduced calculation

    // Directional preference optimizations
    if (pattern.y > 0) penalty += 2; // Below original
    if (pattern.x < 0) penalty += 1; // Left of original

    return penalty;
  }

  /**
   * Optimized importance weight calculation with caching
   * @param {Object} graphic - Label graphic
   * @returns {number} Importance weight
   * @private
   */
  _getLabelImportanceWeight(graphic) {
    // Check if we've cached this calculation
    if (graphic._cachedImportanceWeight) {
      return graphic._cachedImportanceWeight;
    }

    let weight = 1.0;

    // Optimized weight calculations
    const fontSize = graphic.symbol?.font?.size || this.settings.fontSize;
    weight += (fontSize - this.settings.fontSize) * 0.1;

    if (graphic.symbol?.font?.weight === "bold") weight += 0.5;
    if (graphic.symbol?.backgroundColor) weight += 0.3;

    const textLength = (graphic.symbol?.text || "").length;
    weight += Math.min(textLength * 0.02, 0.5);

    // Cache the result
    graphic._cachedImportanceWeight = weight;

    return weight;
  }

  /**
   * Generate optimized search patterns with better distribution
   * @returns {Array} Array of {x, y} offset patterns
   * @private
   */
  _generateOptimizedSearchPatterns() {
    const patterns = [{ x: 0, y: 0 }]; // Original position first

    // Add strategic close positions prioritizing left/right before far distances
    const strategicOffsets = [
      { x: -15, y: 0 }, { x: 15, y: 0 },     // Horizontal (left prioritized)
      { x: 0, y: -15 }, { x: 0, y: 15 },     // Vertical
      { x: -12, y: -12 }, { x: -12, y: 12 }, // Left diagonals
      { x: 12, y: -12 }, { x: 12, y: 12 }    // Right diagonals
    ];

    // Increased radius steps for better spacing and collision avoidance
    // Start with left/right angles first (0°, 180°) before expanding radially
    const radiusSteps = [10, 20, 30, 40, 50];
    const angleSteps = 12;

    for (const radius of radiusSteps) {
      // Prioritize horizontal angles first: left (180°), right (0°)
      const priorityAngles = [Math.PI, 0]; // Left, Right
      for (const angle of priorityAngles) {
        patterns.push({
          x: Math.round(radius * Math.cos(angle)),
          y: Math.round(radius * Math.sin(angle)),
        });
      }

      // Then add other angles
      for (let i = 0; i < angleSteps; i++) {
        const angle = (i * 2 * Math.PI) / angleSteps;
        // Skip the priority angles we already added
        if (Math.abs(angle - 0) > 0.1 && Math.abs(angle - Math.PI) > 0.1) {
          patterns.push({
            x: Math.round(radius * Math.cos(angle)),
            y: Math.round(radius * Math.sin(angle)),
          });
        }
      }
    }

    // Efficiently merge unique patterns
    const existingSet = new Set(patterns.map(p => `${p.x},${p.y}`));
    for (const offset of strategicOffsets) {
      const key = `${offset.x},${offset.y}`;
      if (!existingSet.has(key)) {
        patterns.push(offset);
        existingSet.add(key);
      }
    }

    return patterns;
  }

  /**
   * Optimized zoom-independent search patterns
   * @returns {Array} Array of {x, y} offset patterns in screen pixels
   * @private
   */
  _generateZoomIndependentSearchPatterns() {
    // Calculate zoom-aware scaling
    const currentZoom = this.view?.zoom || 15;
    const zoomScale = Math.pow(2, Math.max(0, currentZoom - 10)); // Scale UP for higher zoom

    // Start with a much smaller zoom-scaled northern offset for closer initial positioning
    const baseOffset = 0.00003 * zoomScale; // Reduced from 0.0001 to 0.00003
    const patterns = [{ latitude: baseOffset, longitude: 0 }];

    // Zoom-aware geographic offsets - start much closer and increase gradually
    const radiusSteps = [
      0.00002 * zoomScale,  // Start very close
      0.00005 * zoomScale,
      0.0001 * zoomScale,
      0.00015 * zoomScale,
      0.0002 * zoomScale
    ];
    const angleSteps = 12;

    for (const radius of radiusSteps) {
      for (let i = 0; i < angleSteps; i++) {
        const angle = (i * 2 * Math.PI) / angleSteps;
        patterns.push({
          latitude: radius * Math.sin(angle),   // North/South offset
          longitude: radius * Math.cos(angle), // East/West offset
        });
      }
    }

    return patterns;
  }

  /**
   * Check for significant overlap with optimized algorithm
   * @param {Array} labels - Array of label data objects
   * @returns {boolean} True if significant overlap exists
   * @private
   */
  _hasSignificantOverlap(labels) {
    if (labels.length < 2) return false;

    const threshold = Math.min(Math.ceil(labels.length * 0.2), 5);
    let overlapCount = 0;

    // Early termination optimization
    for (let i = 0; i < labels.length - 1 && overlapCount < threshold; i++) {
      const labelA = labels[i];
      const boxA = this._calculateLabelBoundingBox(labelA.graphic, labelA.position);
      if (!boxA) continue;

      for (let j = i + 1; j < labels.length && overlapCount < threshold; j++) {
        const labelB = labels[j];
        const boxB = this._calculateLabelBoundingBox(labelB.graphic, labelB.position);
        if (!boxB) continue;

        if (this._checkBoundingBoxOverlap(boxA, boxB)) {
          overlapCount++;
        }
      }
    }

    return overlapCount >= threshold;
  }

  /**
   * Show warning message when there are too many label collisions
   * @param {number} labelCount - Number of labels causing collisions
   * @private
   */
  _showCollisionWarning(labelCount) {
    // Prevent showing multiple warnings in quick succession
    const now = Date.now();
    if (this._lastCollisionWarning && (now - this._lastCollisionWarning) < 10000) {
      return; // Don't show warning more than once every 10 seconds
    }
    this._lastCollisionWarning = now;

    // Create warning message element
    const existingWarning = document.getElementById('label-collision-warning');
    if (existingWarning) {
      existingWarning.remove();
    }

    const warningDiv = document.createElement('div');
    warningDiv.id = 'label-collision-warning';
    warningDiv.style.cssText = `
      position: fixed;
      top: 80px;
      right: 20px;
      background-color: #ff9800;
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      z-index: 9999;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      font-size: 14px;
      max-width: 350px;
      animation: slideInRight 0.3s ease-out;
    `;

    warningDiv.innerHTML = `
      <div style="display: flex; align-items: start; gap: 12px;">
        <div style="font-size: 24px; line-height: 1;">⚠️</div>
        <div style="flex: 1;">
          <div style="font-weight: 600; margin-bottom: 4px;">Too Many Label Collisions</div>
          <div style="font-size: 13px; opacity: 0.95;">
            ${labelCount} labels detected. Please zoom in to avoid label conflicts and improve readability.
          </div>
        </div>
        <button id="close-collision-warning" style="
          background: none;
          border: none;
          color: white;
          font-size: 20px;
          cursor: pointer;
          padding: 0;
          line-height: 1;
          opacity: 0.8;
        ">&times;</button>
      </div>
    `;

    // Add slide-in animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInRight {
        from {
          transform: translateX(400px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(warningDiv);

    // Auto-dismiss after 8 seconds
    const dismissTimeout = setTimeout(() => {
      if (warningDiv && warningDiv.parentNode) {
        warningDiv.style.animation = 'slideInRight 0.3s ease-out reverse';
        setTimeout(() => warningDiv.remove(), 300);
      }
    }, 8000);

    // Close button handler
    const closeButton = document.getElementById('close-collision-warning');
    if (closeButton) {
      closeButton.onclick = () => {
        clearTimeout(dismissTimeout);
        warningDiv.style.animation = 'slideInRight 0.3s ease-out reverse';
        setTimeout(() => warningDiv.remove(), 300);
      };
    }

    console.log(`[SimplifiedLabelManager] Displayed collision warning for ${labelCount} labels`);
  }

  /**
   * Enhanced optimizeAllLabels with better performance and context awareness
   */
  optimizeAllLabels() {
    if (!this.view?.map?.layers) {

      return;
    }
    // Collect all labels efficiently
    const allLabelsToOptimize = this._collectLabelsForOptimization();

    if (allLabelsToOptimize.length === 0) {

      return;
    }

    // Safety check: Too many labels for current zoom level
    const MAX_LABELS_FOR_COLLISION = 100; // Reduced from 150 to catch issues earlier
    if (allLabelsToOptimize.length > MAX_LABELS_FOR_COLLISION) {
      console.warn(`[SimplifiedLabelManager] Too many labels (${allLabelsToOptimize.length}) for collision optimization. Please zoom in.`);
      this._showCollisionWarning(allLabelsToOptimize.length);
      return;
    }

    // Early exit if no significant overlap
    if (!this._hasSignificantOverlap(allLabelsToOptimize)) {

      return;
    }

    // Optimize labels (with built-in time limits)
    const optimizedPositionsMap = this._optimizeLabelPositions(allLabelsToOptimize);

    // Apply optimized positions efficiently
    const appliedCount = this._applyOptimizedPositionsEfficiently(
      optimizedPositionsMap, allLabelsToOptimize
    );
  }

  /**
   * Efficiently collect labels for optimization
   * @returns {Array} Labels to optimize
   * @private
   */
  _collectLabelsForOptimization() {
    const allLabelsToOptimize = [];
    const savedPositions = this._getSavedPositions();

    // Use for...of for better performance with large collections
    for (const layer of this.view.map.layers) {
      if (!layer.graphics || !layer.visible) continue;

      // Context matching optimization
      if (!this._isLayerInCurrentContext(layer)) continue;

      for (const graphic of layer.graphics) {
        if (!this._isValidLabelForOptimization(graphic, savedPositions)) continue;

        const labelId = this.getLabelId(graphic);
        if (!labelId || savedPositions[labelId]) continue;

        allLabelsToOptimize.push({
          graphic: graphic,
          labelId: labelId,
          position: {
            x: graphic.symbol?.xoffset || 0,
            y: graphic.symbol?.yoffset || 0,
          },
        });
      }
    }

    return allLabelsToOptimize;
  }

  /**
   * Check if layer is in current context (optimized)
   * @param {Object} layer - Layer to check
   * @returns {boolean} True if in current context
   * @private
   */
  _isLayerInCurrentContext(layer) {
    const layerMapConfigId = layer.mapConfigId || layer.labelFormatInfo?.mapConfigId;
    const layerMapType = layer.mapType || layer.labelFormatInfo?.mapType;

    const configMatch = !this.mapConfigId || !layerMapConfigId || 
                       this.mapConfigId === layerMapConfigId;
    const typeMatch = !this.mapType || !layerMapType || 
                     this.mapType === layerMapType;

    return configMatch && typeMatch;
  }

  /**
   * Check if graphic is a valid label for optimization
   * @param {Object} graphic - Graphic to check
   * @param {Object} savedPositions - Saved positions
   * @returns {boolean} True if valid
   * @private
   */
  _isValidLabelForOptimization(graphic, savedPositions) {
    const isLabel = graphic.symbol?.type === "text" || 
                   graphic.attributes?.isLabel === true;
    return isLabel && graphic.visible;
  }

  /**
   * Efficiently apply optimized positions
   * @param {Map} optimizedPositionsMap - Optimized positions
   * @param {Array} allLabelsToOptimize - Original labels
   * @returns {number} Number of applied positions
   * @private
   */
  _applyOptimizedPositionsEfficiently(optimizedPositionsMap, allLabelsToOptimize) {
    let appliedCount = 0;
    const minMovementThreshold = 4; // Only apply if moved more than 2px

    for (const [labelId, newPosition] of optimizedPositionsMap) {
      const originalLabelData = allLabelsToOptimize.find(l => l.labelId === labelId);
      if (!originalLabelData?.graphic?.symbol) continue;

      // Check if movement is significant enough
      const distanceSquared = Math.pow(newPosition.x - originalLabelData.position.x, 2) + 
                             Math.pow(newPosition.y - originalLabelData.position.y, 2);

      if (distanceSquared > minMovementThreshold) {
        this._applyPositionToGraphic(originalLabelData.graphic, newPosition, labelId);
        appliedCount++;
      }
    }

    return appliedCount;
  }

  /**
   * Apply position to graphic efficiently
   * @param {Object} graphic - Graphic to update
   * @param {Object} newPosition - New position
   * @param {string} labelId - Label ID
   * @private
   */
  _applyPositionToGraphic(graphic, newPosition, labelId) {
    // For geographic coordinate positioning, we move the geometry instead of using offsets
    if (newPosition && typeof newPosition.latitude === 'number' && typeof newPosition.longitude === 'number') {
      // Update graphic geometry to new geographic coordinates
      const newPoint = new Point({ 
        x: newPosition.longitude,  // ArcGIS uses x for longitude
        y: newPosition.latitude,   // ArcGIS uses y for latitude
        spatialReference: { wkid: 4326 } 
      });
      
      graphic.geometry = newPoint;
      
    } else {
      // Fallback: preserve existing symbol properties, don't force reset offsets
      
    }

    // Update tracking efficiently
    const existingData = this.editedLabels.get(labelId) || {
      graphic: graphic,
      text: graphic.symbol.text || graphic.attributes?.text || "",
      fontSize: graphic.symbol.font?.size || this.settings.fontSize,
      fontWeight: graphic.symbol.font?.weight || "normal",
      backgroundColor: graphic.symbol.backgroundColor || null,
      borderLineColor: graphic.symbol.borderLineColor || null,
      borderLineSize: graphic.symbol.borderLineSize || 0,
      mapConfigId: this.mapConfigId,
      mapType: this.mapType,
    };

    existingData.position = newPosition;
    this.editedLabels.set(labelId, existingData);

    // Create visible bounding box if enabled
    if (this.showVisibleBoundingBoxes) {
      this._createVisibleBoundingBox(graphic, labelId);
    }
  }

  /**
   * Set context with optimization
   * @param {string} projectId - Project ID
   * @param {string} mapConfigId - Map configuration ID
   * @param {string} mapType - Map type (optional)
   */
  setContext(projectId, mapConfigId, mapType = null) {
    const contextChanged = this.mapConfigId !== mapConfigId || 
                          (mapType && this.mapType !== mapType);

    // Update context
    this.projectId = projectId;
    this.mapConfigId = mapConfigId;
    this.mapType = mapType;

    // Invalidate caches on context change
    if (contextChanged) {
      this._cachedStorageKey = null;
      this._textMeasurementCache.clear();
      this._boundingBoxCache = new WeakMap();

      // Clear bounding boxes from previous map configuration
      this._removeAllVisibleBoundingBoxes();
    }

    // Update session storage
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem("currentMapConfigId", mapConfigId);
      if (mapType) {
        sessionStorage.setItem("currentMapType", mapType);
      }
    }

    // Clear tracked labels when switching configurations
    if (contextChanged) {

      this.editedLabels.clear();
    }

    // Reload positions with the new context
    this.loadPositions();
  }

  /**
   * Get a unique AND STABLE ID for a label (optimized for performance)
   * @param {Object} label - The label graphic
   * @returns {string|null} A stable unique ID, or null if no suitable attributes found
   */
  getLabelId(label) {
    // Early validation
    if (!label?.attributes) {
      
      return null;
    }

    const attributes = label.attributes;

    // Use cached ID if available
    if (attributes._cachedLabelId) {
      return attributes._cachedLabelId;
    }

    let labelId = null;

    // Prioritized ID generation (optimized order)
    if (attributes.OBJECTID !== undefined && attributes.OBJECTID !== null) {
      labelId = `oid-${attributes.OBJECTID}`;
    } else if (attributes.parentID) {
      labelId = `parent-${attributes.parentID}`;
    } else if (attributes.labelId) {
      labelId = `labelId-${attributes.labelId}`;
    } else if (attributes.id && typeof attributes.id !== "object") {
      labelId = `id-${attributes.id}`;
    } else if (attributes.MapLabel) {
      labelId = `data-MapLabel-${attributes.MapLabel}`;
    } else if (label.geometry?.type === "point") {
      // Geometry-based fallback with better uniqueness
      const point = label.geometry;
      const x = point.x.toFixed(6); // More precision for coordinates
      const y = point.y.toFixed(6);
      const textPart = attributes.text || label.symbol?.text || "no-text";
      
      // Include more attributes to ensure uniqueness for labels at same location
      const additionalId = attributes.name || attributes.title || attributes.id || 
                          (label.symbol?.text ? label.symbol.text.substring(0, 20) : "");
      
      labelId = `geom-${x}-${y}-${textPart.substring(0, 30)}-${additionalId}`.replace(/[^a-zA-Z0-9\-_.]/g, '_');
    }

    // Cache the result for future use
    if (labelId) {
      attributes._cachedLabelId = labelId;
    } else {
      
    }

    return labelId;
  }

  /**
   * Optimized zoom-independent offset calculation
   * @param {Object} graphic - The label graphic
   * @param {Object} targetPosition - Target position
   * @returns {Object} Screen pixel offset {x, y}
   * @private
   */
  _calculateZoomIndependentOffset(graphic, targetPosition) {
    if (!graphic?.geometry || !this.view) {
      return { x: 0, y: 0 };
    }

    try {
      const anchorPoint = graphic.geometry;
      const anchorScreen = this.view.toScreen(anchorPoint);
      
      if (!anchorScreen) return { x: 0, y: 0 };

      // Optimized position handling
      if (targetPosition && typeof targetPosition.x === 'number' && typeof targetPosition.y === 'number') {
        return {
          x: targetPosition.x - anchorScreen.x,
          y: targetPosition.y - anchorScreen.y
        };
      }

      if (targetPosition?.latitude !== undefined && targetPosition?.longitude !== undefined) {
        const targetScreen = this.view.toScreen({
          latitude: targetPosition.latitude,
          longitude: targetPosition.longitude,
          spatialReference: this.view.spatialReference
        });
        
        if (targetScreen) {
          return {
            x: targetScreen.x - anchorScreen.x,
            y: targetScreen.y - anchorScreen.y
          };
        }
      }

      return { x: 0, y: 0 };
    } catch (error) {
      
      return { x: 0, y: 0 };
    }
  }

  /**
   * Apply coordinate-locked positioning using geographic coordinates instead of pixel offsets
   * @param {Object} graphic - The label graphic
   * @param {Object} offset - Screen pixel offset {x, y} to be converted to geographic offset
   * @private
   */
  _applyZoomIndependentPosition(graphic, offset) {
    if (!graphic?.symbol || !graphic?.geometry) return false;

    try {
      // Convert pixel offset to geographic offset at current zoom level
      if (offset && (offset.x !== 0 || offset.y !== 0)) {
        const currentGeometry = graphic.geometry;
        const currentScreen = this.view.toScreen(currentGeometry);
        
        if (currentScreen) {
          // Calculate new screen position
          const newScreenPosition = {
            x: currentScreen.x + (offset.x || 0),
            y: currentScreen.y + (offset.y || 0)
          };
          
          // Convert back to geographic coordinates
          const newMapPoint = this.view.toMap(newScreenPosition);
          
          if (newMapPoint) {
            // Update the graphic's geometry to the new geographic position
            graphic.geometry = new Point({
              x: newMapPoint.longitude,
              y: newMapPoint.latitude,
              spatialReference: { wkid: 4326 }
            });
            
            // Clear any existing pixel offsets since we're using geographic positioning
            const newSymbol = graphic.symbol.clone();
            newSymbol.xoffset = 0;
            newSymbol.yoffset = 0;
            newSymbol.horizontalAlignment = "left";
            newSymbol.verticalAlignment = "middle";
            graphic.symbol = newSymbol;
            return true;
          }
        }
      } else {
        // No offset, just ensure symbol has no pixel offsets
        const newSymbol = graphic.symbol.clone();
        newSymbol.xoffset = 0;
        newSymbol.yoffset = 0;
        newSymbol.horizontalAlignment = "left";
        newSymbol.verticalAlignment = "middle";
        graphic.symbol = newSymbol;
        return true;
      }
      
      return false;
    } catch (error) {
      
      return false;
    }
  }

  /**
   * Enhanced processLayer method with optimizations
   * @param {Object} layer - The layer to process
   * @returns {Array} Array of label IDs found
   */
  processLayer(layer) {
    if (!layer?.graphics) return [];

    console.log(`[processLayer] Processing layer:`, {
      layerId: layer.id,
      layerTitle: layer.title,
      graphicsCount: layer.graphics.length,
      layerMapConfigId: layer.mapConfigId,
      layerMapType: layer.mapType,
      managerMapConfigId: this.mapConfigId,
      managerMapType: this.mapType
    });

    // Context validation optimization
    if (!this._validateLayerContext(layer)) {
      console.warn(`[processLayer] Layer context validation failed for ${layer.id}`);
      return [];
    }
    // Get label options and saved positions once
    const labelOptions = layer.labelConfiguration?.labelOptions ||
                        layer.layerConfiguration?.labelOptions || {};
    const savedPositions = this._getSavedPositions();

    const labelsFound = [];
    const labelsToOptimize = [];

    // Set context on layer if missing
    this._ensureLayerContext(layer);

    // Process graphics efficiently
    for (const graphic of layer.graphics) {
      const result = this._processGraphicOptimized(
        graphic, labelOptions, savedPositions, labelsToOptimize
      );

      if (result.labelId) {
        labelsFound.push(result.labelId);
      }
    }

    console.log(`[processLayer] Processed ${labelsFound.length} labels from layer ${layer.id}`);

    // Apply optimizations if needed
    if (labelsToOptimize.length > 0 && this.settings.avoidCollisions) {
      this._applyOptimizedPositionsToLayer(labelsToOptimize);
    }

    // Mark layer as processed
    this._markLayerAsProcessed(layer);
    return labelsFound;
  }

  /**
   * Validate layer context efficiently
   * @param {Object} layer - Layer to validate
   * @returns {boolean} True if valid
   * @private
   */
  _validateLayerContext(layer) {
    const layerMapConfigId = layer.mapConfigId || layer.labelFormatInfo?.mapConfigId;
    const layerMapType = layer.mapType || layer.labelFormatInfo?.mapType;

    const configMismatch = this.mapConfigId && layerMapConfigId && 
                          this.mapConfigId !== layerMapConfigId;
    const typeMismatch = this.mapType && layerMapType && 
                        this.mapType !== layerMapType;

    if (configMismatch || typeMismatch) {
      
      return false;
    }

    return true;
  }

  /**
   * Ensure layer has context information
   * @param {Object} layer - Layer to update
   * @private
   */
  _ensureLayerContext(layer) {
    if (this.mapConfigId && !layer.mapConfigId) {
      layer.mapConfigId = this.mapConfigId;
      if (layer.labelFormatInfo) {
        layer.labelFormatInfo.mapConfigId = this.mapConfigId;
      }
    }

    if (this.mapType && !layer.mapType) {
      layer.mapType = this.mapType;
      if (layer.labelFormatInfo) {
        layer.labelFormatInfo.mapType = this.mapType;
      }
    }
  }

  /**
   * Process individual graphic optimized
   * @param {Object} graphic - Graphic to process
   * @param {Object} labelOptions - Label options
   * @param {Object} savedPositions - Saved positions
   * @param {Array} labelsToOptimize - Array to add labels needing optimization
   * @returns {Object} Processing result
   * @private
   */
  _processGraphicOptimized(graphic, labelOptions, savedPositions, labelsToOptimize) {
    const isLabel = graphic.symbol?.type === "text" || 
                   graphic.attributes?.isLabel === true;
    
    if (!isLabel) return { labelId: null };

    const labelId = this.getLabelId(graphic);
    if (!labelId) return { labelId: null };

    // Add context metadata efficiently
    this._addContextToGraphic(graphic);

    const hasSavedStyle = savedPositions[labelId] !== undefined;

    if (!hasSavedStyle) {
      this._applyInitialLabelStyle(graphic, labelOptions);
      
      labelsToOptimize.push({
        graphic: graphic,
        labelId: labelId,
        position: {
          x: graphic.symbol?.xoffset || 0,
          y: graphic.symbol?.yoffset || 0,
        },
      });
    }

    // Update tracking efficiently
    this._updateLabelTracking(graphic, labelId);

    // Apply saved position if it exists
    if (hasSavedStyle) {
      this._applySavedPosition(graphic);
    }

    return { labelId };
  }

  /**
   * Add context to graphic efficiently
   * @param {Object} graphic - Graphic to update
   * @private
   */
  _addContextToGraphic(graphic) {
    if (!graphic.attributes) return;

    if (!graphic.attributes.mapConfigId && this.mapConfigId) {
      graphic.attributes.mapConfigId = this.mapConfigId;
    }
    if (!graphic.attributes.mapType && this.mapType) {
      graphic.attributes.mapType = this.mapType;
    }
  }

  /**
   * Apply initial label style optimized
   * @param {Object} graphic - Graphic to style
   * @param {Object} labelOptions - Style options
   * @private
   */
  _applyInitialLabelStyle(graphic, labelOptions) {
    if (!graphic.symbol) return;

    // Apply bold option
    if (labelOptions.bold === true && graphic.symbol.font) {
      graphic.symbol.font.weight = "bold";
    }

    // Apply background options
    if (labelOptions.whiteBackground === true) {
      graphic.symbol.backgroundColor = [255, 255, 255, 0.9];
      graphic.symbol.kerning = true;
      graphic.symbol.horizontalAlignment = "center";
      graphic.symbol.borderLineColor = [220, 220, 220, 0.5];
      graphic.symbol.borderLineSize = 0.5;
      graphic.symbol.haloSize = 0;
      graphic.symbol.haloColor = null;
    } else if (labelOptions.whiteBackground === false) {
      graphic.symbol.backgroundColor = null;
      graphic.symbol.borderLineColor = null;
      graphic.symbol.borderLineSize = 0;
    }

    // Apply zoom-independent positioning
    this._ensureZoomIndependentPosition(graphic);
  }

  /**
   * Ensure geographic coordinate positioning (no pixel offsets)
   * @param {Object} graphic - Graphic to update
   * @private
   */
  _ensureZoomIndependentPosition(graphic) {
    // Always reset to geographic coordinate positioning, no pixel offsets
    const defaultPosition = this._calculateOptimalDefaultOffset(graphic);
    this._applyPositionToGraphic(graphic, defaultPosition, this.getLabelId(graphic));
    
    // Clear any pixel offsets that may have been set
    if (graphic.symbol) {
      const newSymbol = graphic.symbol.clone();
      newSymbol.xoffset = 0;
      newSymbol.yoffset = 0;
      graphic.symbol = newSymbol;
    }
  }

  /**
   * Update label tracking efficiently
   * @param {Object} graphic - Graphic to track
   * @param {string} labelId - Label ID
   * @private
   */
  _updateLabelTracking(graphic, labelId) {
    // Preserve existing original position if it exists, or get from attributes if stored there
    const existingData = this.editedLabels.get(labelId);
    let originalPosition = existingData?.originalPosition;

    // If no existing original position, check if it's stored in attributes
    if (!originalPosition && graphic.attributes?.dataPointGeometry) {
      originalPosition = graphic.attributes.dataPointGeometry;
    }

    // If still no original position, use current geometry and store it in attributes
    if (!originalPosition) {
      originalPosition = graphic.geometry.clone();
      // Store in attributes so it persists across saves/loads
      if (!graphic.attributes.dataPointGeometry) {
        graphic.attributes.dataPointGeometry = originalPosition;
      }
    }

    // Check saved positions for visibility state
    const savedPositions = this._getSavedPositions();
    const savedData = savedPositions[labelId];
    // Preserve visibility from: 1) saved data, 2) existing tracking data, 3) attributes.visible (intended visibility), 4) default to true
    // NOTE: graphic.visible may be false due to zoom-based visibility (visibleAtAllZooms=false),
    // but attributes.visible stores the user's intended visibility state (always true for new labels)
    const labelVisible = savedData?.visible !== undefined ? savedData.visible :
                        (existingData?.visible !== undefined ? existingData.visible :
                        (graphic.attributes?.visible !== undefined ? graphic.attributes.visible : true));

    const labelData = {
      graphic: graphic,
      position: {
        x: graphic.symbol?.xoffset || 0,
        y: graphic.symbol?.yoffset || 0,
      },
      fontSize: graphic.symbol?.font?.size || this.settings.fontSize,
      text: graphic.symbol?.text || graphic.attributes?.text || "",
      fontWeight: graphic.symbol?.font?.weight || "normal",
      backgroundColor: graphic.symbol?.backgroundColor || null,
      borderLineColor: graphic.symbol?.borderLineColor || null,
      borderLineSize: graphic.symbol?.borderLineSize || 0,
      visible: labelVisible, // CRITICAL: Preserve visibility state
      mapConfigId: this.mapConfigId,
      mapType: this.mapType,
      _zoomIndependent: true,
      originalPosition: originalPosition  // Preserve original position
    };

    console.log(`[_updateLabelTracking] Tracking label ${labelId} with context:`, {
      mapConfigId: this.mapConfigId,
      mapType: this.mapType,
      text: labelData.text.substring(0, 30),
      visible: labelVisible
    });

    this.editedLabels.set(labelId, labelData);

    // CRITICAL: Apply saved visibility to the graphic immediately
    graphic.visible = labelVisible;
    // CRITICAL: Also update attributes.visible so zoom handlers respect saved state
    if (graphic.attributes) {
      graphic.attributes.visible = labelVisible;
    }

    // Create visible bounding box if enabled
    
    if (this.showVisibleBoundingBoxes) {
      
      this._createVisibleBoundingBox(graphic, labelId);
    } else {
      
    }
    
    // Schedule collision resolution after all labels are processed - run quickly for immediate feedback
    // This should run regardless of whether bounding boxes are visible
    // Run 3 passes during initial layer creation for better placement, avoiding collisions from the start
    this._scheduleCollisionResolution(500, 3); // Run 3 collision detection passes after 500ms
  }

  /**
   * Apply optimized positions to layer
   * @param {Array} labelsToOptimize - Labels to optimize
   * @private
   */
  _applyOptimizedPositionsToLayer(labelsToOptimize) {
    const optimizedPositions = this._optimizeLabelPositionsZoomIndependent(labelsToOptimize);

    for (const [labelId, position] of optimizedPositions) {
      const labelData = labelsToOptimize.find(l => l.labelId === labelId);
      if (!labelData?.graphic) continue;

      // Apply geographic positioning directly
      this._applyPositionToGraphic(labelData.graphic, position, labelId);
      
      // Clear any pixel offsets
      if (labelData.graphic.symbol) {
        const newSymbol = labelData.graphic.symbol.clone();
        newSymbol.xoffset = 0;
        newSymbol.yoffset = 0;
        labelData.graphic.symbol = newSymbol;
      }
      
      const existingData = this.editedLabels.get(labelId);
      if (existingData) {
        existingData.position = position;
        existingData._coordinateBased = true;
      }
    }
  }

  /**
   * Mark layer as processed
   * @param {Object} layer - Layer to mark
   * @private
   */
  _markLayerAsProcessed(layer) {
    layer._labelManagerProcessed = true;
    layer._processedWithConfigId = this.mapConfigId;
    layer._processedWithMapType = this.mapType;
  }

  /**
   * Calculate optimal default geographic offset
   * @param {Object} graphic - The label graphic
   * @returns {Object} Default geographic coordinate offset {latitude, longitude}
   * @private
   */
  _calculateOptimalDefaultOffset(graphic) {
    if (!graphic?.geometry) return { latitude: 0, longitude: 0 };

    // Position label slightly north of the data point (geographic coordinates)
    // Using very small offsets in decimal degrees (~2-3 meters)
    const latitudeOffset = 0.00000;  // Approximately 2-3 meters north
    const longitudeOffset = 0.00100;       // Horizontally centered

    return { 
      latitude: graphic.geometry.y + latitudeOffset, 
      longitude: graphic.geometry.x + longitudeOffset 
    };
  }

  /**
   * Optimized zoom-independent label position optimization
   * @param {Array} labelsToOptimize - Array of labels to optimize
   * @returns {Map} Map of labelId to optimized position
   * @private
   */
  _optimizeLabelPositionsZoomIndependent(labelsToOptimize) {
    if (!labelsToOptimize?.length) return new Map();
    const optimizedPositions = new Map();
    const sortedLabels = this._optimizedLabelSorting(labelsToOptimize);
    const searchPatterns = this._zoomIndependentPatterns;

    // Safety checks to prevent infinite loops
    const startTime = performance.now();
    const MAX_OPTIMIZATION_TIME = 4000; // 4 seconds max

    // Process with performance optimization
    for (let index = 0; index < sortedLabels.length; index++) {
      // Safety check: Time limit (check every label for better enforcement)
      const elapsed = performance.now() - startTime;
      if (elapsed > MAX_OPTIMIZATION_TIME) {
        console.warn(`[SimplifiedLabelManager] Zoom-independent optimization stopped - time limit exceeded (${Math.round(elapsed)}ms). Processed ${index}/${sortedLabels.length} labels.`);
        this._showCollisionWarning(sortedLabels.length);
        break;
      }
      const { graphic, labelId, position } = sortedLabels[index];

      if (!position) {
        // Default to geographic coordinate offset instead of pixel offset
        const defaultPosition = this._calculateOptimalDefaultOffset(graphic);
        optimizedPositions.set(labelId, defaultPosition);
        continue;
      }

      const bestPosition = this._findOptimalPositionZoomIndependent(
        graphic, position, searchPatterns, sortedLabels, optimizedPositions, index, startTime, MAX_OPTIMIZATION_TIME
      );

      optimizedPositions.set(labelId, bestPosition);
    }

    return optimizedPositions;
  }

  /**
   * Find optimal position with zoom independence
   * @param {Object} graphic - Label graphic
   * @param {Object} position - Current position
   * @param {Array} searchPatterns - Search patterns
   * @param {Array} sortedLabels - All sorted labels
   * @param {Map} optimizedPositions - Current optimized positions
   * @param {number} index - Current index
   * @param {number} startTime - Optimization start time
   * @param {number} maxTime - Maximum time allowed
   * @returns {Object} Optimal position
   * @private
   */
  _findOptimalPositionZoomIndependent(graphic, position, searchPatterns, sortedLabels, optimizedPositions, index, startTime, maxTime) {
    // Start with the original geographic location of the data point
    const originalGeometry = graphic.geometry;
    let bestPosition = {
      latitude: originalGeometry.y + (searchPatterns[0]?.latitude || 0.00003),
      longitude: originalGeometry.x + (searchPatterns[0]?.longitude || 0)
    };
    let minOverlapScore = Number.MAX_VALUE;
    let foundPerfectPosition = false;

    for (const pattern of searchPatterns) {
      if (foundPerfectPosition) break;

      // Check time limit inside inner loop to ensure we respect it
      if (startTime && maxTime && performance.now() - startTime > maxTime) {
        break;
      }

      // Calculate absolute geographic position from data point + pattern offset
      const testPosition = {
        latitude: originalGeometry.y + pattern.latitude,
        longitude: originalGeometry.x + pattern.longitude,
      };

      const testBox = this._calculateLabelBoundingBoxZoomIndependent(graphic, testPosition);
      if (!testBox) continue;

      let overlapScore = this._calculateTotalOverlapScoreZoomIndependent(
        testBox, { graphic, position }, sortedLabels, optimizedPositions, index
      );

      overlapScore += this._calculatePositionPenalty(position, testPosition, pattern);

      if (overlapScore < minOverlapScore) {
        minOverlapScore = overlapScore;
        bestPosition = testPosition;

        if (overlapScore <= this.settings.collisionDetectionThreshold) {
          foundPerfectPosition = true;
        }
      }
    }

    return bestPosition;
  }

  /**
   * Calculate bounding box with zoom independence optimized
   * @param {Object} label - The label graphic
   * @param {Object} position - Position in screen pixels
   * @returns {Object} Bounding box
   * @private
   */
  _calculateLabelBoundingBoxZoomIndependent(label, position) {
    if (!label?.symbol) return null;

    const text = label.symbol.text || label.attributes?.text || "";
    if (!text) return null;

    const fontSize = label.symbol.font?.size || this.settings.fontSize;
    const fontWeight = label.symbol.font?.weight || "normal";
    const fontFamily = label.symbol.font?.family || this.settings.fontFamily;

    // Use optimized text measurement
    const textDimensions = this._getOptimizedTextDimensions(text, fontSize, fontWeight, fontFamily);

    // Calculate padding efficiently
    const hasBackground = !!label.symbol.backgroundColor;
    const borderSize = label.symbol.borderLineSize || 0;
    const padding = hasBackground ? Math.max(4, borderSize * 2) : 2;
    const haloSize = label.symbol.haloSize || 0;
    const extraPadding = Math.max(padding, haloSize);

    return {
      x: position.x - textDimensions.width / 2 - extraPadding,
      y: position.y - textDimensions.height / 2 - extraPadding,
      width: textDimensions.width + extraPadding * 2,
      height: textDimensions.height + extraPadding * 2,
      centerX: position.x,
      centerY: position.y,
      textWidth: textDimensions.width,
      textHeight: textDimensions.height,
    };
  }

  /**
   * Calculate overlap score with zoom independence optimized
   * @param {Object} testBox - Test bounding box
   * @param {Object} currentLabel - Current label
   * @param {Array} allLabels - All labels
   * @param {Map} optimizedPositions - Optimized positions
   * @param {number} currentIndex - Current index
   * @returns {number} Overlap score
   * @private
   */
  _calculateTotalOverlapScoreZoomIndependent(testBox, currentLabel, allLabels, optimizedPositions, currentIndex) {
    let totalScore = 0;
    const maxScore = 10000;

    // Check optimized labels
    for (let i = 0; i < currentIndex && totalScore < maxScore; i++) {
      const otherLabel = allLabels[i];
      const otherPosition = optimizedPositions.get(otherLabel.labelId);

      if (!otherPosition) continue;

      const otherBox = this._calculateLabelBoundingBoxZoomIndependent(otherLabel.graphic, otherPosition);
      if (!otherBox) continue;

      const overlapArea = this._calculateOverlapArea(testBox, otherBox);
      if (overlapArea > 0) {
        const importanceWeight = this._getLabelImportanceWeight(otherLabel.graphic);
        totalScore += overlapArea * importanceWeight;
      }
    }

    // Early termination
    if (totalScore >= maxScore) return totalScore;

    // Check unprocessed labels
    for (let i = currentIndex + 1; i < allLabels.length && totalScore < maxScore; i++) {
      const otherLabel = allLabels[i];
      const otherBox = this._calculateLabelBoundingBoxZoomIndependent(otherLabel.graphic, otherLabel.position);

      if (!otherBox) continue;

      const overlapArea = this._calculateOverlapArea(testBox, otherBox);
      if (overlapArea > 0) {
        totalScore += overlapArea * 0.5;
      }
    }

    // Early termination
    if (totalScore >= maxScore) return totalScore;

    // Check collisions with ALL data point circles
    const pixelToMapRatio = this.view?.extent?.width / this.view?.width || 0.0001;
    let dataPointCollisionCount = 0;

    for (let i = 0; i < allLabels.length && totalScore < maxScore; i++) {
      if (i === currentIndex) continue; // Skip current label's own data point

      const otherLabel = allLabels[i];

      // Get data point geometry from editedLabels tracking first, then fallback to graphic
      let dataPointGeometry = null;
      if (otherLabel.labelId) {
        const trackedData = this.editedLabels.get(otherLabel.labelId);
        dataPointGeometry = trackedData?.originalPosition;
      }

      // Fallback to graphic attributes or geometry
      if (!dataPointGeometry) {
        dataPointGeometry = otherLabel.graphic?.attributes?.dataPointGeometry ||
                           otherLabel.graphic?.geometry;
      }

      if (dataPointGeometry) {
        const hasCollision = this._checkCollisionWithDataPoint(testBox, dataPointGeometry, pixelToMapRatio);
        if (hasCollision) {
          // Heavy penalty for colliding with data points
          totalScore += 5000;
          dataPointCollisionCount++;
        }
      }
    }

    return totalScore;
  }

  /**
   * Optimized saved position application
   * @param {Object} label - The label graphic
   * @returns {boolean} Whether position was applied successfully
   * @private
   */
  _applySavedPosition(label) {
    if (!label?.symbol) return false;

    try {
      const labelId = this.getLabelId(label);
      if (!labelId) return false;

      const savedPositions = this._getSavedPositions();
      const savedData = savedPositions[labelId];

      if (!savedData) return false;

      // Context validation
      if (!this._validateSavedDataContext(savedData)) return false;
      // Apply saved properties efficiently
      const success = this._applyAllSavedProperties(label, savedData, labelId);

      if (success) {
        this._updateTrackingWithSavedData(label, savedData, labelId);
        
      }

      return success;
    } catch (error) {
      
      return false;
    }
  }

  /**
   * Validate saved data context
   * @param {Object} savedData - Saved data to validate
   * @returns {boolean} True if valid
   * @private
   */
  _validateSavedDataContext(savedData) {
    if (savedData.mapConfigId && this.mapConfigId && 
        savedData.mapConfigId !== this.mapConfigId) {
      
      return false;
    }

    if (savedData.mapType && this.mapType && 
        savedData.mapType !== this.mapType) {
      
      return false;
    }

    return true;
  }

  /**
   * Apply all saved properties efficiently
   * @param {Object} label - Label to update
   * @param {Object} savedData - Saved data
   * @param {string} labelId - Label ID
   * @returns {boolean} Success
   * @private
   */
  _applyAllSavedProperties(label, savedData, labelId) {
    const newSymbol = label.symbol.clone();

    // FIRST: Apply saved geographic position if available
    if (savedData.geographicPosition &&
        savedData.geographicPosition.latitude &&
        savedData.geographicPosition.longitude) {
      // CRITICAL FIX: Create the point in WGS84 and then convert to map's spatial reference if needed
      // This ensures proper coordinate handling regardless of what projection the map uses
      let geoPoint = new Point({
        x: savedData.geographicPosition.longitude,
        y: savedData.geographicPosition.latitude,
        spatialReference: { wkid: 4326 }
      });

      // Convert to the view's spatial reference if it's Web Mercator
      const viewSR = this.view?.spatialReference?.wkid;
      if (viewSR === 3857 || viewSR === 102100) {
        try {
          const mercatorPoint = webMercatorUtils.geographicToWebMercator(geoPoint);
          if (mercatorPoint) {
            geoPoint = mercatorPoint;
          }
        } catch (conversionError) {
          console.error("[LabelManager] Error converting geographic to Web Mercator:", conversionError);
          // Keep the WGS84 point - ArcGIS may still handle it
        }
      }

      // Update the label's actual geometry to the saved position
      label.geometry = geoPoint;
    }

    // Apply saved alignment (but force pixel offsets to 0 for geographic positioning)
    if (savedData.symbolProperties) {
      newSymbol.xoffset = 0; // Always 0 for geographic positioning
      newSymbol.yoffset = 0; // Always 0 for geographic positioning
      newSymbol.horizontalAlignment = savedData.symbolProperties.horizontalAlignment || "center";
      newSymbol.verticalAlignment = savedData.symbolProperties.verticalAlignment || "bottom";
    } else {
      // Fallback for legacy data
      newSymbol.xoffset = 0;
      newSymbol.yoffset = 0;
      newSymbol.horizontalAlignment = "center";
      newSymbol.verticalAlignment = "bottom";
    }

    // Ensure font object exists
    if (!newSymbol.font) {
      newSymbol.font = {
        family: this.settings.fontFamily,
        size: this.settings.fontSize,
        weight: "normal",
      };
    }

    // Apply font properties
    if (savedData.fontSize !== undefined) {
      newSymbol.font.size = savedData.fontSize;
    }

    if (savedData.fontWeight) {
      newSymbol.font.weight = savedData.fontWeight;
    }

    // Apply background properties
    if (savedData.backgroundColor) {
      newSymbol.backgroundColor = savedData.backgroundColor;
      newSymbol.borderLineColor = savedData.borderLineColor || null;
      newSymbol.borderLineSize = savedData.borderLineSize || 0;
      newSymbol.kerning = true;
      newSymbol.horizontalAlignment = "center";
      newSymbol.haloSize = 0;
      newSymbol.haloColor = null;
    }

    // Apply other saved symbol properties
    if (savedData.symbolProperties) {
      if (savedData.symbolProperties.haloSize !== undefined) {
        newSymbol.haloSize = savedData.symbolProperties.haloSize;
      }
      if (savedData.symbolProperties.haloColor !== undefined) {
        newSymbol.haloColor = savedData.symbolProperties.haloColor;
      }
      if (savedData.symbolProperties.kerning !== undefined) {
        newSymbol.kerning = savedData.symbolProperties.kerning;
      }
    }

    // Apply symbol and mark with persistent flags
    label.symbol = newSymbol;
    this._markLabelWithPersistentFlags(label, savedData);

    // IMPORTANT: Restore visibility state from saved data
    if (savedData.visible !== undefined) {
      label.visible = savedData.visible;
      // Also update attributes.visible so zoom handlers respect saved state
      if (label.attributes) {
        label.attributes.visible = savedData.visible;
      }
    }

    return true;
  }

  /**
   * Mark label with persistent flags
   * @param {Object} label - Label to mark
   * @param {Object} savedData - Saved data
   * @private
   */
  _markLabelWithPersistentFlags(label, savedData) {
    if (!label.attributes) return;

    label.attributes._persistentStyle = true;
    label.attributes._fontSize = savedData.fontSize;
    label.attributes._fontWeight = savedData.fontWeight;
    label.attributes._hasBackground = !!savedData.backgroundColor;
    label.attributes._visible = savedData.visible;
    // CRITICAL: Set attributes.visible (not just _visible) so zoom handlers respect user's choice
    if (savedData.visible !== undefined) {
      label.attributes.visible = savedData.visible;
    }
    label.attributes.mapConfigId = savedData.mapConfigId || this.mapConfigId;
    label.attributes.mapType = savedData.mapType || this.mapType;
  }

  /**
   * Update tracking with saved data
   * @param {Object} label - Label graphic
   * @param {Object} savedData - Saved data
   * @param {string} labelId - Label ID
   * @private
   */
  _updateTrackingWithSavedData(label, savedData, labelId) {
    // Try to get the original data point geometry from attributes or saved data
    let originalPosition = label.attributes?.dataPointGeometry || savedData.originalPosition;

    // If not found, use current geometry as fallback and store it
    if (!originalPosition) {
      originalPosition = label.geometry.clone();
      if (!label.attributes.dataPointGeometry) {
        label.attributes.dataPointGeometry = originalPosition;
      }
    }

    // Determine visibility from saved data
    const labelVisible = savedData.visible !== false;

    this.editedLabels.set(labelId, {
      graphic: label,
      position: {
        x: label.symbol.xoffset || 0,
        y: label.symbol.yoffset || 0,
      },
      fontSize: savedData.fontSize || label.symbol.font?.size || this.settings.fontSize,
      text: label.symbol.text || label.attributes?.text || "",
      fontWeight: savedData.fontWeight || label.symbol.font?.weight || "normal",
      backgroundColor: savedData.backgroundColor || null,
      borderLineColor: savedData.borderLineColor || null,
      borderLineSize: savedData.borderLineSize || 0,
      visible: labelVisible, // CRITICAL: Store visibility in tracking data
      manuallyPositioned: savedData.manuallyPositioned || false, // CRITICAL: Restore manual positioning flag
      _persistentStyle: true,
      mapConfigId: savedData.mapConfigId || this.mapConfigId,
      mapType: savedData.mapType || this.mapType,
      originalPosition: originalPosition  // Store original position for zoom-based repositioning
    });

    label.visible = labelVisible;
    // Also update attributes.visible so zoom handlers respect saved state
    if (label.attributes) {
      label.attributes.visible = labelVisible;
    }

    // Log if this label was manually positioned
    if (savedData.manuallyPositioned) {
      const labelText = label.symbol.text || labelId;
      console.log(`[LabelManager] 🔓 Restored manually positioned flag for label "${labelText}" (${labelId})`);
    }
  }

  /**
   * Get saved positions with caching
   * @returns {Object} Map of saved positions by label ID
   * @private
   */
  _getSavedPositions() {
    try {
      const savedData = localStorage.getItem(this.getStorageKey());
      return savedData ? JSON.parse(savedData) : {};
    } catch (error) {
      
      return {};
    }
  }

  /**
   * Setup optimized auto-save with debouncing
   * @private
   */
  _setupOptimizedAutoSave() {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
    }

    this.autoSaveInterval = setInterval(() => {
      if (this.editedLabels.size > 0) {
        this._debouncedSave();
      }
    }, this.settings.autoSaveIntervalMs);
  }
  
  /**
   * Reset collision tracking state
   * Call this when labels are cleared, zoom changes significantly, or new map is loaded
   */
  resetCollisionTracking() {
    this.lastCollisionCount = null;
    this.consecutiveSameCollisionCount = 0;
    this.collisionDetectionDisabled = false;
    console.log(`🔄 Collision tracking reset - detection re-enabled`);
  }

  /**
   * Setup zoom change detection to maintain geographic positions
   * @private
   */
  _setupZoomChangeDetection() {
    if (!this.view) return;

    // Store previous zoom level for comparison
    this._previousZoom = this.view.zoom;
    this._lastSignificantZoom = this.view.zoom;
    // Track whether labels were visible based on zoom (for threshold crossing detection)
    this._labelsWereVisibleByZoom = this.view.zoom >= this.settings.minZoom;

    // Watch for zoom level changes
    this.view.watch("zoom", (newZoom) => {
      const zoomDifference = Math.abs(newZoom - this._previousZoom);

      // Check if we've crossed the minZoom visibility threshold
      const labelsNowVisibleByZoom = newZoom >= this.settings.minZoom;
      if (labelsNowVisibleByZoom !== this._labelsWereVisibleByZoom) {
        console.log(`[LabelManager] Zoom threshold crossed (zoom: ${newZoom.toFixed(2)}, minZoom: ${this.settings.minZoom}), updating label visibility to: ${labelsNowVisibleByZoom}`);
        this._updateLabelVisibilityForZoom(labelsNowVisibleByZoom);
        this._labelsWereVisibleByZoom = labelsNowVisibleByZoom;
      }

      // Only act on significant zoom changes (avoid minor adjustments)
      if (zoomDifference > 0.1) {
        // Regenerate search patterns for the new zoom level to get closer positioning
        this._precomputeSearchPatterns();

        // DISABLED: Manual label positions are now permanent regardless of zoom level
        // Users must explicitly reset labels if they want auto-repositioning
        // Previously, major zoom changes (1.5+ levels) would clear manual positioning flags

        // Still reset collision tracking on major zoom changes for non-manual labels
        const majorZoomChange = Math.abs(newZoom - this._lastSignificantZoom);
        if (majorZoomChange >= this._zoomThreshold) {
          console.log(`[LabelManager] Major zoom change detected (${majorZoomChange.toFixed(2)} levels), resetting collision tracking (manual labels preserved)`);

          // Reset collision tracking on major zoom changes
          this.resetCollisionTracking();

          // Update the last significant zoom level
          this._lastSignificantZoom = newZoom;
        }

        this._previousZoom = newZoom;
      }
    });

    // Watch for view stationary changes (fires when zoom/pan ends)
    this.view.watch("stationary", (isStationary) => {
      if (isStationary) {
        // Debounce position maintenance to avoid running too frequently
        if (this._zoomCollisionTimer) {
          clearTimeout(this._zoomCollisionTimer);
        }

        this._zoomCollisionTimer = setTimeout(() => {
          // Only run collision detection if we explicitly want it
          // Note: No longer calling _maintainGeographicPositions() since we now
          // save collision-free positions as permanent home positions
          if (this.settings.avoidCollisions) {
            this._runMinimalCollisionDetection().catch(error => {
              // Silent error handling
            });
          }
        }, 800); // Wait 800ms after view becomes stationary for better performance
      }
    });
  }

  /**
   * Update label visibility based on zoom level threshold
   * @param {boolean} shouldBeVisible - Whether labels should be visible
   * @private
   */
  _updateLabelVisibilityForZoom(shouldBeVisible) {
    const savedPositions = this._getSavedPositions();
    let updatedCount = 0;

    // Update visibility for all tracked labels
    for (const [labelId, labelData] of this.editedLabels) {
      if (!labelData.graphic) continue;

      try {
        // Get user's explicit visibility setting (default to true if not set)
        const savedVisible = savedPositions[labelId]?.visible;
        const userSetVisibility = savedVisible !== undefined ? savedVisible :
                                 (labelData.visible !== undefined ? labelData.visible : true);

        // Only show if user hasn't explicitly hidden AND zoom is sufficient
        const finalVisibility = userSetVisibility && shouldBeVisible;
        labelData.graphic.visible = finalVisibility;
        updatedCount++;
      } catch (error) {
        // Silent error handling
      }
    }

    // Also update labels in graphics layers that may not be tracked
    if (this.view?.map?.layers) {
      this.view.map.layers.forEach(layer => {
        if (layer.type !== 'graphics' || !layer.graphics) return;

        layer.graphics.forEach(graphic => {
          if (!graphic.attributes?.isLabel) return;

          try {
            // Get user's explicit visibility from attributes (default to true)
            const userSetVisibility = graphic.attributes.visible !== undefined ?
                                     graphic.attributes.visible : true;

            // Only show if user hasn't explicitly hidden AND zoom is sufficient
            graphic.visible = userSetVisibility && shouldBeVisible;
          } catch (error) {
            // Silent error handling
          }
        });
      });
    }

    console.log(`[LabelManager] Updated visibility for ${updatedCount} tracked labels`);
  }

  /**
   * Maintain geographic positions by resetting labels to their original data point positions
   * @private
   */
  _maintainGeographicPositions() {
    if (!this.editedLabels || this.editedLabels.size === 0) return;

    let resetCount = 0;

    for (const [labelId, labelData] of this.editedLabels.entries()) {
      if (labelData.graphic && labelData.originalPosition) {
        try {
          // Reset label to its original data point position (the "home" spot)
          labelData.graphic.geometry = labelData.originalPosition.clone();

          // Also clear any pixel offsets
          const symbol = labelData.graphic.symbol.clone();
          symbol.xoffset = 0;
          symbol.yoffset = 0;
          labelData.graphic.symbol = symbol;

          resetCount++;
        } catch (error) {
          // Silent error handling
        }
      }
    }

    if (resetCount > 0) {
      // After resetting all labels to their home positions, re-optimize to find the closest valid positions
      // This ensures labels start from the data point and move outward only as needed
      if (this.settings.avoidCollisions) {
        // Schedule re-optimization after a brief delay to allow rendering to settle
        setTimeout(() => {
          this._runMinimalCollisionDetection().catch(error => {
            // Silent error handling
          });
        }, 100);
      }
    }
  }


  /**
   * Detect collisions between data point symbols/markers
   * @param {Array} allLabels - Array of label info objects with graphics
   * @returns {Array} Array of collision pairs where data points overlap
   * @private
   */
  _detectDataPointCollisions(allLabels) {
    const collisions = [];
    const dataPointRadius = this._getDataPointRadius(); // Get current data point size

    for (let i = 0; i < allLabels.length - 1; i++) {
      for (let j = i + 1; j < allLabels.length; j++) {
        const labelA = allLabels[i];
        const labelB = allLabels[j];

        if (!labelA.originalPosition || !labelB.originalPosition) continue;

        // Calculate screen distance between original data point positions
        const screenA = this.view.toScreen(labelA.originalPosition);
        const screenB = this.view.toScreen(labelB.originalPosition);

        if (!screenA || !screenB) continue;

        const distance = Math.sqrt(
          Math.pow(screenA.x - screenB.x, 2) +
          Math.pow(screenA.y - screenB.y, 2)
        );

        // If data points are closer than 2x their radius, they're colliding
        const collisionThreshold = dataPointRadius * 2;

        if (distance < collisionThreshold) {
          collisions.push({
            labelA: labelA,
            labelB: labelB,
            distance: distance,
            threshold: collisionThreshold
          });
        }
      }
    }

    return collisions;
  }

  /**
   * Get the current data point radius in screen pixels
   * This should match the actual marker/symbol size being used
   * @returns {number} Radius in pixels
   * @private
   */
  _getDataPointRadius() {
    // Use configured radius if set
    if (this.dataPointRadius) {
      return this.dataPointRadius;
    }

    // Try to auto-detect from the actual graphics in the view
    if (this.view && this.view.map && this.view.map.layers) {
      for (const layer of this.view.map.layers) {
        if (layer.type === "graphics" && layer.graphics && layer.graphics.length > 0) {
          const firstGraphic = layer.graphics.getItemAt(0);
          if (firstGraphic && firstGraphic.symbol) {
            // Try to get symbol size for different symbol types
            const symbol = firstGraphic.symbol;

            if (symbol.size) {
              // Simple marker symbols have a size property
              return Math.max(4, symbol.size / 2); // Convert diameter to radius, minimum 4px
            } else if (symbol.width || symbol.height) {
              // Picture marker symbols might have width/height
              const size = Math.max(symbol.width || 0, symbol.height || 0);
              return Math.max(4, size / 2);
            }
          }
        }
      }
    }

    // Default fallback - increased significantly to account for:
    // 1. Actual marker size on screen (typically 20-24px diameter)
    // 2. Visual spacing so labels don't appear to touch markers
    // 3. Label bounding box size (labels are rectangles, not points)
    return 100; // 100 pixel radius provides clear visual separation
  }

  /**
   * Set the data point radius for collision detection
   * @param {number} radius - Radius in screen pixels
   * @public
   */
  setDataPointRadius(radius) {
    this.dataPointRadius = Math.max(1, Math.min(50, radius)); // Clamp between 1-50 pixels
    console.log(`📍 Data point collision radius set to ${this.dataPointRadius}px`);
  }

  /**
   * Force repositioning of labels when their data points are colliding
   * @param {Array} allLabels - Array of all label info objects
   * @param {number} startTime - Start time for timeout calculation
   * @param {number} maxTime - Maximum time allowed in milliseconds
   * @returns {number} Number of labels repositioned due to data point collisions
   * @private
   */
  async _handleDataPointCollisions(allLabels, startTime = Date.now(), maxTime = 5000) {
    // CRITICAL: Skip if a label is being dragged
    const labelDragger = window.labelDragger;
    if (labelDragger && (labelDragger.isDirectDragging || labelDragger.isMoving)) {
      return 0;
    }

    const dataPointCollisions = this._detectDataPointCollisions(allLabels);

    if (dataPointCollisions.length === 0) {
      // Reset collision tracking when there are no collisions
      this.lastCollisionCount = 0;
      this.consecutiveSameCollisionCount = 0;
      this.collisionDetectionDisabled = false;
      return 0; // No data point collisions
    }

    const currentCount = dataPointCollisions.length;

    // Check if collision detection is disabled due to stuck state
    if (this.collisionDetectionDisabled) {
      // Check if collision count has changed - if so, re-enable
      if (currentCount !== this.lastCollisionCount) {
        console.log(`✅ Collision count changed from ${this.lastCollisionCount} to ${currentCount}, re-enabling collision detection`);
        this.collisionDetectionDisabled = false;
        this.consecutiveSameCollisionCount = 0;
        this.lastCollisionCount = currentCount;
      } else {
        console.log(`⏸️  Collision detection disabled - same ${currentCount} collisions detected. Waiting for change...`);
        return 0;
      }
    }

    // Track if we're seeing the same collision count repeatedly
    if (this.lastCollisionCount === currentCount) {
      this.consecutiveSameCollisionCount++;
      if (this.consecutiveSameCollisionCount >= 2) {
        console.warn(`🛑 Same ${currentCount} collisions detected ${this.consecutiveSameCollisionCount + 1} times in a row. Disabling collision detection until count changes.`);
        this.collisionDetectionDisabled = true;
        return 0;
      }
    } else {
      this.consecutiveSameCollisionCount = 0;
      this.lastCollisionCount = currentCount;
    }

    console.log(`🔴 Found ${dataPointCollisions.length} data point collisions, repositioning affected labels`);

    let repositionedCount = 0;

    // For each collision, reposition the labels of both data points
    for (const collision of dataPointCollisions) {
      const { labelA, labelB } = collision;

      // CRITICAL: Skip manually positioned labels - they should not be moved by collision detection
      const isLabelAManual = labelA.labelData?.manuallyPositioned || false;
      const isLabelBManual = labelB.labelData?.manuallyPositioned || false;

      // Create array of all OTHER labels for collision checking
      const otherLabels = allLabels
        .filter(l => l.labelId !== labelA.labelId)
        .map(l => l.graphic);

      // Try to reposition labelA (only if not manually positioned)
      if (!isLabelAManual) {
        const repositionedA = await this._attemptLabelRepositioning(
          labelA.graphic,
          labelA.labelId,
          otherLabels,
          startTime,
          maxTime
        );

        if (repositionedA) {
          repositionedCount++;
          // Note: Do NOT update originalPosition - it should remain at the data point location
        }
      } else {
        console.log(`[CollisionDetection] ✓ Skipping manually positioned label in data point collision: "${labelA.text}" (${labelA.labelId})`);
      }

      // Update other labels list and try to reposition labelB (only if not manually positioned)
      if (!isLabelBManual) {
        const otherLabelsB = allLabels
          .filter(l => l.labelId !== labelB.labelId)
          .map(l => l.graphic);

        const repositionedB = await this._attemptLabelRepositioning(
          labelB.graphic,
          labelB.labelId,
          otherLabelsB,
          startTime,
          maxTime
        );

        if (repositionedB) {
          repositionedCount++;
          // Note: Do NOT update originalPosition - it should remain at the data point location
        }
      } else {
        console.log(`[CollisionDetection] ✓ Skipping manually positioned label in data point collision: "${labelB.text}" (${labelB.labelId})`);
      }
    }

    return repositionedCount;
  }

  /**
   * Run collision detection that finds optimal positions for ALL labels at current zoom level
   * Every zoom level gets a complete fresh search for the best possible positions
   * @private
   */
  async _runMinimalCollisionDetection() {
    if (!this.view || this.editedLabels.size === 0) {
      return;
    }

    // CRITICAL: Skip collision detection if a label is being dragged
    // This prevents position jumping during drag operations
    const labelDragger = window.labelDragger;
    if (labelDragger && (labelDragger.isDirectDragging || labelDragger.isMoving)) {
      console.log("[CollisionDetection] Skipping - label drag in progress");
      return;
    }

    const startTime = Date.now();
    const MAX_TIMEOUT = 5000; // 5 seconds
    let repositionedCount = 0;
    let skippedLabels = 0;

    // Build array of all visible labels
    const allLabels = [];
    for (const [labelId, labelData] of this.editedLabels.entries()) {
      if (!labelData.graphic || !labelData.graphic.geometry) {
        skippedLabels++;
        continue;
      }

      const labelGraphic = labelData.graphic;
      const originalPosition = labelData.originalPosition || labelGraphic.geometry;

      allLabels.push({
        labelId,
        labelData,
        graphic: labelGraphic,
        text: labelGraphic.attributes?.text || labelGraphic.symbol?.text || labelId,
        originalPosition
      });
    }

    // First, handle data point collisions - this forces label repositioning when markers overlap
    const dataPointRepositioned = await this._handleDataPointCollisions(allLabels, startTime, MAX_TIMEOUT);
    repositionedCount += dataPointRepositioned;

    // For each label, try to find the CLOSEST collision-free position to the data point
    for (const labelInfo of allLabels) {
      // Check timeout
      if (Date.now() - startTime > MAX_TIMEOUT) {
        console.warn(`[_runMinimalCollisionDetection] Timed out after ${MAX_TIMEOUT}ms to prevent page freeze.`);
        break;
      }

      // Debug log to check manual positioning flag
      const isManuallyPositioned = labelInfo.labelData.manuallyPositioned || false;
      const labelText = labelInfo.text || labelInfo.labelId;

      console.log(`[CollisionDetection] Checking label "${labelText}" (${labelInfo.labelId}): manuallyPositioned=${isManuallyPositioned}`);

      // Skip labels that have been manually positioned by the user
      if (isManuallyPositioned) {
        console.log(`[CollisionDetection] ✓ Skipping manually positioned label: "${labelText}" (${labelInfo.labelId})`);
        continue;
      }

      // Create array of all OTHER labels for collision checking
      const otherLabels = allLabels
        .filter(l => l.labelId !== labelInfo.labelId)
        .map(l => l.graphic);

      // Always start from the data point (originalPosition) to find the closest valid spot
      // This ensures labels move closer when zoom allows it
      labelInfo.graphic.geometry = labelInfo.originalPosition;

      // Check if data point position has collision
      const dataPointScreenPoint = this.view.toScreen(labelInfo.originalPosition);
      let needsRepositioning = false;

      if (dataPointScreenPoint) {
        const dataPointBounds = this._getScreenBounds({
          graphic: labelInfo.graphic,
          screenPoint: dataPointScreenPoint,
          text: labelInfo.text
        });

        if (dataPointBounds) {
          // Check for collisions with other labels
          for (const otherLabel of otherLabels) {
            const otherScreenPoint = this.view.toScreen(otherLabel.geometry);
            if (otherScreenPoint) {
              const otherBounds = this._getScreenBounds({
                graphic: otherLabel,
                screenPoint: otherScreenPoint,
                text: otherLabel.symbol?.text || ""
              });

              if (otherBounds && this._checkScreenBoundsOverlap(dataPointBounds, otherBounds)) {
                needsRepositioning = true;
                break;
              }
            }
          }

          // Check collision with own data point
          if (!needsRepositioning) {
            const labelBBox = this._calculateBoundingBoxAtPosition(
              labelInfo.graphic,
              labelInfo.originalPosition,
              null
            );
            if (labelBBox) {
              const pixelToMapRatio = this.view?.extent?.width / this.view?.width || 0.0001;
              const collidesWithOwnDataPoint = this._checkCollisionWithDataPoint(
                labelBBox,
                labelInfo.originalPosition,
                pixelToMapRatio
              );
              if (collidesWithOwnDataPoint) {
                needsRepositioning = true;
              }
            }
          }
        }
      }

      // If data point position is collision-free, keep it there
      if (!needsRepositioning) {
        continue;
      }

      // Find the CLOSEST collision-free position to the data point
      const repositioned = await this._attemptLabelRepositioning(labelInfo.graphic, labelInfo.labelId, otherLabels, startTime, MAX_TIMEOUT);

      if (repositioned) {
        repositionedCount++;
      } else {
        // Could not find collision-free position, keep at data point
        labelInfo.graphic.geometry = labelInfo.originalPosition;
      }
    }

    const endTime = Date.now();
    const duration = endTime - startTime;

    // Save updated positions to localStorage if any labels were repositioned
    if (repositionedCount > 0) {
      console.log(`[CollisionDetection] Saving ${repositionedCount} updated positions to storage`);
      this.savePositions();
    }

    // Update bounding boxes display for debugging
    if (this.showVisibleBoundingBoxes) {
      this._updateAllBoundingBoxes();
    }
  }

  /**
   * Try to move a label closer to its original position without causing collisions (screen space version)
   * @param {Object} labelInfo - Label info object with graphic, screenPoint, originalPosition, etc.
   * @returns {Point|null} New position if found, null otherwise
   * @private
   */
  _tryMoveCloserToOriginalScreenSpace(labelInfo) {
    const { graphic: labelGraphic, labelId, originalPosition } = labelInfo;
    const currentPosition = labelGraphic.geometry;
    const currentDistance = this._calculateDistance(currentPosition, originalPosition);

    if (currentDistance < 0.0001) {
      // Already at original position
      return null;
    }

    // Build list of all OTHER labels for collision checking
    const otherLabels = [];
    for (const [otherId, otherData] of this.editedLabels.entries()) {
      if (otherId !== labelId && otherData.graphic && this.labelLayer.graphics.includes(otherData.graphic)) {
        const screenPoint = this.view.toScreen(otherData.graphic.geometry);
        if (screenPoint) {
          otherLabels.push({
            labelId: otherId,
            graphic: otherData.graphic,
            screenPoint,
            text: otherData.graphic.symbol?.text || otherId
          });
        }
      }
    }

    // Try positions at various percentages between current and original
    const percentages = [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0];

    for (const pct of percentages) {
      // Interpolate between current and original position
      const testX = currentPosition.x + (originalPosition.x - currentPosition.x) * pct;
      const testY = currentPosition.y + (originalPosition.y - currentPosition.y) * pct;

      const testPoint = new Point({
        x: testX,
        y: testY,
        spatialReference: currentPosition.spatialReference
      });

      // Convert test position to screen coordinates
      const testScreenPoint = this.view.toScreen(testPoint);
      if (!testScreenPoint) continue;

      // Create a test label info object for bounds calculation
      const testLabelInfo = {
        graphic: labelGraphic,
        screenPoint: testScreenPoint,
        text: labelGraphic.symbol?.text || labelInfo.text
      };

      const testBounds = this._getScreenBounds(testLabelInfo);
      if (!testBounds) continue;

      // Check if this position would cause collisions with any other label
      let hasCollision = false;
      for (const otherLabel of otherLabels) {
        const otherBounds = this._getScreenBounds(otherLabel);
        if (otherBounds && this._checkScreenBoundsOverlap(testBounds, otherBounds)) {
          hasCollision = true;
          break;
        }
      }

      if (!hasCollision) {
        // Found a valid position closer to original!
        return testPoint;
      }
    }

    return null;
  }

  /**
   * Try to move a label closer to its original position without causing collisions
   * @param {Graphic} labelGraphic - The label graphic
   * @param {Point} originalPosition - The original position to move toward
   * @param {string} labelId - The label ID
   * @returns {Point|null} New position if found, null otherwise
   * @private
   */
  _tryMoveCloserToOriginal(labelGraphic, originalPosition, labelId) {
    const currentPosition = labelGraphic.geometry;
    const currentDistance = this._calculateDistance(currentPosition, originalPosition);

    if (currentDistance < 0.0001) {
      // Already at original position
      return null;
    }

    // Create a cached context for performance
    const textDimensions = this._getOptimizedTextDimensions(
      labelGraphic.attributes?.text || "",
      labelGraphic.symbol?.font?.size || 12,
      labelGraphic.symbol?.font?.weight || "normal",
      labelGraphic.symbol?.font?.family || "Arial"
    );

    const mapExtentWidth = this.view?.extent?.width;
    const viewWidth = this.view?.width;
    const pixelToMapRatio = mapExtentWidth / viewWidth;

    const cachedContext = {
      labelId: labelId,
      textDimensions,
      pixelToMapRatio
    };

    // Try positions at various percentages between current and original
    const percentages = [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0];

    for (const pct of percentages) {
      // Interpolate between current and original position
      const testX = currentPosition.x + (originalPosition.x - currentPosition.x) * pct;
      const testY = currentPosition.y + (originalPosition.y - currentPosition.y) * pct;

      const testPoint = new Point({
        x: testX,
        y: testY,
        spatialReference: currentPosition.spatialReference
      });

      // Check if this position would cause collisions
      const testBox = this._calculateBoundingBoxAtPosition(labelGraphic, testPoint, cachedContext);

      if (testBox) {
        const hasCollision = this._detectAllCollisions(testBox, labelId);

        if (!hasCollision) {
          // Found a valid position closer to original!
          return testPoint;
        }
      }
    }

    return null;
  }

  /**
   * Calculate distance between two points
   * @param {Point} point1 - First point
   * @param {Point} point2 - Second point
   * @returns {number} Distance between points
   * @private
   */
  _calculateDistance(point1, point2) {
    if (!point1 || !point2) return Infinity;
    const dx = point1.x - point2.x;
    const dy = point1.y - point2.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Debug collision detection - finds collisions and shows them visually
   * Uses screen-space collision detection based on actual visible label bounds
   * @returns {Object} Collision detection results
   */
  debugCollisionDetection() {
    if (!this.view) {
      return { 
        totalLabels: 0,
        collisions: [],
        message: "MapView not available"
      };
    }
    
    // Debug: Check available layers and graphics
    // Try to find labels from multiple sources
    const allLabels = [];
    
    // Method 1: From editedLabels (our tracked labels)
    let foundFromEdited = 0;
    for (const [labelId, labelData] of this.editedLabels.entries()) {
      if (labelData.graphic && labelData.graphic.geometry) {
        const screenPoint = this.view.toScreen(labelData.graphic.geometry);
        if (screenPoint) {
          allLabels.push({
            labelId,
            graphic: labelData.graphic,
            screenPoint: screenPoint,
            text: labelData.graphic.symbol?.text || labelId,
            source: 'editedLabels'
          });
          foundFromEdited++;
        }
      }
    }
    // Method 2: From labelLayer graphics directly
    let foundFromLayer = 0;
    if (this.labelLayer && this.labelLayer.graphics) {
      for (let i = 0; i < this.labelLayer.graphics.length; i++) {
        const graphic = this.labelLayer.graphics.getItemAt(i);
        if (graphic && graphic.geometry && graphic.symbol && graphic.symbol.text) {
          const screenPoint = this.view.toScreen(graphic.geometry);
          if (screenPoint) {
            // Check if we already have this one from editedLabels
            const alreadyExists = allLabels.some(label => label.graphic === graphic);
            if (!alreadyExists) {
              allLabels.push({
                labelId: `layer-${i}`,
                graphic: graphic,
                screenPoint: screenPoint,
                text: graphic.symbol.text,
                source: 'labelLayer'
              });
              foundFromLayer++;
            }
          }
        }
      }
    }
    // Method 3: From all map layers (comprehensive search)
    let foundFromMap = 0;
    if (this.view.map && this.view.map.layers) {
      for (let layerIdx = 0; layerIdx < this.view.map.layers.length; layerIdx++) {
        const layer = this.view.map.layers.getItemAt(layerIdx);
        if (layer && layer.graphics) {
          for (let i = 0; i < layer.graphics.length; i++) {
            const graphic = layer.graphics.getItemAt(i);
            if (graphic && graphic.geometry && graphic.symbol && graphic.symbol.text) {
              const screenPoint = this.view.toScreen(graphic.geometry);
              if (screenPoint) {
                // Check if we already have this graphic from previous searches
                const alreadyExists = allLabels.some(label => label.graphic === graphic);
                if (!alreadyExists) {
                  allLabels.push({
                    labelId: `map-${layerIdx}-${i}`,
                    graphic: graphic,
                    screenPoint: screenPoint,
                    text: graphic.symbol.text,
                    source: 'mapLayers'
                  });
                  foundFromMap++;
                }
              }
            }
          }
        }
        
        // Also check if this is a feature layer with labeling enabled
        if (layer && layer.type === 'feature' && layer.labelsVisible && layer.labelingInfo) {
        }
      }
    }
    // Debug: Check for duplicate labels (same text, same position)
    const duplicateCheck = {};
    allLabels.forEach((label, index) => {
      const key = `${label.text}_${label.screenPoint.x}_${label.screenPoint.y}`;
      if (!duplicateCheck[key]) {
        duplicateCheck[key] = [];
      }
      duplicateCheck[key].push({index, labelId: label.labelId, source: label.source});
    });
    
    const duplicates = Object.entries(duplicateCheck).filter(([key, instances]) => instances.length > 1);
    if (duplicates.length > 0) {
      
    }
    
    if (allLabels.length < 2) {
      return { 
        totalLabels: allLabels.length,
        collisions: [],
        message: `Found ${allLabels.length} label(s). Need at least 2 for collision detection.`,
        debug: {
          fromEditedLabels: foundFromEdited,
          fromLabelLayer: foundFromLayer,
          fromMapLayers: foundFromMap,
          editedLabelsSize: this.editedLabels.size,
          labelLayerExists: !!this.labelLayer,
          labelLayerGraphicsCount: this.labelLayer?.graphics?.length || 0,
          mapLayersCount: this.view.map?.layers?.length || 0
        }
      };
    }
    // Find all collisions using screen coordinates
    const collisions = [];
    const collidingLabelIds = new Set();
    const processedCollisions = new Set(); // Track unique collision pairs
    
    for (let i = 0; i < allLabels.length; i++) {
      const labelA = allLabels[i];
      const boundsA = this._getScreenBounds(labelA);
      if (!boundsA) continue;
      
      for (let j = i + 1; j < allLabels.length; j++) {
        const labelB = allLabels[j];
        const boundsB = this._getScreenBounds(labelB);
        if (!boundsB) continue;
        
        // Skip collision if these are likely duplicate labels (same text, very close positions)
        const positionDistance = Math.sqrt(
          Math.pow(labelA.screenPoint.x - labelB.screenPoint.x, 2) + 
          Math.pow(labelA.screenPoint.y - labelB.screenPoint.y, 2)
        );
        
        if (labelA.text === labelB.text && positionDistance < 5) {
          
          continue;
        }
        
        // Check if screen bounds overlap
        if (this._checkScreenBoundsOverlap(boundsA, boundsB)) {
          const overlapArea = this._calculateScreenOverlapArea(boundsA, boundsB);
          
          // Create unique collision key (use sorted labels to prevent A-B vs B-A duplicates)
          const sortedLabels = [labelA.text, labelB.text].sort();
          const collisionKey = `${sortedLabels[0]}_${sortedLabels[1]}_${labelA.screenPoint.x}_${labelA.screenPoint.y}_${labelB.screenPoint.x}_${labelB.screenPoint.y}`;
          
          // Skip if we've already processed this collision
          if (processedCollisions.has(collisionKey)) {
            
            continue;
          }
          processedCollisions.add(collisionKey);
          
          collisions.push({
            labelA: labelA.text,
            labelB: labelB.text,
            labelAId: labelA.labelId,
            labelBId: labelB.labelId,
            overlapArea: overlapArea,
            boundsA: boundsA,
            boundsB: boundsB
          });
          
          collidingLabelIds.add(labelA.labelId);
          collidingLabelIds.add(labelB.labelId);
        }
      }
    }
    
    // Show bounding boxes only for colliding labels
    this._showBoundingBoxesForLabels(collidingLabelIds);
    
    const result = {
      totalLabels: allLabels.length,
      collisions: collisions,
      collidingLabels: Array.from(collidingLabelIds),
      zoom: this.view.zoom.toFixed(1),
      scale: this.view.scale.toLocaleString(),
      message: collisions.length > 0 
        ? `Found ${collisions.length} collision(s) between ${collidingLabelIds.size} labels at zoom ${this.view.zoom.toFixed(1)}`
        : `No collisions detected at zoom ${this.view.zoom.toFixed(1)}`
    };
    return result;
  }

  /**
   * Get screen bounds for a label based on its current appearance
   * @param {Object} labelInfo - Label info with graphic and screenPoint
   * @returns {Object|null} Screen bounds {left, top, right, bottom, width, height}
   * @private
   */
  _getScreenBounds(labelInfo) {
    const { graphic, screenPoint, text } = labelInfo;
    
    if (!graphic.symbol || !screenPoint || !text) {
      return null;
    }
    
    // Get font properties from the symbol
    const fontSize = graphic.symbol.font?.size || 10;
    const fontFamily = graphic.symbol.font?.family || 'Arial';
    const fontWeight = graphic.symbol.font?.weight || 'normal';
    
    // Measure the actual text dimensions
    const textDimensions = this._getOptimizedTextDimensions(text, fontSize, fontWeight, fontFamily);
    if (!textDimensions) {
      return null;
    }
    
    // Calculate screen bounds centered on the screen point
    const halfWidth = textDimensions.width / 2;
    const halfHeight = textDimensions.height / 2;
    
    // Add small padding for more accurate collision detection
    const padding = 2;
    
    const bounds = {
      left: screenPoint.x - halfWidth - padding,
      top: screenPoint.y - halfHeight - padding,
      right: screenPoint.x + halfWidth + padding,
      bottom: screenPoint.y + halfHeight + padding,
      width: textDimensions.width + (padding * 2),
      height: textDimensions.height + (padding * 2),
      centerX: screenPoint.x,
      centerY: screenPoint.y
    };
    
    return bounds;
  }

  /**
   * Check if two screen bounds overlap
   * @param {Object} boundsA - First bounds
   * @param {Object} boundsB - Second bounds
   * @returns {boolean} True if bounds overlap
   * @private
   */
  _checkScreenBoundsOverlap(boundsA, boundsB) {
    return !(boundsA.right < boundsB.left || 
             boundsB.right < boundsA.left || 
             boundsA.bottom < boundsB.top || 
             boundsB.bottom < boundsA.top);
  }

  /**
   * Calculate overlap area between two screen bounds
   * @param {Object} boundsA - First bounds
   * @param {Object} boundsB - Second bounds
   * @returns {number} Overlap area in screen pixels
   * @private
   */
  _calculateScreenOverlapArea(boundsA, boundsB) {
    if (!this._checkScreenBoundsOverlap(boundsA, boundsB)) {
      return 0;
    }
    
    const overlapLeft = Math.max(boundsA.left, boundsB.left);
    const overlapRight = Math.min(boundsA.right, boundsB.right);
    const overlapTop = Math.max(boundsA.top, boundsB.top);
    const overlapBottom = Math.min(boundsA.bottom, boundsB.bottom);
    
    const overlapWidth = overlapRight - overlapLeft;
    const overlapHeight = overlapBottom - overlapTop;
    
    return overlapWidth * overlapHeight;
  }

  /**
   * Show bounding boxes only for specific labels
   * @param {Set} labelIds - Set of label IDs to show bounding boxes for
   * @private
   */
  _showBoundingBoxesForLabels(labelIds) {
    // First, remove all existing bounding boxes
    this.boundingBoxGraphics.forEach((graphic, labelId) => {
      this._removeVisibleBoundingBox(labelId);
    });
    
    // Then create bounding boxes only for colliding labels
    for (const labelId of labelIds) {
      const labelData = this.editedLabels.get(labelId);
      if (labelData && labelData.graphic) {
        this._createVisibleBoundingBox(labelData.graphic, labelId);
      }
    }
  }

  /**
   * Clear all debug bounding boxes
   */
  clearDebugBoundingBoxes() {
    this.boundingBoxGraphics.forEach((graphic, labelId) => {
      this._removeVisibleBoundingBox(labelId);
    });
    
  }

  /**
   * Debounced save to prevent excessive storage operations
   * @private
   */
  _debouncedSave() {
    if (this._saveDebounceTimer) {
      clearTimeout(this._saveDebounceTimer);
    }

    this._saveDebounceTimer = setTimeout(() => {
      this.savePositions();
    }, 1000); // 1 second debounce
  }

  /**
   * Optimized direct drag mode enablement
   * @param {boolean} enable - Whether to enable direct dragging
   */
  enableDirectDragMode(enable = true) {
    this._cleanupEventHandlers();

    if (enable && this.view) {
      // Reset drag state
      this._resetDragState();

      // Disable map navigation
      this._setMapNavigation(false);

      // Set up optimized event handlers
      this._setupOptimizedDragHandlers();
    } else {
      this._setMapNavigation(true);
      this._resetDragState();
      
    }
  }

  /**
   * Reset drag state
   * @private
   */
  _resetDragState() {
    this.isDragging = false;
    this.dragStartPoint = null;
    this.draggedLabel = null;
    this.originalOffset = null;
  }

  /**
   * Set map navigation state
   * @param {boolean} enabled - Whether navigation is enabled
   * @private
   */
  _setMapNavigation(enabled) {
    if (this.view?.navigation) {
      this.view.navigation.browserTouchPanEnabled = enabled;
    }
  }

  /**
   * Setup optimized drag event handlers
   * @private
   */
  _setupOptimizedDragHandlers() {
    // Optimized pointer down handler
    const pointerDownHandler = this.view.on("pointer-down", (event) => {
      if (this.isDragging || event.button !== 0) return;

      this._handleOptimizedPointerDown(event);
    });
    this.handlers.push(pointerDownHandler);

    // Optimized pointer move handler
    const pointerMoveHandler = this.view.on("pointer-move", (event) => {
      if (!this.isDragging || !this.draggedLabel) return;

      this._handleOptimizedPointerMove(event);
    });
    this.handlers.push(pointerMoveHandler);

    // Optimized pointer up handler
    const pointerUpHandler = this.view.on("pointer-up", (event) => {
      if (!this.isDragging) return;

      this._handleOptimizedPointerUp(event);
    });
    this.handlers.push(pointerUpHandler);

    // Optimized pointer leave handler
    const pointerLeaveHandler = this.view.on("pointer-leave", () => {
      if (this.isDragging) {
        this._handleOptimizedPointerLeave();
      }
    });
    this.handlers.push(pointerLeaveHandler);
  }

  /**
   * Handle optimized pointer down
   * @param {Object} event - Pointer event
   * @private
   */
  _handleOptimizedPointerDown(event) {
    if (!event?.screenPoint) return;

    this.view.hitTest(event.screenPoint)
      .then((response) => {
        const labelHit = this._findValidLabelHit(response);
        
        if (labelHit) {
          this._startDragOperation(event, labelHit);
        }
      })
      .catch((err) => {
        
      });
  }

  /**
   * Find valid label hit from hit test results
   * @param {Object} response - Hit test response
   * @returns {Object|null} Valid label hit
   * @private
   */
  _findValidLabelHit(response) {
    if (!response?.results) return null;

    for (const result of response.results) {
      const graphic = result.graphic;
      
      if (!this._isValidLabelGraphic(graphic)) continue;
      if (!this._isGraphicInCurrentContext(graphic)) continue;

      return graphic;
    }

    return null;
  }

  /**
   * Check if graphic is a valid label
   * @param {Object} graphic - Graphic to check
   * @returns {boolean} True if valid label
   * @private
   */
  _isValidLabelGraphic(graphic) {
    return graphic && 
           (graphic.symbol?.type === "text" || graphic.attributes?.isLabel === true);
  }

  /**
   * Check if graphic is in current context
   * @param {Object} graphic - Graphic to check
   * @returns {boolean} True if in current context
   * @private
   */
  _isGraphicInCurrentContext(graphic) {
    const attrs = graphic.attributes || {};
    
    const configMatch = !attrs.mapConfigId || !this.mapConfigId || 
                       attrs.mapConfigId === this.mapConfigId;
    const typeMatch = !attrs.mapType || !this.mapType || 
                     attrs.mapType === this.mapType;

    return configMatch && typeMatch;
  }

  /**
   * Start drag operation
   * @param {Object} event - Pointer event
   * @param {Object} labelGraphic - Label graphic
   * @private
   */
  _startDragOperation(event, labelGraphic) {
    event.stopPropagation();

    this.draggedLabel = labelGraphic;
    this.dragStartPoint = { x: event.x, y: event.y };
    this.originalOffset = {
      x: labelGraphic.symbol?.xoffset || 0,
      y: labelGraphic.symbol?.yoffset || 0,
    };
    this.isDragging = true;
    this.selectedLabel = labelGraphic;

    // Update cursor
    if (this.view.container) {
      this.view.container.style.cursor = "grabbing";
    }
  }

  /**
   * Handle optimized pointer move
   * @param {Object} event - Pointer event
   * @private
   */
  _handleOptimizedPointerMove(event) {
    if (!this.dragStartPoint || !this.originalOffset) return;

    event.stopPropagation();

    // Calculate new offset
    const dx = event.x - this.dragStartPoint.x;
    const dy = event.y - this.dragStartPoint.y;

    const newOffset = {
      x: this.originalOffset.x + dx,
      y: this.originalOffset.y - dy, // Invert Y axis
    };

    // Update label position efficiently
    this._updateDraggedLabelPosition(newOffset);
  }

  _updateDraggedLabelPosition(newOffset) {
      if (!this.draggedLabel?.symbol) return;

      try {
          // Convert pixel offset to geographic coordinates
          const originalScreenPos = this.view.toScreen(this.draggedLabel.geometry);
          const newScreenPos = {
              x: originalScreenPos.x + (newOffset.x - this.originalOffset.x),
              y: originalScreenPos.y + (newOffset.y - this.originalOffset.y)
          };
          
          // Convert screen position back to geographic coordinates
          const newMapPoint = this.view.toMap(newScreenPos);
          if (newMapPoint) {
              // IMPORTANT: Ensure we're using WGS84 (4326) coordinates
              const geographicPoint = webMercatorUtils.webMercatorToGeographic(newMapPoint);
              
              const labelId = this.getLabelId(this.draggedLabel);
              this._applyPositionToGraphic(this.draggedLabel, {
                  latitude: geographicPoint.y,  // Use geographic coordinates
                  longitude: geographicPoint.x
              }, labelId);
          }
      } catch (error) {
          
      }
  }

  /**
   * Handle optimized pointer up
   * @param {Object} event - Pointer event
   * @private
   */
  _handleOptimizedPointerUp(event) {
    event.stopPropagation();

    // Restore cursor
    if (this.view.container) {
      this.view.container.style.cursor = "move";
    }

    // Save changes with debouncing
    if (this.draggedLabel) {
      this._debouncedSave();
      
    }

    this._resetDragState();
  }

  /**
   * Handle optimized pointer leave
   * @private
   */
  _handleOptimizedPointerLeave() {
    // Restore cursor
    if (this.view.container) {
      this.view.container.style.cursor = "move";
    }

    // Save changes
    if (this.draggedLabel) {
      this._debouncedSave();
    }

    this._resetDragState();
  }

  /**
   * Optimized save positions with compression
   * @param {boolean} force - Whether to force save even if no changes
   * @returns {Object} Result of the save operation
   */
  savePositions(force = false) {
    if (this.editedLabels.size === 0 && !force) {
      return { success: true, message: "No changes to save", count: 0 };
    }

    try {
      // Ensure context
      this._ensureContextForSave();

      const storageKey = this.getStorageKey();
      let savedPositions = this._loadExistingSavedPositions(storageKey);

      // Update with current edited labels efficiently
      const updatedCount = this._updateSavedPositions(savedPositions);

      // Optimized storage operation
      localStorage.setItem(storageKey, JSON.stringify(savedPositions));
      return {
        success: true,
        message: `Saved ${updatedCount} label positions and styles`,
        count: updatedCount,
      };
    } catch (error) {
      
      return {
        success: false,
        message: `Error saving positions: ${error.message}`,
        count: 0,
      };
    }
  }

  /**
   * Ensure context for save operation
   * @private
   */
  _ensureContextForSave() {
    if (!this.mapConfigId) {
      this.mapConfigId = sessionStorage.getItem("currentMapConfigId");
    }
    if (!this.mapType) {
      this.mapType = sessionStorage.getItem("currentMapType");
    }
  }

  /**
   * Load existing saved positions
   * @param {string} storageKey - Storage key
   * @returns {Object} Existing saved positions
   * @private
   */
  _loadExistingSavedPositions(storageKey) {
    try {
      const savedData = localStorage.getItem(storageKey);
      return savedData ? JSON.parse(savedData) : {};
    } catch (readError) {
      
      return {};
    }
  }

  /**
   * Update saved positions efficiently
   * @param {Object} savedPositions - Positions to update
   * @returns {number} Number of updated positions
   * @private
   */
  _updateSavedPositions(savedPositions) {
    let updatedCount = 0;

    for (const [labelId, labelData] of this.editedLabels) {
      const positionData = this._createPositionData(labelData);
      
      savedPositions[labelId] = positionData;
      updatedCount++;
    }

    return updatedCount;
  }

  /**
   * Create position data object
   * @param {Object} labelData - Label data
   * @returns {Object} Position data
   * @private
   */
  _createPositionData(labelData) {
    const fontSize = labelData.fontSize ||
                    labelData.graphic?.symbol?.font?.size ||
                    this.settings.fontSize;

    const fontWeight = labelData.fontWeight ||
                      labelData.graphic?.symbol?.font?.weight ||
                      "normal";

    // CRITICAL FIX: Properly convert coordinates to WGS84 geographic format
    // The geometry may be in Web Mercator (x/y in meters) but we need to save as WGS84 (lat/lon in degrees)
    let geographicPosition = null;

    if (labelData.graphic?.geometry) {
      const geom = labelData.graphic.geometry;

      // First, check if labelData already has explicit latitude/longitude (set during drag)
      if (labelData.latitude !== undefined && labelData.longitude !== undefined) {
        geographicPosition = {
          latitude: labelData.latitude,
          longitude: labelData.longitude
        };
      }
      // Check if the geometry has latitude/longitude properties (WGS84)
      else if (geom.latitude !== undefined && geom.longitude !== undefined) {
        geographicPosition = {
          latitude: geom.latitude,
          longitude: geom.longitude
        };
      }
      // Check if it's Web Mercator - need to convert
      else if (geom.spatialReference &&
               (geom.spatialReference.wkid === 3857 || geom.spatialReference.wkid === 102100)) {
        try {
          const geoPoint = webMercatorUtils.webMercatorToGeographic(geom);
          if (geoPoint) {
            geographicPosition = {
              latitude: geoPoint.y,
              longitude: geoPoint.x
            };
          }
        } catch (conversionError) {
          console.error("[LabelManager] Error converting Web Mercator to geographic:", conversionError);
          // Fallback to raw values (may cause issues)
          geographicPosition = {
            latitude: geom.y,
            longitude: geom.x
          };
        }
      }
      // Default fallback - assume coordinates are in degrees
      else {
        geographicPosition = {
          latitude: geom.y,
          longitude: geom.x
        };
      }
    } else if (labelData.position) {
      geographicPosition = labelData.position;
    }

    // IMPORTANT: Save user's INTENDED visibility, not current graphic visibility
    // graphic.visible can be false due to zoom level, but we want to preserve
    // the user's explicit visibility preference (stored in labelData.visible or attributes.visible)
    const userIntendedVisibility = labelData.visible !== undefined ? labelData.visible :
                                   (labelData.graphic?.attributes?.visible !== undefined ?
                                    labelData.graphic.attributes.visible : true);

    return {
      position: labelData.position, // Keep for backward compatibility
      geographicPosition: geographicPosition, // NEW: Save actual geographic coordinates
      fontSize: fontSize,
      text: labelData.text,
      visible: userIntendedVisibility,
      fontWeight: fontWeight,
      backgroundColor: labelData.backgroundColor,
      borderLineColor: labelData.borderLineColor,
      borderLineSize: labelData.borderLineSize,
      manuallyPositioned: labelData.manuallyPositioned || false, // Track manual positioning
      lastEdited: Date.now(),
      mapConfigId: this.mapConfigId,
      mapType: this.mapType,
      symbolProperties: {
        haloSize: labelData.graphic?.symbol?.haloSize,
        haloColor: labelData.graphic?.symbol?.haloColor,
        kerning: labelData.graphic?.symbol?.kerning,
        horizontalAlignment: labelData.graphic?.symbol?.horizontalAlignment,
        verticalAlignment: labelData.graphic?.symbol?.verticalAlignment,
        xoffset: 0, // Force to 0 since we're using geographic positioning
        yoffset: 0, // Force to 0 since we're using geographic positioning
      },
    };
  }

  /**
   * Optimized load positions
   * @param {boolean} force - Whether to force reload
   * @param {boolean} preserveEdits - Whether to preserve current edits
   * @returns {Object} Result of the load operation
   */
  loadPositions(force = false, preserveEdits = true) {
    try {
      const savedPositions = this._getSavedPositions();
      const positionCount = Object.keys(savedPositions).length;

      if (positionCount === 0) {
        
        return { success: true, message: "No saved positions found", count: 0 };
      }
      // Apply positions efficiently
      const appliedCount = this._applyAllSavedPositions(savedPositions);
      return {
        success: true,
        message: `Applied ${appliedCount} saved positions and styles`,
        count: appliedCount,
      };
    } catch (error) {
      
      return {
        success: false,
        message: `Error loading positions: ${error.message}`,
        count: 0,
      };
    }
  }

  /**
   * Apply all saved positions efficiently
   * @param {Object} savedPositions - Saved positions
   * @returns {number} Number of applied positions
   * @private
   */
  _applyAllSavedPositions(savedPositions) {
    let appliedCount = 0;

    // Apply to tracked labels first
    appliedCount += this._applyPositionsToTrackedLabels(savedPositions);

    // Apply to visible layers
    appliedCount += this._applyPositionsToVisibleLayers(savedPositions);

    return appliedCount;
  }

  /**
   * Apply positions to tracked labels
   * @param {Object} savedPositions - Saved positions
   * @returns {number} Number of applied positions
   * @private
   */
  _applyPositionsToTrackedLabels(savedPositions) {
    let appliedCount = 0;

    for (const [labelId, labelData] of this.editedLabels) {
      if (!savedPositions[labelId] || !labelData.graphic) continue;

      const savedData = savedPositions[labelId];
      
      if (!this._validateSavedDataContext(savedData)) continue;

      if (this._applyAllSavedProperties(labelData.graphic, savedData, labelId)) {
        this._updateTrackingWithSavedData(labelData.graphic, savedData, labelId);
        appliedCount++;
      }
    }

    return appliedCount;
  }

  /**
   * Apply positions to visible layers
   * @param {Object} savedPositions - Saved positions
   * @returns {number} Number of applied positions
   * @private
   */
  _applyPositionsToVisibleLayers(savedPositions) {
    if (!this.view?.map?.layers) return 0;

    let appliedCount = 0;

    for (const layer of this.view.map.layers) {
      if (!layer.graphics) continue;

      // Skip if already processed with current context
      if (layer._processedWithConfigId === this.mapConfigId &&
          layer._processedWithMapType === this.mapType) {
        continue;
      }

      appliedCount += this._applyPositionsToLayer(layer, savedPositions);
    }

    return appliedCount;
  }

  /**
   * Apply positions to specific layer
   * @param {Object} layer - Layer to process
   * @param {Object} savedPositions - Saved positions
   * @returns {number} Number of applied positions
   * @private
   */
  _applyPositionsToLayer(layer, savedPositions) {
    let layerAppliedCount = 0;

    for (const graphic of layer.graphics) {
      const isLabel = graphic.symbol?.type === "text" || 
                     graphic.attributes?.isLabel === true;
      if (!isLabel) continue;

      const labelId = this.getLabelId(graphic);
      if (!labelId || this.editedLabels.has(labelId)) continue;

      const savedData = savedPositions[labelId];
      if (!savedData || !this._validateSavedDataContext(savedData)) continue;

      if (this._applyAllSavedProperties(graphic, savedData, labelId)) {
        this._addContextToGraphic(graphic);
        this._updateTrackingWithSavedData(graphic, savedData, labelId);
        layerAppliedCount++;
      }
    }

    if (layerAppliedCount > 0) {
      
    }

    // Mark layer as processed
    layer._processedWithConfigId = this.mapConfigId;
    layer._processedWithMapType = this.mapType;

    return layerAppliedCount;
  }

  /**
   * Optimized reset all labels
   * @returns {Object} Result of the reset operation
   */
  resetAllLabels() {
    try {
      // Remove saved positions for current context
      localStorage.removeItem(this.getStorageKey());

      let resetCount = 0;

      // Reset tracked labels efficiently
      for (const [labelId, labelData] of this.editedLabels) {
        if (!this._isLabelInCurrentContext(labelData)) continue;

        if (this._resetLabelToDefaults(labelData.graphic)) {
          resetCount++;
        }
      }

      // Clear tracking for current context
      this._clearContextLabelsFromTracking();
      return {
        success: true,
        message: `Reset ${resetCount} label positions`,
        count: resetCount,
      };
    } catch (error) {
      
      return {
        success: false,
        message: `Error resetting positions: ${error.message}`,
        count: 0,
      };
    }
  }

  /**
   * Check if label is in current context
   * @param {Object} labelData - Label data
   * @returns {boolean} True if in current context
   * @private
   */
  _isLabelInCurrentContext(labelData) {
    const configMatch = !labelData.mapConfigId || !this.mapConfigId || 
                       labelData.mapConfigId === this.mapConfigId;
    const typeMatch = !labelData.mapType || !this.mapType || 
                     labelData.mapType === this.mapType;

    return configMatch && typeMatch;
  }

  /**
   * Reset label to defaults
   * @param {Object} graphic - Graphic to reset
   * @returns {boolean} Success
   * @private
   */
  _resetLabelToDefaults(graphic) {
    if (!graphic?.symbol) return false;

    const newSymbol = graphic.symbol.clone();
    
    // Reset to default font properties without forcing offset changes
    newSymbol.font = {
      size: this.settings.fontSize,
      family: this.settings.fontFamily,
      weight: "normal"
    };
    
    // Preserve existing alignment and positioning
    if (!newSymbol.horizontalAlignment) newSymbol.horizontalAlignment = "center";
    if (!newSymbol.verticalAlignment) newSymbol.verticalAlignment = "bottom";

    if (newSymbol.font) {
      newSymbol.font = {
        ...newSymbol.font,
        size: this.settings.fontSize,
      };
    }

    graphic.symbol = newSymbol;
    graphic.visible = true;

    return true;
  }

  /**
   * Clear context labels from tracking
   * @private
   */
  _clearContextLabelsFromTracking() {
    const filteredEditedLabels = new Map();

    for (const [labelId, labelData] of this.editedLabels) {
      if (!this._isLabelInCurrentContext(labelData)) {
        filteredEditedLabels.set(labelId, labelData);
      }
    }

    this.editedLabels = filteredEditedLabels;
  }

  /**
   * Reset single label position optimized
   * @param {string|Object} labelOrId - Label ID or label graphic
   * @returns {Object} Result of the reset operation
   */
  resetLabelPosition(labelOrId) {
    try {
      const labelId = typeof labelOrId === "string" ? labelOrId : this.getLabelId(labelOrId);
      if (!labelId) {
        return { success: false, message: "Invalid label ID" };
      }

      const labelData = this.editedLabels.get(labelId);
      if (!labelData?.graphic?.symbol) {
        return { success: false, message: "Label not found or invalid" };
      }

      // Verify context
      if (!this._isLabelInCurrentContext(labelData)) {
        return {
          success: false,
          message: `Label belongs to a different context`,
        };
      }

      // Reset label
      if (!this._resetLabelToDefaults(labelData.graphic)) {
        return { success: false, message: "Failed to reset label" };
      }

      // Update tracking
      this._updateTrackingAfterReset(labelId, labelData);

      // Remove from saved positions
      this._removeLabelFromSavedPositions(labelId);
      return {
        success: true,
        message: `Reset label position for ${labelId}`,
        graphic: labelData.graphic,
      };
    } catch (error) {
      
      return {
        success: false,
        message: `Error resetting label position: ${error.message}`,
      };
    }
  }

  /**
   * Update tracking after reset
   * @param {string} labelId - Label ID
   * @param {Object} labelData - Label data
   * @private
   */
  _updateTrackingAfterReset(labelId, labelData) {
    this.editedLabels.set(labelId, {
      graphic: labelData.graphic,
      position: { x: 0, y: 0 },
      fontSize: this.settings.fontSize,
      text: labelData.graphic.symbol.text || "",
      mapConfigId: this.mapConfigId,
      mapType: this.mapType,
    });
  }

  /**
   * Remove label from saved positions
   * @param {string} labelId - Label ID
   * @private
   */
  _removeLabelFromSavedPositions(labelId) {
    const savedPositions = this._getSavedPositions();
    if (savedPositions[labelId]) {
      delete savedPositions[labelId];
      localStorage.setItem(this.getStorageKey(), JSON.stringify(savedPositions));
    }
  }

  /**
   * Update label position optimized
   * @param {string|Object} labelOrId - Label ID or label graphic
   * @param {Object} position - New position {x, y}
   * @param {boolean} isManualMove - Whether this is a manual user move (default: false)
   * @returns {Object} Result of the update operation
   */
  updateLabelPosition(labelOrId, position, isManualMove = false) {
    try {
      const labelId = typeof labelOrId === "string" ? labelOrId : this.getLabelId(labelOrId);
      if (!labelId) {
        return { success: false, message: "Invalid label ID" };
      }

      const labelData = this.editedLabels.get(labelId);
      if (!labelData?.graphic?.symbol) {
        return { success: false, message: "Label not found or invalid" };
      }

      // Verify context
      if (!this._isLabelInCurrentContext(labelData)) {
        return {
          success: false,
          message: `Label belongs to a different context`,
        };
      }

      // Update label properties without forcing position resets
      const newSymbol = labelData.graphic.symbol.clone();

      // Apply any font/style changes but preserve positioning
      if (labelData.fontSize) {
        newSymbol.font = newSymbol.font || {};
        newSymbol.font.size = labelData.fontSize;
      }

      labelData.graphic.symbol = newSymbol;

      // Update tracking
      labelData.position = { x: position.x, y: position.y };

      // Mark as manually positioned if this is a user drag operation
      if (isManualMove) {
        labelData.manuallyPositioned = true;
        console.log(`[LabelManager] Marked label ${labelId} as manually positioned`);
        // Reset collision tracking since manual repositioning may have resolved collisions
        this.resetCollisionTracking();
      }

      return {
        success: true,
        message: `Updated label position for ${labelId}`,
      };
    } catch (error) {

      return {
        success: false,
        message: `Error updating label position: ${error.message}`,
      };
    }
  }

  /**
   * Mark a label as manually positioned by the user
   * This prevents automatic collision detection from repositioning it
   * @param {string|Object} labelOrId - Label ID or label graphic
   * @returns {Object} Result of the operation
   */
  markLabelAsManuallyPositioned(labelOrId) {
    try {
      const labelId = typeof labelOrId === "string" ? labelOrId : this.getLabelId(labelOrId);
      if (!labelId) {
        console.warn("[LabelManager] ⚠️ Cannot mark label as manually positioned: Invalid label ID");
        return { success: false, message: "Invalid label ID" };
      }

      const labelData = this.editedLabels.get(labelId);
      if (!labelData) {
        console.warn(`[LabelManager] ⚠️ Cannot mark label as manually positioned: Label ${labelId} not found in editedLabels`);
        console.warn(`[LabelManager] Current editedLabels keys:`, Array.from(this.editedLabels.keys()));
        return { success: false, message: "Label not found" };
      }

      labelData.manuallyPositioned = true;
      const labelText = labelData.text || labelData.graphic?.symbol?.text || labelId;
      console.log(`[LabelManager] 🔒 Marked label "${labelText}" (${labelId}) as manually positioned`);
      // Reset collision tracking since manual repositioning may have resolved collisions
      this.resetCollisionTracking();
      console.log(`[LabelManager] Label data now:`, {
        labelId,
        text: labelText,
        manuallyPositioned: labelData.manuallyPositioned,
        position: labelData.position
      });

      return {
        success: true,
        message: `Marked label ${labelId} as manually positioned`,
      };
    } catch (error) {
      console.error("[LabelManager] Error marking label as manually positioned:", error);
      return {
        success: false,
        message: `Error marking label: ${error.message}`,
      };
    }
  }

  /**
   * Update label font size optimized with immediate save
   * @param {string|Object} labelOrId - Label ID or label graphic
   * @param {number} fontSize - New font size
   * @returns {Object} Result of the update operation
   */
  updateLabelFontSize(labelOrId, fontSize) {
    try {
      const labelId = typeof labelOrId === "string" ? labelOrId : this.getLabelId(labelOrId);
      if (!labelId) {
        return { success: false, message: "Invalid label ID" };
      }

      const labelData = this.editedLabels.get(labelId);
      if (!labelData?.graphic?.symbol) {
        return { success: false, message: "Label not found or invalid" };
      }

      // Verify context
      if (!this._isLabelInCurrentContext(labelData)) {
        return {
          success: false,
          message: `Label belongs to a different context`,
        };
      }

      // Update font size efficiently
      const newSymbol = labelData.graphic.symbol.clone();
      
      if (newSymbol.font) {
        newSymbol.font = { ...newSymbol.font, size: fontSize };
      } else {
        newSymbol.font = {
          family: this.settings.fontFamily,
          size: fontSize,
        };
      }

      labelData.graphic.symbol = newSymbol;

      // Update tracking with explicit fontSize
      labelData.fontSize = fontSize;
      // Trigger immediate save
      this._debouncedSave();

      return {
        success: true,
        message: `Updated font size for ${labelId}`,
      };
    } catch (error) {
      
      return {
        success: false,
        message: `Error updating font size: ${error.message}`,
      };
    }
  }

  /**
   * Update label text optimized
   * @param {string|Object} labelOrId - Label ID or label graphic
   * @param {string} text - New text
   * @returns {Object} Result of the update operation
   */
  updateLabelText(labelOrId, text) {
    try {
      const labelId = typeof labelOrId === "string" ? labelOrId : this.getLabelId(labelOrId);
      if (!labelId) {
        return { success: false, message: "Invalid label ID" };
      }

      const labelData = this.editedLabels.get(labelId);
      if (!labelData?.graphic?.symbol) {
        return { success: false, message: "Label not found or invalid" };
      }

      // Verify context
      if (!this._isLabelInCurrentContext(labelData)) {
        return {
          success: false,
          message: `Label belongs to a different context`,
        };
      }

      // Update text efficiently
      const newSymbol = labelData.graphic.symbol.clone();

      // Handle variable formatting
      if (labelData.graphic.attributes?.hasCustomFormat === true) {
        const originalText = newSymbol.text || "";
        const varMatch = originalText.match(/\s*\((.*)\)/);
        
        newSymbol.text = varMatch && varMatch[1] ? 
                        text + " (" + varMatch[1] + ")" : text;
      } else {
        newSymbol.text = text;
      }

      labelData.graphic.symbol = newSymbol;

      // Update attributes
      if (labelData.graphic.attributes) {
        labelData.graphic.attributes.text = text;
        labelData.graphic.attributes.labelText = newSymbol.text;
      }

      // Update tracking
      labelData.text = newSymbol.text;
      return {
        success: true,
        message: `Updated text for ${labelId}`,
      };
    } catch (error) {
      
      return {
        success: false,
        message: `Error updating text: ${error.message}`,
      };
    }
  }

  /**
   * Toggle editing mode optimized
   * @param {boolean} enableEdit - Whether to enable or disable edit mode
   * @param {boolean} exclusive - Whether to clear selection when disabling (default: true)
   * @returns {boolean} New edit mode state
   */
  toggleEditingMode(enableEdit, exclusive = true) {
    const newEditMode = enableEdit !== undefined ? enableEdit : !this.editMode;

    if (newEditMode === this.editMode) {
      return this.editMode;
    }

    this._cleanupEventHandlers();

    if (newEditMode) {
      this._enableOptimizedEditMode();
    } else {
      this._disableOptimizedEditMode();
      
      if (exclusive) {
        this.selectedLabel = null;
      }
    }

    this.editMode = newEditMode;
    return this.editMode;
  }

  /**
   * Enable optimized edit mode
   * @private
   */
  _enableOptimizedEditMode() {
    if (!this.view) return;

    // Change cursor
    if (this.view.container) {
      this.view.container.style.cursor = "pointer";
    }

    // Add optimized click handler for label selection
    this.handlers.push(
      this.view.on("click", (event) => {
        if (this.isMoving) {
          event.stopPropagation();
          return;
        }

        this._handleOptimizedLabelSelection(event);
      })
    );
  }

  /**
   * Handle optimized label selection
   * @param {Object} event - Click event
   * @private
   */
  _handleOptimizedLabelSelection(event) {
    try {
      this.view.hitTest(event.screenPoint)
        .then((response) => {
          if (!response?.results) {
            
            return;
          }

          // Find context-appropriate label hits
          const contextLabelHits = this._findContextLabelHits(response.results);

          if (contextLabelHits.length > 0) {
            event.stopPropagation();
            this.selectLabel(contextLabelHits[0]);
          } else {
            // Check if a data point was clicked and find its associated label
            const dataPointHit = this._findDataPointHits(response.results);
            if (dataPointHit) {
              const associatedLabel = this._findAssociatedLabel(dataPointHit);
              if (associatedLabel) {
                event.stopPropagation();
                this.selectLabel(associatedLabel);
                
                return;
              }
            }
            this.selectedLabel = null;
          }
        })
        .catch((error) => {
          
        });
    } catch (error) {
      
    }
  }

  /**
   * Find label hits in current context
   * @param {Array} results - Hit test results
   * @returns {Array} Context-appropriate label hits
   * @private
   */
  _findContextLabelHits(results) {
    const labelHits = results.filter((result) => {
      const graphic = result.graphic;
      return graphic && 
             (graphic.symbol?.type === "text" || graphic.attributes?.isLabel === true);
    });

    return labelHits.filter((hit) => {
      const attrs = hit.graphic.attributes || {};
      
      const configMatch = !attrs.mapConfigId || !this.mapConfigId || 
                         attrs.mapConfigId === this.mapConfigId;
      const typeMatch = !attrs.mapType || !this.mapType || 
                       attrs.mapType === this.mapType;

      return configMatch && typeMatch;
    }).map(hit => hit.graphic);
  }

  /**
   * Find data point hits in current context
   * @param {Array} results - Hit test results
   * @returns {Object|null} First context-appropriate data point hit
   * @private
   */
  _findDataPointHits(results) {
    const dataPointHits = results.filter((result) => {
      const graphic = result.graphic;
      // Data points are typically circle symbols without text
      return graphic && 
             graphic.geometry?.type === 'point' &&
             graphic.symbol?.style === 'circle' &&
             !graphic.symbol?.text && // Data points don't have text
             graphic.attributes?.isLabel !== true; // Explicitly not a label
    });

    // Filter by context like labels
    const contextDataPointHits = dataPointHits.filter((hit) => {
      const attrs = hit.graphic.attributes || {};
      
      const configMatch = !attrs.mapConfigId || !this.mapConfigId || 
                         attrs.mapConfigId === this.mapConfigId;
      const typeMatch = !attrs.mapType || !this.mapType || 
                       attrs.mapType === this.mapType;

      return configMatch && typeMatch;
    });

    return contextDataPointHits.length > 0 ? contextDataPointHits[0].graphic : null;
  }

  /**
   * Find the label associated with a data point
   * @param {Object} dataPointGraphic - The data point graphic
   * @returns {Object|null} Associated label graphic or null
   * @private
   */
  _findAssociatedLabel(dataPointGraphic) {
    if (!dataPointGraphic?.geometry || !this.view?.map) return null;

    const dataPointX = dataPointGraphic.geometry.x;
    const dataPointY = dataPointGraphic.geometry.y;
    const searchDistance = 0.01; // ~1000 meters search radius - ensures labels moved by collision resolution can be found

    // Search through all graphics in all layers to find associated labels
    const labels = [];
    const layers = this.view.map.allLayers.items;
    for (const layer of layers) {
      if (layer.graphics && layer.graphics.items) {
        for (const graphic of layer.graphics.items) {
          // Look for label graphics (text symbols)
          if (graphic.geometry?.type === 'point' && 
              graphic.symbol?.type === 'text' &&
              graphic !== dataPointGraphic) {
            
            // Check if this label is close to the data point
            const distance = Math.sqrt(
              Math.pow(graphic.geometry.x - dataPointX, 2) +
              Math.pow(graphic.geometry.y - dataPointY, 2)
            );

            // If within search distance, this is likely an associated label
            if (distance < searchDistance) {
              // Verify it's in the correct context
              const attrs = graphic.attributes || {};
              const configMatch = !attrs.mapConfigId || !this.mapConfigId || 
                                 attrs.mapConfigId === this.mapConfigId;
              const typeMatch = !attrs.mapType || !this.mapType || 
                               attrs.mapType === this.mapType;

              if (configMatch && typeMatch) {
                labels.push({ graphic, distance });
              }
            }
          }
        }
      }
    }

    if (labels.length === 0) {
      
      return null;
    }

    // Always sort by distance and return the closest label
    labels.sort((a, b) => a.distance - b.distance);
    
    if (labels.length > 1) {
      
    } else {
      
    }
    
    return labels[0].graphic;
  }

  /**
   * Remove duplicate labels that are too close to each other
   * @param {number} minDistance - Minimum distance between labels (default: 0.0001 degrees ~10 meters)
   */
  removeDuplicateLabels(minDistance = 0.0001) {
    if (!this.view?.map) return { removed: 0, message: "No map available" };

    const labelsToCheck = [];
    const layers = this.view.map.allLayers.items;

    // Collect all labels from all layers
    for (const layer of layers) {
      if (layer.graphics && layer.graphics.items) {
        for (const graphic of layer.graphics.items) {
          if (graphic.geometry?.type === 'point' && 
              graphic.symbol?.type === 'text') {
            
            // Verify it's in the correct context
            const attrs = graphic.attributes || {};
            const configMatch = !attrs.mapConfigId || !this.mapConfigId || 
                               attrs.mapConfigId === this.mapConfigId;
            const typeMatch = !attrs.mapType || !this.mapType || 
                             attrs.mapType === this.mapType;

            if (configMatch && typeMatch) {
              labelsToCheck.push({ graphic, layer });
            }
          }
        }
      }
    }

    const duplicatesToRemove = [];
    const processedLabels = new Set();

    // Check each label against all others
    for (let i = 0; i < labelsToCheck.length; i++) {
      const labelA = labelsToCheck[i];
      if (processedLabels.has(labelA.graphic)) continue;

      for (let j = i + 1; j < labelsToCheck.length; j++) {
        const labelB = labelsToCheck[j];
        if (processedLabels.has(labelB.graphic)) continue;

        // Calculate distance between labels
        const distance = Math.sqrt(
          Math.pow(labelA.graphic.geometry.x - labelB.graphic.geometry.x, 2) +
          Math.pow(labelA.graphic.geometry.y - labelB.graphic.geometry.y, 2)
        );

        // If labels are very close, mark one for removal
        if (distance < minDistance) {
          // Keep the label with more text content or better attributes
          const textA = labelA.graphic.symbol?.text || "";
          const textB = labelB.graphic.symbol?.text || "";
          
          if (textA.length >= textB.length) {
            duplicatesToRemove.push(labelB);
            processedLabels.add(labelB.graphic);
          } else {
            duplicatesToRemove.push(labelA);
            processedLabels.add(labelA.graphic);
            break; // Don't process labelA anymore
          }
        }
      }
    }

    // Remove duplicates
    let removedCount = 0;
    for (const duplicate of duplicatesToRemove) {
      try {
        duplicate.layer.remove(duplicate.graphic);
        
        // Also remove from tracking if present
        const labelId = this.getLabelId(duplicate.graphic);
        if (labelId && this.editedLabels.has(labelId)) {
          this.editedLabels.delete(labelId);
        }
        
        removedCount++;
      } catch (error) {
        
      }
    }
    return { removed: removedCount, message: `Removed ${removedCount} duplicate labels` };
  }

  /**
   * Disable optimized edit mode
   * @private
   */
  _disableOptimizedEditMode() {
    // Reset cursor
    if (this.view?.container) {
      this.view.container.style.cursor = "default";
    }

    // Reset states
    this.isMoving = false;
  }

  /**
   * Optimized event handler cleanup
   * @private
   */
  _cleanupEventHandlers() {
    for (const handler of this.handlers) {
      if (handler && typeof handler.remove === "function") {
        try {
          handler.remove();
        } catch (e) {
          // Ignore errors during cleanup
        }
      }
    }
    this.handlers = [];
  }

  /**
   * Cleanup all visible bounding boxes
   * @private
   */
  _cleanupBoundingBoxes() {
    this.boundingBoxGraphics.forEach((graphic, labelId) => {
      this._removeVisibleBoundingBox(labelId);
    });
    this.boundingBoxGraphics.clear();
  }

  /**
   * Select a label for editing with optimization
   * @param {Object} labelGraphic - The label graphic to select
   * @returns {Object} The selected label
   */
  selectLabel(labelGraphic) {
    if (!labelGraphic) return null;

    // Verify context efficiently
    if (!this._isGraphicInCurrentContext(labelGraphic)) {
      
      return null;
    }

    this.selectedLabel = labelGraphic;
    const labelId = this.getLabelId(labelGraphic);

    // Ensure tracking
    if (labelId && !this.editedLabels.has(labelId)) {
      this._addLabelToTracking(labelGraphic, labelId);
    }
    return labelGraphic;
  }

  /**
   * Add label to tracking efficiently
   * @param {Object} labelGraphic - Label graphic
   * @param {string} labelId - Label ID
   * @private
   */
  _addLabelToTracking(labelGraphic, labelId) {
    this.editedLabels.set(labelId, {
      graphic: labelGraphic,
      position: {
        x: labelGraphic.symbol?.xoffset || 0,
        y: labelGraphic.symbol?.yoffset || 0,
      },
      fontSize: labelGraphic.symbol?.font?.size || this.settings.fontSize,
      text: labelGraphic.symbol?.text || "",
      mapConfigId: this.mapConfigId,
      mapType: this.mapType,
      originalPosition: labelGraphic.geometry  // Store original position for zoom-based repositioning
    });
  }

  /**
   * Start moving a label with optimization
   * @returns {boolean} Whether moving was successfully started
   */
  startMovingLabel() {
    if (!this.selectedLabel || !this.view) {
      return false;
    }

    // Verify context
    if (!this._isGraphicInCurrentContext(this.selectedLabel)) {
      
      return false;
    }

    // Set moving state
    this.isMoving = true;

    // Update cursor
    if (this.view.container) {
      this.view.container.style.cursor = "move";
    }

    // Store and disable navigation
    this._storeAndDisableNavigation();

    // Add optimized move event handlers
    this._setupMoveEventHandlers();
    return true;
  }

  /**
   * Store and disable navigation
   * @private
   */
  _storeAndDisableNavigation() {
    this.originalNavState = {
      browserTouchPanEnabled: this.view.navigation.browserTouchPanEnabled,
      mouseWheelZoomEnabled: this.view.navigation.mouseWheelZoomEnabled,
      keyboardNavigation: this.view.navigation.keyboardNavigation,
    };

    this.view.navigation.browserTouchPanEnabled = false;
    this.view.navigation.mouseWheelZoomEnabled = false;
    this.view.navigation.keyboardNavigation = false;
  }

  /**
   * Setup optimized move event handlers
   * @private
   */
  _setupMoveEventHandlers() {
    // Pointer down handler
    this.handlers.push(
      this.view.on("pointer-down", (event) => {
        if (!this.isMoving || !this.selectedLabel) return;

        event.stopPropagation();
        event.preventDefault();

        this.dragInfo.startPoint = { x: event.x, y: event.y };
        this.dragInfo.labelOffset = {
          x: this.selectedLabel.symbol?.xoffset || 0,
          y: this.selectedLabel.symbol?.yoffset || 0,
        };
      })
    );

    // Pointer move handler
    this.handlers.push(
      this.view.on("pointer-move", (event) => {
        if (!this.isMoving || !this.selectedLabel || !this.dragInfo.startPoint) return;

        event.stopPropagation();
        event.preventDefault();

        // Track current pointer position for geometry update
        this.lastPointerPosition = { x: event.x, y: event.y };

        const dx = event.x - this.dragInfo.startPoint.x;
        const dy = event.y - this.dragInfo.startPoint.y;

        const newOffset = {
          x: this.dragInfo.labelOffset.x + dx,
          y: this.dragInfo.labelOffset.y - dy,
        };

        this.updateLabelOffset(newOffset);
      })
    );

    // Pointer up handler
    this.handlers.push(
      this.view.on("pointer-up", (event) => {
        event.stopPropagation();
        event.preventDefault();
        this.stopMovingLabel();
      })
    );

    // Pointer leave handler
    this.handlers.push(
      this.view.on("pointer-leave", () => {
        this.stopMovingLabel();
      })
    );
  }

  /**
   * Stop moving a label with optimization
   */
  stopMovingLabel() {
    if (!this.isMoving) return;

    // COORDINATE-LOCKED: Update geometry to final position
    if (this.selectedLabel && this.dragInfo.startPoint) {
      try {
        // Calculate final screen position
        const dx = this.lastPointerPosition?.x - this.dragInfo.startPoint.x || 0;
        const dy = this.lastPointerPosition?.y - this.dragInfo.startPoint.y || 0;
        
        // Get original label screen position
        const originalScreenPos = this.view.toScreen(this.selectedLabel.geometry);
        if (originalScreenPos) {
          // Calculate new screen position
          const newScreenPos = {
            x: originalScreenPos.x + dx,
            y: originalScreenPos.y + dy
          };
          
          // Convert to map coordinates
          const newMapPoint = this.view.toMap(newScreenPos);
          if (newMapPoint) {
            // Update the geometry to the new position
            this.selectedLabel.geometry = newMapPoint;
            
          }
        }
      } catch (error) {
        
      }
    }

    // Reset state
    this.isMoving = false;

    // Reset cursor
    if (this.view?.container) {
      this.view.container.style.cursor = "pointer";
    }

    // Clear drag info
    this.dragInfo = { startPoint: null, labelOffset: null };
    this.lastPointerPosition = null;

    // Restore navigation
    this._restoreNavigation();

    // Clean up and re-enable editing
    this._cleanupEventHandlers();

    if (this.editMode) {
      this._enableOptimizedEditMode();
    }

    // Save changes with debouncing
    this._debouncedSave();
  }

  /**
   * Restore navigation state
   * @private
   */
  _restoreNavigation() {
    if (this.view && this.originalNavState) {
      this.view.navigation.browserTouchPanEnabled = this.originalNavState.browserTouchPanEnabled;
      this.view.navigation.mouseWheelZoomEnabled = this.originalNavState.mouseWheelZoomEnabled;
      this.view.navigation.keyboardNavigation = this.originalNavState.keyboardNavigation;
    }
  }

  /**
   * Update the offset of the selected label optimized
   * @param {Object} offset - New pixel offset {x, y}
   */
  updateLabelOffset(offset) {
    if (!this.selectedLabel?.symbol) return;

    try {
      // Convert pixel offset to geographic coordinates for the selected label
      const originalScreenPos = this.view.toScreen(this.selectedLabel.geometry);
      
      // Calculate new screen position based on pixel offset
      const newScreenPos = {
        x: originalScreenPos.x + offset.x,
        y: originalScreenPos.y - offset.y // Y is inverted in screen coordinates
      };
      
      // Convert screen position to geographic coordinates
      const newMapPoint = this.view.toMap(newScreenPos);
      if (newMapPoint) {
        const labelId = this.getLabelId(this.selectedLabel);
        
        // Update label geometry using geographic coordinates
        this._applyPositionToGraphic(this.selectedLabel, {
          latitude: newMapPoint.y,
          longitude: newMapPoint.x
        }, labelId);
      }
    } catch (error) {
      
    }
  }

  /**
   * Optimized refresh labels with debouncing
   * @returns {Object} Result of the refresh operation
   */
  refreshLabels() {
    // Use debouncing to prevent excessive refresh calls
    if (this._refreshDebounceTimer) {
      clearTimeout(this._refreshDebounceTimer);
    }

    this._refreshDebounceTimer = setTimeout(() => {
      this._performOptimizedRefresh();
    }, 100);

    return {
      success: true,
      message: "Refresh scheduled",
      count: 0,
    };
  }

  /**
   * Perform optimized refresh
   * @private
   */
  _performOptimizedRefresh() {
    try {
      const savedPositions = this._getSavedPositions();
      let refreshCount = 0;
      let appliedStylesCount = 0;

      // Process tracked labels efficiently
      for (const [labelId, labelData] of this.editedLabels) {
        if (!this._isLabelInCurrentContext(labelData)) continue;

        if (labelData.graphic?.symbol) {
          try {
            // Reapply saved styles during refresh
            if (savedPositions[labelId]) {
              if (this._applySavedPosition(labelData.graphic)) {
                appliedStylesCount++;
              }
            } else if (this._hasTrackingStyles(labelData)) {
              // Save tracking styles to localStorage
              this._saveTrackingStyles(labelId, labelData, savedPositions);
              appliedStylesCount++;
            }

            // Update visibility based on zoom, but respect user's explicit visibility setting
            const savedVisible = savedPositions[labelId]?.visible;
            const userSetVisibility = savedVisible !== undefined ? savedVisible : (labelData.visible !== undefined ? labelData.visible : true);
            // Only show if user hasn't explicitly hidden AND zoom is sufficient
            const finalVisibility = userSetVisibility && (this.view.zoom >= this.settings.minZoom);
            labelData.graphic.visible = finalVisibility;
            // Also update attributes.visible so zoom handlers respect saved state
            if (labelData.graphic.attributes) {
              labelData.graphic.attributes.visible = userSetVisibility; // Keep user's preference in attributes
            }
            refreshCount++;
          } catch (labelError) {
            
          }
        }
      }

      // Refresh layers efficiently
      this._refreshLayers();
      return {
        success: true,
        message: `Refreshed ${refreshCount} labels, reapplied ${appliedStylesCount} styles`,
        count: refreshCount,
      };
    } catch (error) {
      
      return {
        success: false,
        message: `Error refreshing labels: ${error.message}`,
        count: 0,
      };
    }
  }

  /**
   * Check if tracking has styles
   * @param {Object} labelData - Label data
   * @returns {boolean} True if has styles
   * @private
   */
  _hasTrackingStyles(labelData) {
    return labelData.fontSize || labelData.fontWeight || labelData.backgroundColor;
  }

  /**
   * Save tracking styles to localStorage
   * @param {string} labelId - Label ID
   * @param {Object} labelData - Label data
   * @param {Object} savedPositions - Saved positions object
   * @private
   */
  _saveTrackingStyles(labelId, labelData, savedPositions) {
    // IMPORTANT: Save user's INTENDED visibility, not current graphic visibility
    // graphic.visible can be false due to zoom level, but we want to preserve
    // the user's explicit visibility preference (stored in labelData.visible or attributes.visible)
    const userIntendedVisibility = labelData.visible !== undefined ? labelData.visible :
                                   (labelData.graphic?.attributes?.visible !== undefined ?
                                    labelData.graphic.attributes.visible : true);

    savedPositions[labelId] = {
      position: labelData.position || { x: 0, y: 0 },
      fontSize: labelData.fontSize || this.settings.fontSize,
      fontWeight: labelData.fontWeight || "normal",
      backgroundColor: labelData.backgroundColor,
      borderLineColor: labelData.borderLineColor,
      borderLineSize: labelData.borderLineSize,
      text: labelData.text || "",
      visible: userIntendedVisibility,
      lastEdited: Date.now(),
      mapConfigId: this.mapConfigId,
      mapType: this.mapType,
    };

    localStorage.setItem(this.getStorageKey(), JSON.stringify(savedPositions));
    this._applySavedPosition(labelData.graphic);
  }

  /**
   * Refresh layers efficiently
   * @private
   */
  _refreshLayers() {
    // Refresh label layer
    if (this.labelLayer && typeof this.labelLayer.refresh === "function") {
      this.labelLayer.refresh();
    }

    // Refresh map layers with error handling
    if (this.view?.map?.layers) {
      for (const layer of this.view.map.layers) {
        if (layer.graphics && typeof layer.refresh === "function") {
          try {
            layer.refresh();
          } catch (layerError) {
            
          }
        }
      }
    }

    // Force view redraw
    if (this.view && typeof this.view.redraw === "function") {
      try {
        this.view.redraw();
      } catch (redrawError) {
        
      }
    }
  }

  /**
   * Update manager settings with optimization
   * @param {Object} newSettings - New settings
   */
  updateSettings(newSettings) {
    if (!newSettings) return;

    // Cache invalidation for settings that affect calculations
    if (newSettings.fontSize !== undefined || 
        newSettings.fontFamily !== undefined ||
        newSettings.enableTextMeasurementCache !== undefined) {
      this._textMeasurementCache.clear();
    }

    if (newSettings.enableBoundingBoxCache !== undefined) {
      this._boundingBoxCache = new WeakMap();
    }

    // Update settings
    this.settings = { ...this.settings, ...newSettings };
    // Apply relevant settings to labels
    if (newSettings.fontSize || newSettings.minZoom) {
      this.refreshLabels();
    }

    // Update auto-save if interval changed
    if (newSettings.autoSaveIntervalMs) {
      this._setupOptimizedAutoSave();
    }
  }

  /**
   * Configure layer settings based on layer type with optimization
   * @param {string} layerType - The type of layer (pipe, comp, custom, etc.)
   */
  configureLayerSettings(layerType) {
    if (!layerType) return;

    const type = layerType.toLowerCase();

    // Optimized settings lookup
    const typeSettings = {
      pipe: { fontSize: 10, haloSize: 3, minZoom: 8, autoSaveIntervalMs: 30000 },
      comp: { fontSize: 9, haloSize: 3.5, minZoom: 8, autoSaveIntervalMs: 30000 },
      custom: { fontSize: 8, haloSize: 3, minZoom: 8, autoSaveIntervalMs: 30000 },
    };

    const defaultSettings = typeSettings[type] || typeSettings.custom;

    // Check for existing user settings efficiently
    const savedPositions = this._getSavedPositions();
    const hasUserSettings = Object.keys(savedPositions).length > 0;

    let userFontSize = null;
    if (hasUserSettings) {
      // Find representative label font size
      const firstLabelId = Object.keys(savedPositions)[0];
      const firstLabel = savedPositions[firstLabelId];
      if (firstLabel?.fontSize !== undefined) {
        userFontSize = firstLabel.fontSize;
        
      }
    }

    // Merge settings preserving user fontSize
    const mergedSettings = {
      ...defaultSettings,
      fontSize: userFontSize !== null ? userFontSize : defaultSettings.fontSize,
    };

    this.updateSettings(mergedSettings);
    this.mapType = type;

    // Update session storage
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem("currentMapType", type);
    }
  }

  /**
   * Get all label graphics for the current context optimized
   * @returns {Array} Array of label graphics
   */
  getAllLabelGraphics() {
    const labels = [];
    let filteredCount = 0;

    console.log(`[getAllLabelGraphics] Current context:`, {
      mapConfigId: this.mapConfigId,
      mapType: this.mapType,
      totalLabels: this.editedLabels.size
    });

    for (const [labelId, labelData] of this.editedLabels.entries()) {
      const inContext = this._isLabelInCurrentContext(labelData);

      if (!inContext) {
        filteredCount++;
        console.log(`[getAllLabelGraphics] Filtered out label ${labelId}:`, {
          labelMapConfigId: labelData.mapConfigId,
          labelMapType: labelData.mapType,
          currentMapConfigId: this.mapConfigId,
          currentMapType: this.mapType
        });
        continue;
      }

      if (labelData.graphic) {
        labels.push(labelData.graphic);
      }
    }

    console.log(`[getAllLabelGraphics] Found ${labels.length} labels in current context, filtered ${filteredCount}`);
    return labels;
  }

  /**
   * Check if the API is available (optimized)
   * @returns {Promise<boolean>} Whether the API is available
   */
  async isAPIAvailable() {
    // Since we're using localStorage, always return true
    return Promise.resolve(true);
  }

  /**
   * Mark all saved positions as edited optimized
   * @returns {number} Number of positions marked
   */
  markSavedPositionsAsEdited() {
    const savedPositions = this._getSavedPositions();
    const positionIds = Object.keys(savedPositions);

    let markedCount = 0;

    // Process positions efficiently
    for (const labelId of positionIds) {
      const savedPos = savedPositions[labelId];

      // Skip different contexts
      if (!this._isSavedPositionInCurrentContext(savedPos)) continue;

      // Skip if already tracked
      if (this.editedLabels.has(labelId)) {
        markedCount++;
        continue;
      }

      // Find graphic efficiently
      const graphic = this._findGraphicInLayers(labelId);

      if (graphic) {
        this._addSavedPositionToTracking(labelId, savedPos, graphic);
        markedCount++;
      }
    }
    return markedCount;
  }

  /**
   * Check if saved position is in current context
   * @param {Object} savedPos - Saved position
   * @returns {boolean} True if in current context
   * @private
   */
  _isSavedPositionInCurrentContext(savedPos) {
    const configMatch = !savedPos.mapConfigId || !this.mapConfigId || 
                       savedPos.mapConfigId === this.mapConfigId;
    const typeMatch = !savedPos.mapType || !this.mapType || 
                     savedPos.mapType === this.mapType;

    return configMatch && typeMatch;
  }

  /**
   * Find graphic in layers efficiently
   * @param {string} targetLabelId - Label ID to find
   * @returns {Object|null} Found graphic or null
   * @private
   */
  _findGraphicInLayers(targetLabelId) {
    if (!this.view?.map?.layers) return null;

    for (const layer of this.view.map.layers) {
      if (!layer.graphics) continue;

      for (const graphic of layer.graphics) {
        const isLabel = graphic.symbol?.type === "text" || 
                       graphic.attributes?.isLabel === true;
        if (!isLabel) continue;

        const id = this.getLabelId(graphic);
        if (id === targetLabelId) {
          return graphic;
        }
      }
    }

    return null;
  }

  /**
   * Add saved position to tracking
   * @param {string} labelId - Label ID
   * @param {Object} savedPos - Saved position
   * @param {Object} graphic - Graphic object
   * @private
   */
  _addSavedPositionToTracking(labelId, savedPos, graphic) {
    this.editedLabels.set(labelId, {
      graphic: graphic,
      position: savedPos.position || { x: 0, y: 0 },
      fontSize: savedPos.fontSize || this.settings.fontSize,
      text: savedPos.text || "",
      fontWeight: savedPos.fontWeight || "normal",
      backgroundColor: savedPos.backgroundColor,
      borderLineColor: savedPos.borderLineColor,
      borderLineSize: savedPos.borderLineSize,
      manuallyPositioned: savedPos.manuallyPositioned || false, // Restore manual positioning flag
      mapConfigId: savedPos.mapConfigId || this.mapConfigId,
      mapType: savedPos.mapType || this.mapType,
    });
  }

  /**
   * Clean up and destroy the manager with optimization
   */
  destroy() {
    // Save any pending changes
    this.savePositions(true);

    // Reset collision tracking
    this.resetCollisionTracking();

    // Clean up event handlers
    this._cleanupEventHandlers();

    // Clean up visible bounding boxes
    this._cleanupBoundingBoxes();

    // Clear intervals and timers
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
      this.autoSaveInterval = null;
    }

    if (this._saveDebounceTimer) {
      clearTimeout(this._saveDebounceTimer);
      this._saveDebounceTimer = null;
    }

    if (this._refreshDebounceTimer) {
      clearTimeout(this._refreshDebounceTimer);
      this._refreshDebounceTimer = null;
    }

    // Clear caches
    this._textMeasurementCache.clear();
    this._boundingBoxCache = new WeakMap();

    // Clear references
    this.view = null;
    this.selectedLabel = null;
    this.editedLabels.clear();
    this.boundingBoxGraphics.clear();
    this._canvasContext = null;
    this._searchPatterns = null;
    this._zoomIndependentPatterns = null;
  }

  /**
   * Performance monitoring and statistics
   * @returns {Object} Performance statistics
   */
  getPerformanceStats() {
    return {
      trackedLabels: this.editedLabels.size,
      textMeasurementCacheSize: this._textMeasurementCache.size,
      boundingBoxCacheEnabled: this.settings.enableBoundingBoxCache,
      textMeasurementCacheEnabled: this.settings.enableTextMeasurementCache,
      currentContext: {
        mapConfigId: this.mapConfigId,
        mapType: this.mapType,
        projectId: this.projectId,
      },
      settings: {
        collisionDetectionThreshold: this.settings.collisionDetectionThreshold,
        maxOptimizationAttempts: this.settings.maxOptimizationAttempts,
        autoSaveIntervalMs: this.settings.autoSaveIntervalMs,
      },
    };
  }

  /**
   * Clear all caches for memory management
   */
  clearCaches() {
    this._textMeasurementCache.clear();
    this._boundingBoxCache = new WeakMap();
    this._cachedStorageKey = null;
    
    // Clear any cached importance weights on graphics
    for (const labelData of this.editedLabels.values()) {
      if (labelData.graphic && labelData.graphic._cachedImportanceWeight) {
        delete labelData.graphic._cachedImportanceWeight;
      }
    }
  }

  /**
   * Optimize performance by adjusting settings based on label count
   * @param {number} labelCount - Number of labels
   */
  optimizeForLabelCount(labelCount) {
    if (labelCount > 1000) {
      // High label count - prioritize performance
      this.updateSettings({
        maxOptimizationAttempts: 50,
        collisionDetectionThreshold: 0.5,
        enableTextMeasurementCache: true,
        enableBoundingBoxCache: true,
      });
      
    } else if (labelCount > 100) {
      // Medium label count - balanced approach
      this.updateSettings({
        maxOptimizationAttempts: 70,
        collisionDetectionThreshold: 0.3,
        enableTextMeasurementCache: true,
        enableBoundingBoxCache: true,
      });
      
    } else {
      // Low label count - prioritize quality
      this.updateSettings({
        maxOptimizationAttempts: 100,
        collisionDetectionThreshold: 0.1,
        enableTextMeasurementCache: false,
        enableBoundingBoxCache: false,
      });
      
    }
  }

  /**
   * Check if two bounding boxes overlap
   * @param {Object} box1 - First bounding box {x, y, width, height}
   * @param {Object} box2 - Second bounding box {x, y, width, height}
   * @returns {boolean} True if boxes overlap
   * @private
   */
  _boxesOverlap(box1, box2) {
    return !(box1.x + box1.width < box2.x ||
             box2.x + box2.width < box1.x ||
             box1.y + box1.height < box2.y ||
             box2.y + box2.height < box1.y);
  }

  /**
   * Check if a label bounding box collides with a data point circle
   * @param {Object} labelBox - Label bounding box {x, y, width, height, centerX, centerY}
   * @param {Point} dataPointGeometry - Data point position
   * @param {number} pixelToMapRatio - Pixel to map unit ratio
   * @returns {boolean} True if collision detected
   * @private
   */
  _checkCollisionWithDataPoint(labelBox, dataPointGeometry, pixelToMapRatio) {
    if (!labelBox || !dataPointGeometry) {
      return false;
    }

    // Check if dataPointGeometry has x and y properties
    if (typeof dataPointGeometry.x === 'undefined' || typeof dataPointGeometry.y === 'undefined') {
      return false;
    }

    // CRITICAL FIX: Normalize data point geometry to view's spatial reference
    // The labelBox is in view coordinates, but dataPointGeometry might be in a different SR
    let normalizedPoint = dataPointGeometry;
    const viewSR = this.view?.spatialReference?.wkid;
    const pointSR = dataPointGeometry.spatialReference?.wkid;

    if (viewSR && pointSR && viewSR !== pointSR) {
      try {
        if (pointSR === 4326 && (viewSR === 3857 || viewSR === 102100)) {
          // Convert Geographic (lat/lon) to Web Mercator
          const mercatorPoint = webMercatorUtils.geographicToWebMercator(dataPointGeometry);

          if (mercatorPoint && typeof mercatorPoint.x === 'number' && typeof mercatorPoint.y === 'number') {
            normalizedPoint = {
              x: mercatorPoint.x,
              y: mercatorPoint.y,
              spatialReference: { wkid: viewSR }
            };
          } else {
            console.error('[DataPointCollision] Conversion returned invalid point:', mercatorPoint);
            return false;
          }
        } else if ((pointSR === 3857 || pointSR === 102100) && viewSR === 4326) {
          // Convert Web Mercator to Geographic (lat/lon)
          const geoPoint = webMercatorUtils.webMercatorToGeographic(dataPointGeometry);
          normalizedPoint = {
            x: geoPoint.x,
            y: geoPoint.y,
            spatialReference: { wkid: 4326 }
          };
        }
      } catch (error) {
        console.error('[DataPointCollision] Spatial reference conversion failed:', error);
        return false;
      }
    }

    const dataPointRadius = this._getDataPointRadius();
    // Add a buffer (50% of radius) to ensure labels stay clearly separated from data points
    // This accounts for label bounding boxes being rectangles, not circles
    const bufferFactor = 1.5;
    const effectiveRadius = dataPointRadius * bufferFactor;

    // Convert data point radius from screen pixels to map units
    const dataPointRadiusInMapUnits = effectiveRadius * pixelToMapRatio;

    // CRITICAL: Rectangle-to-Circle collision detection
    // Find the closest point on the rectangle to the circle's center
    const closestX = Math.max(labelBox.x, Math.min(normalizedPoint.x, labelBox.x + labelBox.width));
    const closestY = Math.max(labelBox.y, Math.min(normalizedPoint.y, labelBox.y + labelBox.height));

    // Calculate distance from this closest point to the circle's center
    const dx = normalizedPoint.x - closestX;
    const dy = normalizedPoint.y - closestY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Collision if distance is less than circle radius
    const hasCollision = distance < dataPointRadiusInMapUnits;

    // Debug logging disabled for performance
    // Uncomment only when debugging collision detection issues
    // if (Math.random() < 0.001) {
    //   console.log(`[DataPointCollision] distance=${distance.toFixed(2)}, radius=${dataPointRadiusInMapUnits.toFixed(2)}, collision=${hasCollision}`);
    // }

    return hasCollision;
  }

  /**
   * Calculate bounding box for a label at given position
   * @param {Object} labelGraphic - Label graphic
   * @param {Object} centerMapPoint - Center position in map coordinates
   * @param {Object} cachedContext - Optional cached calculations to avoid redundant work
   * @returns {Object} Bounding box in map coordinates
   * @private
   */
  _calculateBoundingBoxAtPosition(labelGraphic, centerMapPoint, cachedContext = null) {
    const text = labelGraphic.symbol?.text || "";
    const fontSize = labelGraphic.symbol?.font?.size || this.settings.fontSize;
    const fontWeight = labelGraphic.symbol?.font?.weight || this.settings.fontWeight;
    const fontFamily = labelGraphic.symbol?.font?.family || this.settings.fontFamily;

    // CRITICAL FIX: Normalize the center point to the view's spatial reference system
    // This ensures all bounding boxes are calculated in the same coordinate system
    let normalizedCenterPoint = centerMapPoint;
    const viewSR = this.view?.spatialReference?.wkid;
    const pointSR = centerMapPoint.spatialReference?.wkid;

    if (viewSR && pointSR && viewSR !== pointSR) {
      try {
        if (pointSR === 4326 && (viewSR === 3857 || viewSR === 102100)) {
          // Convert Geographic to Web Mercator
          const mercatorPoint = webMercatorUtils.geographicToWebMercator(centerMapPoint);
          normalizedCenterPoint = new Point({
            x: mercatorPoint.x,
            y: mercatorPoint.y,
            spatialReference: { wkid: viewSR }
          });
        } else if ((pointSR === 3857 || pointSR === 102100) && viewSR === 4326) {
          // Convert Web Mercator to Geographic
          const geoPoint = webMercatorUtils.webMercatorToGeographic(centerMapPoint);
          normalizedCenterPoint = new Point({
            x: geoPoint.x,
            y: geoPoint.y,
            spatialReference: { wkid: 4326 }
          });
        }
      } catch (error) {
        
      }
    }

    let textDimensions, pixelToMapRatio;

    if (cachedContext && cachedContext.labelId === labelGraphic.attributes?.id) {
      // Use cached values if available for this specific label
      textDimensions = cachedContext.textDimensions;
      pixelToMapRatio = cachedContext.pixelToMapRatio;
    } else {
      // Calculate fresh values
      textDimensions = this._getOptimizedTextDimensions(text, fontSize, fontWeight, fontFamily);

      // Validate text dimensions
      if (!textDimensions || typeof textDimensions.width !== 'number' || typeof textDimensions.height !== 'number') {
        
        return null;
      }

      // Calculate bounding box size in map units
      const mapExtentWidth = this.view?.extent?.width;
      const viewWidth = this.view?.width;

      // Validate view dimensions
      if (!mapExtentWidth || !viewWidth || mapExtentWidth <= 0 || viewWidth <= 0) {
        
        return null;
      }

      // For geographic coordinates (lat/lon), we need to convert to degrees per pixel
      // For projected coordinates, we use the standard map units per pixel
      // IMPORTANT: Use the view's spatial reference for consistency, not the centerMapPoint's
      const viewSR = this.view?.spatialReference?.wkid;
      const isGeographic = viewSR === 4326;
      pixelToMapRatio = mapExtentWidth / viewWidth;

      // Only log once per collision detection session, not for every position test
      if (!cachedContext) {
        if (isGeographic) {
          
        } else {
          
        }
      }
    }

    // Validate pixel ratio
    if (!isFinite(pixelToMapRatio) || pixelToMapRatio <= 0) {
      
      return null;
    }

    const padding = 4;

    // Ensure minimum dimensions for short labels to prevent collision detection failures
    // Short labels (1-3 characters) can have very small bounding boxes that slip through collision detection
    const minWidth = 40; // Minimum 40 pixels width
    const minHeight = 20; // Minimum 20 pixels height

    const effectiveWidth = Math.max(textDimensions.width + padding * 2, minWidth);
    const effectiveHeight = Math.max(textDimensions.height + padding * 2, minHeight);

    const boxWidthInMapUnits = effectiveWidth * pixelToMapRatio;
    const boxHeightInMapUnits = effectiveHeight * pixelToMapRatio;

    // Validate final calculations
    if (!isFinite(boxWidthInMapUnits) || !isFinite(boxHeightInMapUnits) || boxWidthInMapUnits <= 0 || boxHeightInMapUnits <= 0) {
      
      return null;
    }

    const boundingBox = {
      x: normalizedCenterPoint.x - boxWidthInMapUnits / 2,
      y: normalizedCenterPoint.y - boxHeightInMapUnits / 2,
      width: boxWidthInMapUnits,
      height: boxHeightInMapUnits,
      centerX: normalizedCenterPoint.x,
      centerY: normalizedCenterPoint.y,
      xmin: normalizedCenterPoint.x - boxWidthInMapUnits / 2,
      xmax: normalizedCenterPoint.x + boxWidthInMapUnits / 2,
      ymin: normalizedCenterPoint.y - boxHeightInMapUnits / 2,
      ymax: normalizedCenterPoint.y + boxHeightInMapUnits / 2
    };

    // Final validation
    if (Object.values(boundingBox).some(val => !isFinite(val))) {
      
      return null;
    }

    return boundingBox;
  }

  /**
   * Find optimal position for label to avoid collisions
   * @param {Object} labelGraphic - Label graphic to position
   * @param {Array} existingBoxes - Array of existing bounding boxes
   * @param {number} maxAttempts - Maximum positioning attempts
   * @param {number} startTime - Start time for timeout calculation
   * @param {number} maxTime - Maximum time allowed in milliseconds
   * @returns {Object} Optimal map position {x, y} or null if no position found
   * @private
   */
  _findOptimalPosition(labelGraphic, existingBoxes, maxAttempts = 12, startTime = Date.now(), maxTime = 5000) {
    const originalGeometry = labelGraphic.geometry;

    // Create a cached context to avoid recalculating text dimensions and pixel ratios
    const text = labelGraphic.symbol?.text || "";
    const fontSize = labelGraphic.symbol?.font?.size || this.settings.fontSize;
    const fontWeight = labelGraphic.symbol?.font?.weight || this.settings.fontWeight;
    const fontFamily = labelGraphic.symbol?.font?.family || this.settings.fontFamily;
    const textDimensions = this._getOptimizedTextDimensions(text, fontSize, fontWeight, fontFamily);

    const mapExtentWidth = this.view?.extent?.width;
    const viewWidth = this.view?.width;
    const pixelToMapRatio = mapExtentWidth / viewWidth;

    const cachedContext = {
      labelId: labelGraphic.attributes?.id,
      textDimensions,
      pixelToMapRatio
    };

    // Calculate the label's current bounding box to size the search radius appropriately
    const currentBoundingBox = this._calculateBoundingBoxAtPosition(labelGraphic, originalGeometry, cachedContext);
    const labelSize = currentBoundingBox ? Math.max(currentBoundingBox.width, currentBoundingBox.height) : null;

    // Generate concentric circles of positions to try, considering the coordinate system and label size
    const concentricCircles = this._generateConcentricCirclePositions(originalGeometry.spatialReference, labelSize, originalGeometry, cachedContext);

    let validPositions = [];
    let totalAttempts = 0;
    let timedOut = false;

    // Test all positions across all circles to find all collision-free options
    for (let circleIndex = 0; circleIndex < concentricCircles.length && totalAttempts < maxAttempts; circleIndex++) {
      // Check timeout
      if (Date.now() - startTime > maxTime) {
        timedOut = true;
        break;
      }

      const circle = concentricCircles[circleIndex];
      let circleValidPositions = [];

      for (let posIndex = 0; posIndex < circle.length && totalAttempts < maxAttempts; posIndex++) {
        // Check timeout
        if (Date.now() - startTime > maxTime) {
          timedOut = true;
          break;
        }
        const offset = circle[posIndex];
        const testMapPoint = new Point({
          x: originalGeometry.x + offset.x,
          y: originalGeometry.y + offset.y,
          spatialReference: originalGeometry.spatialReference
        });

        const testBox = this._calculateBoundingBoxAtPosition(labelGraphic, testMapPoint, cachedContext);
        totalAttempts++;

        if (!testBox) continue;

        // Check if this position collides with existing label boxes
        let hasCollision = false;
        let collidingBox = null;
        for (const existingBox of existingBoxes) {
          if (this._boxesOverlap(testBox, existingBox)) {
            hasCollision = true;
            collidingBox = existingBox;
            break;
          }
        }

        // Check collision with this label's own data point circle (home position)
        const ownCollision = this._checkCollisionWithDataPoint(testBox, originalGeometry, pixelToMapRatio);
        if (ownCollision) {
          hasCollision = true;
        }

        // Check collision with ALL other data point circles
        const currentLabelId = this.getLabelId(labelGraphic);
        for (const [otherId, otherData] of this.editedLabels.entries()) {
          if (otherId !== currentLabelId && otherData.originalPosition) {
            const collisionResult = this._checkCollisionWithDataPoint(testBox, otherData.originalPosition, pixelToMapRatio);
            if (collisionResult) {
              hasCollision = true;
              break;
            }
          }
        }

        if (!hasCollision) {
          circleValidPositions.push({
            point: testMapPoint,
            offset: offset,
            circleIndex: circleIndex,
            posIndex: posIndex,
            distance: Math.sqrt(offset.x * offset.x + offset.y * offset.y)
          });
        }
      }

      if (timedOut) break;

      if (circleValidPositions.length > 0) {
        // IMPORTANT: Always return positions from the closest circle immediately
        // The whole point is to find the CLOSEST valid position, not explore all options
        // Since circles are ordered from closest to farthest, the first valid circle wins

        // Within this closest circle, positions are already ordered by smart direction priority
        // No need to sort by distance as all positions in this circle are equidistant

        // Return all valid positions for this circle
        return {
          positions: circleValidPositions,
          circle: circleIndex + 1,
          distance: circleValidPositions[0].distance
        };
      }
    }
    return originalGeometry; // Return original position to allow overlapping labels
  }

  /**
   * Clear all bounding box visualizations from the debug layer
   * @private
   */
  _clearBoundingBoxVisualizations() {
    try {
      // Find the bounding box debug layer
      const layers = this.view?.map?.layers || [];
      for (const layer of layers) {
        if (layer.title && layer.title.includes('Bounding Boxes') && layer.type === 'graphics') {
          const beforeCount = layer.graphics.length;
          layer.graphics.removeAll();
          
          break;
        }
      }
    } catch (error) {
      
    }
  }

  /**
   * Analyze surrounding space to determine optimal direction priority
   * @param {Point} centerPoint - Center point to analyze around
   * @param {number} radius - Current search radius
   * @param {Object} cachedContext - Cached context for collision detection
   * @returns {Array} Array of direction objects ordered by available space
   * @private
   */
  _getSmartDirectionPriority(centerPoint, radius, cachedContext) {
    // Default directions with their properties
    const allDirections = [
      { angle: 90, name: 'N' },    // North (up)
      { angle: 45, name: 'NE' },   // Northeast
      { angle: 0, name: 'E' },     // East (right)
      { angle: 315, name: 'SE' },  // Southeast
      { angle: 270, name: 'S' },   // South (down)
      { angle: 225, name: 'SW' },  // Southwest
      { angle: 180, name: 'W' },   // West (left)
      { angle: 135, name: 'NW' }   // Northwest
    ];

    // If no center point or context provided, use fallback priority (prefer vertical over horizontal)
    if (!centerPoint || !cachedContext) {
      return [
        { angle: 90, name: 'N' },    // North (up)
        { angle: 270, name: 'S' },   // South (down)
        { angle: 135, name: 'NW' },  // Northwest
        { angle: 45, name: 'NE' },   // Northeast
        { angle: 225, name: 'SW' },  // Southwest
        { angle: 315, name: 'SE' },  // Southeast
        { angle: 180, name: 'W' },   // West (left)
        { angle: 0, name: 'E' }      // East (right)
      ];
    }

    // Analyze each direction for available space AND proximity
    const directionScores = allDirections.map(direction => {
      let score = 0;
      let closestValidDistance = Infinity;
      const angleRad = (direction.angle * Math.PI) / 180;

      // Check multiple points along this direction to find the CLOSEST valid position
      // Start very close and work outward
      const checkDistances = [
        radius * 0.1,  // Very close
        radius * 0.3,  // Close
        radius * 0.5,  // Medium-close
        radius * 0.8,  // Medium
        radius * 1.0,  // Target radius
        radius * 1.3,  // Slightly farther
        radius * 1.6   // Farther
      ];

      for (const checkDistance of checkDistances) {
        const testX = centerPoint.x + checkDistance * Math.cos(angleRad);
        const testY = centerPoint.y + checkDistance * Math.sin(angleRad);

        const testPoint = new Point({
          x: testX,
          y: testY,
          spatialReference: centerPoint.spatialReference
        });

        // Quick collision check at this test point
        const testBox = this._calculateBoundingBoxAtPosition(
          { geometry: testPoint }, // Mock graphic with just geometry
          testPoint,
          cachedContext
        );

        if (testBox) {
          const hasCollision = this._detectAllCollisions(testBox, cachedContext.labelId);
          if (!hasCollision) {
            // Found a valid position! Track how close it is
            if (checkDistance < closestValidDistance) {
              closestValidDistance = checkDistance;
            }
            // Give points for having valid positions
            score += 1;
          }
        }
      }

      // HUGE bonus for directions that can place labels very close to the original point
      if (closestValidDistance !== Infinity) {
        // The closer we can get, the higher the bonus (inverse relationship)
        const proximityBonus = 10 / (closestValidDistance / radius + 0.1); // Max ~100 points for very close positions
        score += proximityBonus;
      }

      // Small bonus for cardinal directions (N, S, E, W) as they're often cleaner
      if ([0, 90, 180, 270].includes(direction.angle)) {
        score += 0.5;
      }

      // Slight preference for vertical positioning (N, S) over horizontal (E, W)
      if ([90, 270].includes(direction.angle)) {
        score += 0.2;
      }

      return { ...direction, score, closestValidDistance };
    });

    // Sort by score (higher is better), then by traditional preference as tiebreaker
    directionScores.sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score; // Higher score first
      }

      // Tiebreaker: prefer traditional order (N, S, NW, NE, SW, SE, W, E)
      const traditionalPriority = { 'N': 1, 'S': 2, 'NW': 3, 'NE': 4, 'SW': 5, 'SE': 6, 'W': 7, 'E': 8 };
      return (traditionalPriority[a.name] || 9) - (traditionalPriority[b.name] || 9);
    });
    return directionScores;
  }

  /**
   * Generate concentric circles of positions around the original point
   * Each circle has 8 evenly distributed positions with smart direction prioritization
   * @param {Object} spatialReference - Spatial reference system
   * @param {number} labelSize - Size of the label for radius calculation
   * @param {Point} centerPoint - Center point to analyze surrounding space (optional)
   * @param {Object} cachedContext - Cached context for collision detection (optional)
   * @returns {Array<Array>} Array of circles, each containing position offsets
   * @private
   */
  _generateConcentricCirclePositions(spatialReference = null, labelSize = null, centerPoint = null, cachedContext = null) {
    const circles = [];

    // Determine appropriate base radius based on coordinate system, zoom level, and label size
    let baseRadius;
    const wkid = spatialReference?.wkid || this.view?.spatialReference?.wkid;
    const currentZoom = this.view?.zoom || 15;

    if (wkid === 4326) {
      // Geographic coordinates (WGS84) - scale with zoom level
      // At higher zoom levels (zoomed in), we need LARGER geographic offsets to move the same screen distance
      const zoomScale = Math.pow(2, Math.max(0, currentZoom - 10)); // Scale UP for higher zoom
      let defaultRadius = 0.00001 * zoomScale; // Increase radius proportionally with zoom level

      if (labelSize && labelSize > 0) {
        // For geographic coordinates, the labelSize is likely in map units (meters)
        // Convert to degrees: rough approximation ~111,000 meters per degree at equator
        const labelSizeInDegrees = labelSize / 111000; // Convert meters to degrees

        // Base radius should be just enough to clear the label boundary - much smaller than 2x
        const minRadius = labelSizeInDegrees * 0.15; // Reduced from 0.3x to 0.15x for much closer positioning
        baseRadius = Math.max(defaultRadius, minRadius);
      } else {
        // Always start with a very small base radius when no label size
        baseRadius = defaultRadius;
        
      }
    } else {
      // Web Mercator or other projected coordinates - scale with zoom
      // At higher zoom levels, use larger offsets to achieve same screen distance
      const zoomScale = Math.pow(2, Math.max(0, currentZoom - 10));
      let defaultRadius = 5 * zoomScale; // Scale radius with zoom level

      if (labelSize && labelSize > 0) {
        // Base radius should be just enough to clear the label boundary - much smaller than 2x
        const minRadius = labelSize * 0.15; // Reduced from 0.3x to 0.15x for much closer positioning
        baseRadius = Math.max(defaultRadius, minRadius);
      } else {
        // Always start with a very small base radius when no label size
        baseRadius = defaultRadius;
        
      }
    }

    // Start with very tiny increments, then gradually increase - much more conservative
    const radiusMultipliers = [0.05, 0.15, 0.3, 0.5, 0.8, 1.2, 1.8, 2.5]; // Start extremely close, then progress very gradually

    for (const multiplier of radiusMultipliers) {
      const radius = baseRadius * multiplier;
      const circle = [];

      // 8 directions around the circle - intelligently ordered based on available space
      const directions = this._getSmartDirectionPriority(centerPoint, radius, cachedContext);

      for (const direction of directions) {
        const angleRad = (direction.angle * Math.PI) / 180;
        const offset = {
          x: radius * Math.cos(angleRad),
          y: radius * Math.sin(angleRad),
          radius: radius,
          direction: direction.name,
          angle: direction.angle
        };
        circle.push(offset);
      }

      circles.push(circle);
    }

    return circles;
  }

  /**
   * Calculate the visual center of a label (extracted from _createVisibleBoundingBox)
   * @param {Object} labelGraphic - Label graphic
   * @param {string} labelId - Label ID
   * @returns {Promise<Object>} Visual center map point
   * @private
   */
  async _calculateLabelVisualCenter(labelGraphic, labelId) {
    if (!labelGraphic.geometry) return null;

    const anchorPoint = labelGraphic.geometry;
    const text = labelGraphic.symbol?.text || "";
    const fontSize = labelGraphic.symbol?.font?.size || this.settings.fontSize;
    const fontWeight = labelGraphic.symbol?.font?.weight || this.settings.fontWeight;
    const fontFamily = labelGraphic.symbol?.font?.family || this.settings.fontFamily;
    const horizontalAlignment = labelGraphic.symbol?.horizontalAlignment || "center";
    const verticalAlignment = labelGraphic.symbol?.verticalAlignment || "bottom";
    
    const textDimensions = this._getOptimizedTextDimensions(text, fontSize, fontWeight, fontFamily);
    const anchorScreen = this.view.toScreen(anchorPoint);
    
    if (!anchorScreen) return null;
    
    // Calculate visual center offset based on alignment
    let centerOffsetX = 0;
    let centerOffsetY = 0;
    
    if (horizontalAlignment === "left") {
      centerOffsetX = textDimensions.width / 2;
    } else if (horizontalAlignment === "right") {
      centerOffsetX = -textDimensions.width / 2;
    }
    
    if (verticalAlignment === "baseline" || verticalAlignment === "bottom") {
      centerOffsetY = -textDimensions.height / 2;
    } else if (verticalAlignment === "top") {
      centerOffsetY = textDimensions.height / 2;
    }
    
    const centerScreen = {
      x: anchorScreen.x + centerOffsetX,
      y: anchorScreen.y + centerOffsetY
    };
    
    return this.view.toMap(centerScreen);
  }

  /**
   * Resolve label collisions by repositioning conflicting labels
   * @returns {Promise<Object>} Result of collision resolution
   */
  async resolveCollisions() {
    // Start timer to prevent freezing - max 5 seconds
    const startTime = Date.now();
    const MAX_TIMEOUT = 5000; // 5 seconds
    let timedOut = false;

    // Find all map layers with graphics (labels and points)
    const mapLayers = this.view?.map?.allLayers?.items?.filter(
      layer => layer.graphics && layer.title?.includes('Map Layer')
    ) || [];

    const allLabels = [];
    for (const layer of mapLayers) {
      const layerLabels = layer.graphics.toArray().filter(
        graphic => graphic.attributes?.isLabel ||
                  (graphic.symbol?.type === 'text' || graphic.symbol?.declaredClass?.includes('Text'))
      );
      allLabels.push(...layerLabels);
    }

    if (!allLabels.length) {
      return { success: true, message: "No labels to process", repositioned: 0 };
    }
    // Clear any existing bounding box visualizations to prevent stale collision data
    this._clearBoundingBoxVisualizations();

    const labels = allLabels;
    let repositionedCount = 0;
    let totalCollisionCount = 0;
    const maxIterations = 3; // Maximum number of collision resolution passes

    // Perform multiple passes to handle newly created collisions
    for (let iteration = 0; iteration < maxIterations; iteration++) {
      // Check timeout before each iteration
      if (Date.now() - startTime > MAX_TIMEOUT) {
        console.warn(`[SimplifiedLabelManager] Collision resolution timed out after ${MAX_TIMEOUT}ms. Stopping to prevent page freeze.`);
        timedOut = true;
        break;
      }

      const currentCollisions = await this._detectAllCollisions(labels);

      if (currentCollisions.length === 0) {

        break;
      }
      totalCollisionCount = Math.max(totalCollisionCount, currentCollisions.length);

      // Process each collision
      let passRepositionedCount = 0;
      for (let i = 0; i < currentCollisions.length; i++) {
        // Check timeout during collision processing
        if (Date.now() - startTime > MAX_TIMEOUT) {
          console.warn(`[SimplifiedLabelManager] Collision resolution timed out after ${MAX_TIMEOUT}ms during iteration ${iteration + 1}. Stopping to prevent page freeze.`);
          timedOut = true;
          break;
        }

        const collision = currentCollisions[i];
        const { label1, label2, labelId1, labelId2 } = collision;
        // Try to reposition the second label (label2) first, as it's typically processed later
        const repositioned = await this._attemptLabelRepositioning(label2, labelId2, labels, startTime, MAX_TIMEOUT);

        if (repositioned) {
          passRepositionedCount++;
          repositionedCount++;
        } else {
          // If label2 couldn't be repositioned, try label1
          const repositioned1 = await this._attemptLabelRepositioning(label1, labelId1, labels, startTime, MAX_TIMEOUT);
          if (repositioned1) {
            passRepositionedCount++;
            repositionedCount++;
          }
        }
      }

      // Break outer loop if timed out
      if (timedOut) {
        break;
      }

      // If no labels were repositioned in this pass, stop to avoid infinite loops
      if (passRepositionedCount === 0) {

        break;
      }
    }

    // Update bounding boxes display
    if (this.showVisibleBoundingBoxes) {
      this._updateAllBoundingBoxes();
    }

    // Final collision check for reporting (skip if timed out to avoid extra processing)
    const finalCollisions = timedOut ? [] : await this._detectAllCollisions(labels);

    let finalMessage;
    if (timedOut) {
      finalMessage = `⏱️ Collision resolution stopped after 5 seconds to prevent page freeze. Repositioned ${repositionedCount} labels. Some collisions may remain.`;
    } else if (repositionedCount > 0) {
      finalMessage = `🎯 Enhanced collision resolution complete! Processed ${totalCollisionCount} initial collisions, repositioned ${repositionedCount} labels, ${finalCollisions.length} collisions remain.`;
    } else if (totalCollisionCount > 0) {
      finalMessage = `⚠️  Found ${totalCollisionCount} collisions but could not reposition any labels. ${finalCollisions.length} collisions remain.`;
    } else {
      finalMessage = `✨ All ${labels.length} labels are perfectly positioned - no collisions detected!`;
    }

    return {
      success: true,
      message: finalMessage,
      repositioned: repositionedCount,
      totalLabels: labels.length,
      collisionsDetected: totalCollisionCount,
      finalCollisions: timedOut ? totalCollisionCount : finalCollisions.length,
      timedOut: timedOut
    };
  }

  /**
   * Detect all collisions between labels
   * @param {Array} labels - Array of label graphics
   * @returns {Promise<Array>} Array of collision objects
   * @private
   */
  async _detectAllCollisions(labels) {
    const collisions = [];

    // Pre-calculate shared pixel ratio to avoid redundant calculations
    const mapExtentWidth = this.view?.extent?.width;
    const viewWidth = this.view?.width;
    const pixelToMapRatio = mapExtentWidth / viewWidth;

    for (let i = 0; i < labels.length; i++) {
      const label1 = labels[i];
      const labelId1 = this.getLabelId(label1);
      if (!labelId1) continue;

      // Create cached context for label1
      const text1 = label1.symbol?.text || "";
      const fontSize1 = label1.symbol?.font?.size || this.settings.fontSize;
      const fontWeight1 = label1.symbol?.font?.weight || this.settings.fontWeight;
      const fontFamily1 = label1.symbol?.font?.family || this.settings.fontFamily;
      const textDimensions1 = this._getOptimizedTextDimensions(text1, fontSize1, fontWeight1, fontFamily1);

      const cachedContext1 = {
        labelId: labelId1,
        textDimensions: textDimensions1,
        pixelToMapRatio
      };

      const box1 = await this._calculateBoundingBoxFromLabel(label1, cachedContext1);
      if (!box1) continue;

      for (let j = i + 1; j < labels.length; j++) {
        const label2 = labels[j];
        const labelId2 = this.getLabelId(label2);
        if (!labelId2) continue;

        // Create cached context for label2
        const text2 = label2.symbol?.text || "";
        const fontSize2 = label2.symbol?.font?.size || this.settings.fontSize;
        const fontWeight2 = label2.symbol?.font?.weight || this.settings.fontWeight;
        const fontFamily2 = label2.symbol?.font?.family || this.settings.fontFamily;
        const textDimensions2 = this._getOptimizedTextDimensions(text2, fontSize2, fontWeight2, fontFamily2);

        const cachedContext2 = {
          labelId: labelId2,
          textDimensions: textDimensions2,
          pixelToMapRatio
        };

        const box2 = await this._calculateBoundingBoxFromLabel(label2, cachedContext2);
        if (!box2) continue;

        if (this._boxesOverlap(box1, box2)) {
          collisions.push({
            label1,
            label2,
            labelId1,
            labelId2,
            box1,
            box2
          });
        }
      }
    }

    return collisions;
  }

  /**
   * Calculate bounding box directly from a label graphic
   * @param {Object} label - Label graphic
   * @returns {Promise<Object|null>} Bounding box or null if calculation fails
   * @private
   */
  async _calculateBoundingBoxFromLabel(label, cachedContext = null) {
    const labelId = this.getLabelId(label);
    if (!labelId) return null;

    try {
      const centerPoint = await this._calculateLabelVisualCenter(label, labelId);
      if (!centerPoint) return null;

      return this._calculateBoundingBoxAtPosition(label, centerPoint, cachedContext);
    } catch (error) {
      
      return null;
    }
  }

  /**
   * Attempt to reposition a label to avoid collisions
   * @param {Object} label - Label graphic to reposition
   * @param {string} labelId - Label ID
   * @param {Array} allLabels - All labels for collision checking
   * @param {number} startTime - Start time for timeout calculation
   * @param {number} maxTime - Maximum time allowed in milliseconds
   * @returns {boolean} True if repositioned successfully
   * @private
   */
  async _attemptLabelRepositioning(label, labelId, allLabels, startTime = Date.now(), maxTime = 5000) {
    // Pre-calculate shared pixel ratio to avoid redundant calculations
    const mapExtentWidth = this.view?.extent?.width;
    const viewWidth = this.view?.width;
    const pixelToMapRatio = mapExtentWidth / viewWidth;

    // Create list of existing boxes using the SAME method as the search phase
    const existingBoxes = [];
    for (const otherLabel of allLabels) {
      if (otherLabel === label) continue; // Skip the label we're trying to reposition

      // Create cached context for other labels
      const text = otherLabel.symbol?.text || "";
      const fontSize = otherLabel.symbol?.font?.size || this.settings.fontSize;
      const fontWeight = otherLabel.symbol?.font?.weight || this.settings.fontWeight;
      const fontFamily = otherLabel.symbol?.font?.family || this.settings.fontFamily;
      const textDimensions = this._getOptimizedTextDimensions(text, fontSize, fontWeight, fontFamily);

      const cachedContext = {
        labelId: this.getLabelId(otherLabel),
        textDimensions,
        pixelToMapRatio
      };

      // Use the same collision detection method as the search phase
      const otherBox = this._calculateBoundingBoxAtPosition(otherLabel, otherLabel.geometry, cachedContext);
      if (otherBox) {
        existingBoxes.push(otherBox);
      }
    }

    // Log existing boxes for debugging
    
    for (let i = 0; i < Math.min(3, existingBoxes.length); i++) {
      const box = existingBoxes[i];
      
    }

    // Find optimal positions (may return multiple options)
    const positionResult = this._findOptimalPosition(label, existingBoxes, this.settings.maxOptimizationAttempts, startTime, maxTime);
    const originalGeometry = label.geometry;

    // Handle both old format (single position) and new format (multiple positions)
    let positionsToTry = [];
    if (positionResult && positionResult.positions) {
      // New format: multiple positions
      positionsToTry = positionResult.positions.map(p => p.point);
    } else if (positionResult && positionResult.x !== undefined) {
      // Old format: single position
      positionsToTry = [positionResult];
    }

    if (positionsToTry.length > 0) {
      // Try each position until we find one that actually works
      for (let i = 0; i < positionsToTry.length; i++) {
        const newPosition = positionsToTry[i];
        const isOriginalPosition = (newPosition.x === originalGeometry.x && newPosition.y === originalGeometry.y);

        if (isOriginalPosition) continue; // Skip if it's the same as current position

        // Recalculate existing boxes using the SAME method as the search phase
        const freshExistingBoxes = [];
        for (const otherLabel of allLabels) {
          if (otherLabel === label) continue; // Skip the label we're trying to reposition

          // Create cached context for other labels (reuse existing calculation)
          const text = otherLabel.symbol?.text || "";
          const fontSize = otherLabel.symbol?.font?.size || this.settings.fontSize;
          const fontWeight = otherLabel.symbol?.font?.weight || this.settings.fontWeight;
          const fontFamily = otherLabel.symbol?.font?.family || this.settings.fontFamily;
          const textDimensions = this._getOptimizedTextDimensions(text, fontSize, fontWeight, fontFamily);

          const cachedContext = {
            labelId: this.getLabelId(otherLabel),
            textDimensions,
            pixelToMapRatio
          };

          // Use the same collision detection method as the search phase
          const otherBox = this._calculateBoundingBoxAtPosition(otherLabel, otherLabel.geometry, cachedContext);
          if (otherBox) {
            freshExistingBoxes.push(otherBox);
          }
        }

        // Create cached context for the label being repositioned
        const repositionLabelText = label.symbol?.text || "";
        const repositionLabelFontSize = label.symbol?.font?.size || this.settings.fontSize;
        const repositionLabelFontWeight = label.symbol?.font?.weight || this.settings.fontWeight;
        const repositionLabelFontFamily = label.symbol?.font?.family || this.settings.fontFamily;
        const repositionLabelTextDimensions = this._getOptimizedTextDimensions(repositionLabelText, repositionLabelFontSize, repositionLabelFontWeight, repositionLabelFontFamily);

        const repositionCachedContext = {
          labelId: labelId,
          textDimensions: repositionLabelTextDimensions,
          pixelToMapRatio
        };

        if (await this._tryRepositionToPoint(label, labelId, newPosition, originalGeometry, freshExistingBoxes, i + 1, positionsToTry.length, repositionCachedContext)) {
          return true; // Successfully repositioned
        }
      }
      return false;
    } else {
      
      return false;
    }
  }

  /**
   * Try to reposition a label to a specific point and verify it resolves collisions
   * @param {Object} label - Label to reposition
   * @param {string} labelId - Label ID
   * @param {Object} newPosition - Position to try
   * @param {Object} originalPosition - Original position to revert to if failed
   * @param {Array} existingBoxes - Existing bounding boxes to check against
   * @param {number} attemptNum - Current attempt number
   * @param {number} totalAttempts - Total attempts available
   * @returns {boolean} True if successfully repositioned
   * @private
   */
  async _tryRepositionToPoint(label, labelId, newPosition, originalPosition, existingBoxes, attemptNum, totalAttempts, cachedContext = null) {
    // Ensure the new position is in the correct coordinate system
    let finalPosition = newPosition;

    // Always ensure the new position matches the original coordinate system
    const originalSR = originalPosition.spatialReference?.wkid;
    const newSR = newPosition.spatialReference?.wkid;

    if (originalSR !== newSR) {
      if (originalSR === 4326 && (newSR === 3857 || newSR === 102100)) {
        // Convert Web Mercator to Geographic
        const geoPoint = webMercatorUtils.webMercatorToGeographic(newPosition);
        finalPosition = new Point({
          x: geoPoint.x,
          y: geoPoint.y,
          spatialReference: { wkid: 4326 }
        });
      } else if ((originalSR === 3857 || originalSR === 102100) && newSR === 4326) {
        // Convert Geographic to Web Mercator
        const mercatorPoint = webMercatorUtils.geographicToWebMercator(newPosition);
        finalPosition = new Point({
          x: mercatorPoint.x,
          y: mercatorPoint.y,
          spatialReference: { wkid: originalSR }
        });
      } else {
        // Force same spatial reference as original
        finalPosition = new Point({
          x: newPosition.x,
          y: newPosition.y,
          spatialReference: { wkid: originalSR }
        });
      }
    }

    // Temporarily update label position to test this specific position
    label.geometry = finalPosition;

    // Calculate bounding box at new position using the same method as the search phase
    const newBoundingBox = this._calculateBoundingBoxAtPosition(label, finalPosition, cachedContext);

    if (!newBoundingBox) {
      // Revert to original position if bounding box calculation failed
      label.geometry = originalPosition;
      
      return false;
    }

    // Verify the new position actually resolves collisions
    let hasCollision = false;
    let collidingBoxIndex = -1;
    for (let i = 0; i < existingBoxes.length; i++) {
      const existingBox = existingBoxes[i];
      if (this._boxesOverlap(newBoundingBox, existingBox)) {
        hasCollision = true;
        collidingBoxIndex = i;
        break;
      }
    }
    // Log details about collision checking for debugging
    for (let i = 0; i < existingBoxes.length; i++) {
      const existingBox = existingBoxes[i];
      const overlaps = this._boxesOverlap(newBoundingBox, existingBox);
      if (overlaps) {
        
      }
    }

    if (!hasCollision) {
      
      return true;
    } else {
      // Revert to original position and try next position
      label.geometry = originalPosition;
      return false;
    }
  }

  /**
   * Public method to manually trigger collision resolution
   * This can be called from the UI or other components
   * @returns {Promise<Object>} Result of collision resolution
   */
  async resolveAllLabelCollisions() {
    try {
      const result = await this.resolveCollisions();
      
      return result;
    } catch (error) {
      
      return {
        success: false,
        message: `Error resolving collisions: ${error.message}`,
        repositioned: 0
      };
    }
  }

  /**
   * Remove all visible bounding boxes
   * @private
   */
  _removeAllVisibleBoundingBoxes() {
    const targetLayer = this.boundingBoxLayer || this.labelLayer;
    if (!targetLayer) return;

    // Remove all graphics from the map that are bounding boxes or data point circles
    const graphicsToRemove = [];
    for (const [key, graphic] of this.boundingBoxGraphics.entries()) {
      graphicsToRemove.push(graphic);
    }

    // Remove all at once for better performance
    graphicsToRemove.forEach(graphic => {
      try {
        targetLayer.remove(graphic);
      } catch (error) {
        // Ignore removal errors
      }
    });

    // Clear the tracking map
    this.boundingBoxGraphics.clear();
  }

  /**
   * Update all bounding boxes after collision resolution
   * @private
   */
  _updateAllBoundingBoxes() {
    if (!this.labelLayer?.graphics?.length) return;

    // Clear existing bounding boxes
    this._removeAllVisibleBoundingBoxes();

    // Recreate bounding boxes for all labels
    const labels = this.labelLayer.graphics.toArray();
    labels.forEach(label => {
      const labelId = this.getLabelId(label);
      if (labelId) {
        this._createVisibleBoundingBox(label, labelId);
      }
    });
  }

  /**
   * Automatically trigger collision resolution after a delay
   * This allows all labels to be processed first
   * @param {number} delay - Delay in milliseconds (default 1000ms)
   * @param {number} iterations - Number of times to run collision detection (default 1)
   * @private
   */
  _scheduleCollisionResolution(delay = 500, iterations = 1) {
    // Clear any existing timer
    if (this._collisionResolutionTimer) {
      clearTimeout(this._collisionResolutionTimer);
    }

    // Schedule collision detection with proper coordinate conversion
    this._collisionResolutionTimer = setTimeout(() => {
      // Run collision detection if enabled
      // NOTE: Do NOT call _maintainGeographicPositions() here as it resets labels
      // back to their data point positions, undoing collision avoidance work
      if (this.settings.avoidCollisions) {
        this._runMultipleCollisionDetectionPasses(iterations).catch(error => {
          console.error('[CollisionDetection] Error during collision detection:', error);
        });
      }
    }, delay);
  }

  /**
   * Run collision detection multiple times in sequence for better initial placement
   * Each pass can improve positioning as labels settle into collision-free spots
   * @param {number} passes - Number of collision detection passes to run (default 3)
   * @private
   */
  async _runMultipleCollisionDetectionPasses(passes = 3) {
    console.log(`[CollisionDetection] Running ${passes} collision detection passes for initial placement`);

    for (let i = 0; i < passes; i++) {
      console.log(`[CollisionDetection] Starting pass ${i + 1} of ${passes}`);

      try {
        await this._runMinimalCollisionDetection();

        // Add a small delay between passes to allow rendering to update
        if (i < passes - 1) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      } catch (error) {
        console.error(`[CollisionDetection] Error in pass ${i + 1}:`, error);
        // Continue with remaining passes even if one fails
      }
    }

    console.log(`[CollisionDetection] Completed ${passes} collision detection passes`);
  }
}

export default LabelManager;